"use client";

import React from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession, useOrganizationList, useOrganization, useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import {
  Loader2,
  Rocket,
  ChevronLeft,
  ChevronRight,
  Camera,
  Settings,
  ClipboardList,
  Globe,
  MessageCircle,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { api } from "~/trpc/react";
import { urlSchema, cn } from "~/lib/utils";
import { formatHireLocationLabel, ensureHireLocationDetailsInSelection } from "~/lib/marketplace/hire-location-utils";
import { ensureHireDepartmentDetailsInSelection } from "~/lib/marketplace/hire-department-utils";
import {
  buildHireTeamRoster,
  memberDisplayName,
  splitDisplayName,
} from "~/lib/marketplace/hire-team-roster";
import { newProjectLeadSessionKey } from "~/lib/onboarding/build-import-locations";
import {
  AGENT_PROFILE_PRESETS,
  DEFAULT_AGENT_PROFILE_PRESET,
  parseAgentPresetLabel,
} from "~/lib/onboarding/agent-profile-presets";
import {
  DEFAULT_HIRE_RESPONSIBILITIES,
  type HireAgentPreferences,
  type HireAgentResponsibilities,
  type HireContact,
  hireContactToLeadForm,
  readHireAgentPreferences,
  serializeHirePreferencesForApi,
  writeHireAgentPreferences,
} from "~/lib/onboarding/hire-agent-preferences";
import { HireOnboardingAuthModal } from "~/components/marketplace/hire-onboarding-auth-modal";
import { HireOnboardingAuthPanel } from "~/components/marketplace/hire-onboarding-auth-panel";
import { HireOnboardingProgressModal } from "~/components/marketplace/hire-onboarding-progress-modal";
import { resolveHireOnboardingProgressStepIndex } from "~/lib/marketplace/hire-onboarding-progress-steps";
import {
  buildSignInHref,
  navigateToAppUrl,
  resolveEasyGptAppOrigin,
  buildProjectDashboardProfileUrl,
} from "~/lib/auth-paths";
import {
  buildGuestPreviewHireEmail,
  buildMarketplaceHireCompletionPath,
  clearHireCustomPreviewState,
  clearPendingHireIntentId,
  clearStripePaymentCompletionState,
  getTrpcErrorMessage,
  readHireCustomPreviewState,
  readStripePaymentCompletionState,
  resolveStripeReturnHireIntentId,
  writeHireCustomPreviewState,
  writePendingHireIntentId,
  writeStripePaymentCompletionState,
} from "~/lib/marketplace/marketplace-hire-flow";
import { HirePlanSelectionStep } from "~/components/marketplace/hire-plan-selection-step";
import { HireRecommendationsStep } from "~/components/marketplace/hire-recommendations-step";
import { HireCompanyProfilePricingSection } from "~/components/marketplace/hire-company-profile-pricing-section";
import { HireRecommendationsOverviewStep } from "~/components/marketplace/hire-recommendations-overview-step";
import { HireCustomDeployPanel } from "~/components/marketplace/hire-custom-deploy-panel";
import { HireSplitLayout } from "~/components/marketplace/hire-split-layout";
import { switchToHireOrganization } from "~/lib/marketplace/switch-hire-organization";
import { hireContactFromClerkUser } from "~/lib/onboarding/clerk-hire-contact";
import type { HireRecommendationsAgentProfileDraft } from "~/components/marketplace/hire-recommendations-overview-step";
import {
  type HirePlanSelection,
  defaultHirePlanSelection,
  writeHirePlanSelection,
} from "~/lib/marketplace/hire-pricing";
import {
  writeHireRecommendationsSelection,
  readHireRecommendationsSelection,
  computeEffectiveTeamMemberCount,
  computeRecommendationsTotalCents,
  summarizeRecommendationsPricing,
  isEmrIntegrationId,
  defaultHireDepartmentsForVertical,
  extractHireDepartmentsFromProfileText,
  defaultRecommendationsFromSuggestion,
  resetRecommendationsToFreeTier,
  type HireRecommendationsSelection,
} from "~/lib/marketplace/hire-recommendations-pricing";
import {
  normalizeHireCompanyDisplayName,
  resolveHireBusinessDisplayName,
} from "~/lib/marketplace/hire-company-name";
import { formatHireTotalPrice } from "~/lib/marketplace/hire-pricing";
import {
  responsibilitiesFromHirePlan,
  responsibilitiesFromTemplateChannels,
  type HireTemplateDefaults,
  mergeHireResponsibilities,
} from "~/lib/marketplace/hire-template-defaults";
import type { MarketplaceCommunicationOptionId } from "~/lib/marketplace/hire-pricing";
import type { MoonlightAgentListItem } from "~/lib/marketplace/moonlight-home";
import type { WebsiteAgentSuggestion } from "~/lib/marketplace/website-suggestions";
import { InfoTip } from "~/components/marketplace/hire-recommendations-step";
import { TooltipProvider } from "~/components/ui/tooltip";
import { HireWizardTrainingStep } from "~/components/marketplace/hire-wizard-training-step";
import { HireWizardToolsStep } from "~/components/marketplace/hire-wizard-tools-step";
import {
  HireBuilderTabbedLayout,
  type HireBuilderLeftTab,
  type HireBuilderRightTab,
} from "~/components/marketplace/hire-builder-tabbed-layout";
import { HireBuilderPreviewPanel } from "~/components/marketplace/hire-builder-preview-panel";
import { HireBuilderTabPlaceholder } from "~/components/marketplace/hire-builder-tab-placeholder";
import {
  HireBuilderBuildChat,
  markHireGoogleConnectResumePending,
} from "~/components/marketplace/hire-builder-build-chat";
import { TrishOnboardingChat } from "~/components/marketplace/trish-onboarding-chat";
import { PartnerOnboardingChat } from "~/components/org-onboarding/partner-onboarding-chat";
import { SiteGlueConsultantChat } from "~/components/siteglue/siteglue-consultant-chat";
import { YourAiEmployeePreview } from "~/components/siteglue/your-ai-employee-preview";
import { SiteGlueLiveSitePreviewPanel } from "~/components/siteglue/siteglue-live-site-preview-panel";
import { SiteGlueExtendedKnowledgePanel, BuildingCompanyProfileProgress } from "~/components/siteglue/siteglue-extended-knowledge-panel";
import { AiImplementationStrategyView } from "~/components/siteglue/ai-implementation-strategy";
import { SiteGluePlanPricingPanel } from "~/components/siteglue/siteglue-plan-pricing-panel";
import {
  SiteGluePlanCheckoutDialog,
  type SiteGlueCheckoutContact,
} from "~/components/siteglue/siteglue-plan-checkout-dialog";
import { ContactSalesModal } from "~/components/marketing/contact-sales-modal";
import {
  SITEGLUE_CTA_CONFIG,
  trackSiteGlueCta,
} from "~/lib/siteglue/cta-config";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { resolveClientMarketingBrand } from "~/lib/marketing-brand";
import { recommendationsFromAiEmployeePlan } from "~/lib/siteglue/plan-presets";
import { isSiteGlueIntakeStep } from "~/lib/siteglue/siteglue-intake-flow";
import {
  aiEmployeePlanIdFromHirePlanId,
  aiEmployeePlanPriceCents,
  formatAiEmployeePlanHeaderLabel,
  parseAiEmployeePlanIdFromParam,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import {
  readConsultantState,
  type SiteGlueConsultantState,
} from "~/lib/siteglue/consultant-onboarding";
import { type TrishConfigPreference } from "~/lib/marketplace/home-trish-onboarding";
import { PostAiJobInlineSignup } from "~/components/marketplace/post-ai-job-inline-signup";
import {
  buildOnboardingGoalsFromWebsite,
  readHireBuilderOnboardingComplete,
  readSavedHireBuilderOnboardingState,
  shouldSkipHireBuilderOnboarding,
  markHireBuilderOnboardingComplete,
  writeSavedHireBuilderOnboardingState,
} from "~/lib/marketplace/hire-builder-onboarding";
import {
  clearHireForceNewAgent,
  isHireForceNewAgent,
  normalizeHireWebsiteSessionKey,
} from "~/lib/marketplace/hire-onboarding-session";
import { MARKETING_BUILD_CUSTOM } from "~/lib/inbound-gpt/paths";
import { normalizeHireTeamHandoffCtaClusters } from "~/lib/marketplace/hire-team-handoff-ctas";
import {
  getMarketplacePoweredByDefaults,
} from "~/lib/onboarding/marketplace-onboarding-defaults";
import { HireBuilderHirePanel } from "~/components/marketplace/hire-builder-hire-panel";
import { HireBuilderTestAgentPanel } from "~/components/marketplace/hire-builder-test-agent-panel";
import { HireWizardCompanyProfileDialog } from "~/components/marketplace/hire-wizard-company-profile-dialog";
import { HireWizardWidgetColorsSection } from "~/components/marketplace/hire-wizard-widget-colors";
import {
  buildCompanyProfileDisplayText,
  enrichCompanyProfileWithScrapedData,
  locationDetailsToList,
  mapGeneratedLocationsToHireDetails,
  dedupeHireLocationDetails,
  type HireWizardWebsiteGenerationResult,
} from "~/lib/marketplace/hire-wizard-company-profile";
import {
  applyOrganizationLayoutToSelection,
  getOrganizationVisibleSections,
  resolveHireOrganizationLayout,
} from "~/lib/marketplace/hire-organization-layout";
import {
  readHireWizardStepForWebsite,
  writeHireWizardContext,
  type HireWizardStepId,
} from "~/lib/marketplace/hire-wizard-steps";
import {
  DEFAULT_HIRE_WIDGET_COLORS,
  defaultHireWizardTrainingDraft,
  readHireWizardToolCredentials,
  readHireWizardEmrCredentialsSkipped,
  readHireWizardTrainingDraft,
  writeHireWizardToolCredentials,
  writeHireWizardTrainingDraft,
  type HireWizardWidgetColors,
} from "~/lib/marketplace/hire-wizard-draft";

export type HireAgentFormProps = {
  templateProjectId: string;
  agentName: string;
  /** Called after successful hire before navigation */
  onSuccess?: () => void;
  className?: string;
  /** When true, step 1 is plan selection (3-step flow). */
  showPlanStep?: boolean;
  planSelection?: HirePlanSelection;
  onPlanSelectionChange?: (selection: HirePlanSelection) => void;
  /** Override the main form heading (default: Onboard {agentName}) */
  formTitle?: string;
  /** Clone flow: pre-fill Agent Profile from the marketplace template (channels, optional name/title). */
  templateDefaults?: HireTemplateDefaults;
  /** Website-analysis flow: step 1 shows recommendations instead of plan tiers. */
  recommendationsMode?: boolean;
  recommendationsSelection?: HireRecommendationsSelection;
  onRecommendationsChange?: (selection: HireRecommendationsSelection) => void;
  recommendationsBusinessName?: string;
  websiteSuggestion?: WebsiteAgentSuggestion | null;
  suggestedAgents?: MoonlightAgentListItem[];
  suggestedAgentsLoading?: boolean;
  onLiveDemo?: (agent: MoonlightAgentListItem) => void;
  onCloneAgent?: (agent: MoonlightAgentListItem) => void;
  pendingCloneAgent?: MoonlightAgentListItem | null;
  onPendingCloneHandled?: () => void;
  /** Fired when the active onboarding step changes (1–3 in website recommendations flow). */
  onStepChange?: (step: 1 | 2 | 3 | 4) => void;
  /** Two-column layout: configure left, test/deploy right (custom hire page). */
  splitLayout?: boolean;
  /** Opens live agent sidebar for interactive test. */
  onTestAgent?: (projectId: string) => void;
  /** Live company name from Company Settings — drives shell header title. */
  onDisplayCompanyNameChange?: (name: string) => void;
  /** Live agent name for page header (e.g. "Onboard Victoria"). */
  onDisplayAgentNameChange?: (name: string) => void;
  /** Website bootstrap finished — safe to show configure UI and auto-generate preview. */
  bootstrapComplete?: boolean;
  /** Automatically create agent preview after bootstrap (custom hire). */
  autoGeneratePreview?: boolean;
  onPreviewGenerateStart?: () => void;
  onPreviewGenerateComplete?: () => void;
  onPreviewGenerateError?: (message: string) => void;
  /** Multi-step configuration wizard (custom hire). */
  wizardMode?: boolean;
  /** Submit website URL from Build tab onboarding chat. */
  onSubmitWebsite?: (url: string) => void;
  isBootstrapping?: boolean;
  bootstrapError?: string | null;
  onOnboardingGoalsComplete?: (goals: string[]) => void;
  /** SiteGlue Start Over — clear website bootstrap + company profile in the shell. */
  onStartOver?: () => void;
  /** Partner white-label onboarding consultant (from ?brand=slug). */
  partnerConfig?: import("~/lib/org-onboarding/types").PartnerOnboardingAgentConfig;
};

function responsibilitiesFromRecommendations(
  rec: HireRecommendationsSelection,
): HireAgentResponsibilities {
  return {
    liveChat: rec.liveChat,
    textMessages: rec.textMessages,
    email: rec.email,
    browserCalls: rec.browserCalls,
    phoneCalls: rec.phoneCalls,
    videoCalls: rec.videoCalls,
    welcomeVideo: rec.welcomeVideo,
    messenger: rec.messenger,
    whatsapp: rec.whatsapp,
    teamHandoff: rec.teamHandoffEnabled ?? true,
    appointmentsScheduling:
      (rec.appointmentsEnabled && rec.schedulingIntegrationId !== "none") ||
      (rec.emrIntegrationEnabled && rec.emrIntegrationId !== "none"),
  };
}

function planSelectionFromRecommendations(
  rec: HireRecommendationsSelection,
): HirePlanSelection {
  const communicationOptionIds: MarketplaceCommunicationOptionId[] = [];
  if (rec.phoneCalls) communicationOptionIds.push("agent_phone_number");
  if (rec.dedicatedEmail || rec.email) communicationOptionIds.push("agent_email");

  const schedulingIntegrationId =
    rec.appointmentsEnabled && rec.schedulingIntegrationId !== "none"
      ? rec.schedulingIntegrationId
      : rec.emrIntegrationEnabled && rec.emrIntegrationId !== "none"
        ? rec.emrIntegrationId
        : "none";

  const hasAdvancedChannels =
    rec.textMessages || rec.whatsapp || rec.messenger || rec.videoCalls || rec.emrIntegrationEnabled;
  const hasPhoneOrLightScheduling =
    rec.phoneCalls || rec.browserCalls || rec.dedicatedPhone || rec.appointmentsEnabled;

  let planId: HirePlanSelection["planId"] = "intern";
  if (hasAdvancedChannels || rec.locationCount >= 3 || rec.teamMemberCount >= 6) {
    planId = "full_time";
  } else if (hasPhoneOrLightScheduling || rec.locationCount >= 2 || rec.teamMemberCount >= 2) {
    planId = "part_time";
  }

  return {
    planId,
    addonIds: [],
    teamMemberCount: rec.teamMemberCount,
    locationCount: rec.locationCount,
    schedulingIntegrationId,
    communicationOptionIds,
  };
}

function buildHireRedirectUrl(hireIntentId: string) {
  const path = buildMarketplaceHireCompletionPath(hireIntentId);
  if (typeof window !== "undefined") {
    return new URL(path, resolveEasyGptAppOrigin()).href;
  }
  return path;
}

function buildSignInUrl(opts: { email: string; hireIntentId: string }) {
  const redirect = buildHireRedirectUrl(opts.hireIntentId);
  const params = new URLSearchParams({
    email: opts.email,
    redirect_url: redirect,
  });
  return `/sign-in?${params.toString()}`;
}

type PendingHire = {
  hireIntentId: string;
  websiteUrl: string;
  form: {
    firstName: string;
    lastName: string;
    email: string;
    leadTeamMember?: HireContact;
  };
};

type EmployerFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  websiteUrl: string;
  leadTeamMember?: HireContact;
};

function zodEmailSafeParse(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
  return trimmed;
}

type HireFormStep = 1 | 2 | 3 | 4;

export function HireAgentForm({
  templateProjectId,
  agentName,
  onSuccess,
  className,
  showPlanStep = false,
  planSelection: planSelectionProp,
  onPlanSelectionChange,
  formTitle,
  templateDefaults,
  recommendationsMode = false,
  recommendationsSelection,
  onRecommendationsChange,
  recommendationsBusinessName = "your business",
  websiteSuggestion,
  suggestedAgents = [],
  suggestedAgentsLoading = false,
  onLiveDemo,
  onCloneAgent,
  pendingCloneAgent,
  onPendingCloneHandled,
  onStepChange,
  splitLayout = false,
  onTestAgent,
  onDisplayCompanyNameChange,
  onDisplayAgentNameChange,
  bootstrapComplete = true,
  autoGeneratePreview = false,
  onPreviewGenerateStart,
  onPreviewGenerateComplete,
  onPreviewGenerateError,
  wizardMode = false,
  onSubmitWebsite,
  isBootstrapping = false,
  bootstrapError = null,
  onOnboardingGoalsComplete,
  onStartOver,
  partnerConfig,
}: HireAgentFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isSignedIn } = useSession();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList({ userMemberships: { pageSize: 100 } });

  const switchToHireOrganizationIfNeeded = React.useCallback(
    async (organizationId: string | null | undefined) => {
      if (!isSignedIn) return;
      await switchToHireOrganization(setActive, organizationId, organization?.id);
    },
    [isSignedIn, organization?.id, setActive],
  );

  const readGuestSessionId = React.useCallback((): string | undefined => {
    if (typeof document === "undefined") return undefined;
    return (
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("ez-gpt-session="))
        ?.split("=")[1] || undefined
    );
  }, []);
  const [internalPlanSelection, setInternalPlanSelection] = React.useState<HirePlanSelection>(() =>
    defaultHirePlanSelection("intern"),
  );
  const planSelection = planSelectionProp ?? internalPlanSelection;
  const setPlanSelection = onPlanSelectionChange ?? setInternalPlanSelection;
  const [activeTemplateProjectId, setActiveTemplateProjectId] = React.useState(templateProjectId);
  const [clonedTemplateDefaults, setClonedTemplateDefaults] =
    React.useState<HireTemplateDefaults | undefined>(templateDefaults);

  React.useEffect(() => {
    setActiveTemplateProjectId(templateProjectId);
  }, [templateProjectId]);

  React.useEffect(() => {
    setClonedTemplateDefaults(templateDefaults);
  }, [templateDefaults]);

  const effectiveTemplateDefaults = clonedTemplateDefaults ?? templateDefaults;

  const [step, setStep] = React.useState<HireFormStep>(1);

  React.useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  const [websiteUrl, setWebsiteUrl] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [leadFirstName, setLeadFirstName] = React.useState("");
  const [leadLastName, setLeadLastName] = React.useState("");
  const [leadEmail, setLeadEmail] = React.useState("");
  const [leadPhone, setLeadPhone] = React.useState("");
  const [employerTitle, setEmployerTitle] = React.useState("");
  const [employerPhone, setEmployerPhone] = React.useState("");
  const [employerImageUrl, setEmployerImageUrl] = React.useState("");
  const [leadTitle, setLeadTitle] = React.useState("");
  const [leadImageUrl, setLeadImageUrl] = React.useState("");
  const [locationLeads, setLocationLeads] = React.useState<Record<string, HireContact>>({});
  const restoredPreviewState =
    typeof window !== "undefined" ? readHireCustomPreviewState() : null;
  const initialPreset =
    AGENT_PROFILE_PRESETS.find(
      (p) =>
        p.url ===
        (restoredPreviewState?.profileImageUrl || templateDefaults?.profileImageUrl),
    ) ??
    AGENT_PROFILE_PRESETS.find((p) => p.url === templateDefaults?.profileImageUrl) ??
    DEFAULT_AGENT_PROFILE_PRESET;
  const [profilePresetUrl, setProfilePresetUrl] = React.useState(
    restoredPreviewState?.profileImageUrl?.trim() || initialPreset.url,
  );
  const [isUploadingAgentImage, setIsUploadingAgentImage] = React.useState(false);
  const [customAgentName, setCustomAgentName] = React.useState(() => {
    if (restoredPreviewState?.agentName?.trim()) return restoredPreviewState.agentName.trim();
    if (templateDefaults?.agentName?.trim()) return templateDefaults.agentName.trim();
    return parseAgentPresetLabel(initialPreset.label).agentName;
  });
  const [customJobTitle, setCustomJobTitle] = React.useState(() => {
    if (restoredPreviewState?.jobTitle?.trim()) return restoredPreviewState.jobTitle.trim();
    if (templateDefaults?.jobTitle?.trim()) return templateDefaults.jobTitle.trim();
    return parseAgentPresetLabel(initialPreset.label).titleHint;
  });
  const [customPersona, setCustomPersona] = React.useState("");
  const [personaGenerating, setPersonaGenerating] = React.useState(false);
  const personaUserEditedRef = React.useRef(false);
  const agentProfileUserEditedRef = React.useRef(false);
  /** True after Trish (or Configure) confirms an agent name — drives shell "Onboard X" title. */
  const [agentNameConfirmed, setAgentNameConfirmed] = React.useState(
    Boolean(
      restoredPreviewState?.agentNameConfirmed ||
        restoredPreviewState?.agentName?.trim() ||
        templateDefaults?.agentName?.trim(),
    ),
  );
  const [responsibilities, setResponsibilities] =
    React.useState<HireAgentResponsibilities>(() => DEFAULT_HIRE_RESPONSIBILITIES);
  const [authModalOpen, setAuthModalOpen] = React.useState(false);
  const [onboardingProgressDismissed, setOnboardingProgressDismissed] =
    React.useState(false);
  const onboardingProgressStepRef = React.useRef<string | null>(null);
  const [authPhase, setAuthPhase] = React.useState<"signup" | "verify-email">("signup");
  const [pendingHire, setPendingHire] = React.useState<PendingHire | null>(null);
  /** SiteGlue: Sign Up modal opened from Onboard/Test before a hire intent exists. */
  const [authGateOnly, setAuthGateOnly] = React.useState(false);
  /** SiteGlue Onboard click: deploy after email verified. */
  const pendingDeployAfterAuthRef = React.useRef(false);
  /** SiteGlue Sign Up: open this right tab after email verified (no auto-deploy). */
  const pendingOpenTabAfterAuthRef = React.useRef<HireBuilderRightTab | null>(null);
  const [isGeneratingLocations, setIsGeneratingLocations] = React.useState(false);
  const [isGeneratingDepartments, setIsGeneratingDepartments] = React.useState(false);
  /** Blocks Service Offerings auto-sync when Locations Generate updates selection. */
  const suppressDepartmentAutoSyncRef = React.useRef(false);
  const recommendationsSelectionRef = React.useRef(recommendationsSelection);
  recommendationsSelectionRef.current = recommendationsSelection;
  // Sync restore from session — prevents OAuth remount from auto-regenerating a new avatar.
  const [previewProjectId, setPreviewProjectId] = React.useState<string | null>(
    () => restoredPreviewState?.previewProjectId ?? null,
  );
  const [previewHireIntentId, setPreviewHireIntentId] = React.useState<string | null>(
    () => restoredPreviewState?.previewHireIntentId ?? null,
  );
  const [previewGenerated, setPreviewGenerated] = React.useState(() =>
    Boolean(restoredPreviewState?.previewProjectId && restoredPreviewState?.previewHireIntentId),
  );
  const [resumeAfterGoogleConnect, setResumeAfterGoogleConnect] = React.useState(false);
  const autoGenerateAttemptedRef = React.useRef(false);
  const autoGenerateInFlightRef = React.useRef(false);
  /** Trish free vs recommended choice — applied synchronously before generate. */
  const trishConfigPreferenceRef = React.useRef<TrishConfigPreference | null>(null);
  const [previewVersion, setPreviewVersion] = React.useState(0);
  const [previewRefreshKey, setPreviewRefreshKey] = React.useState(0);
  const [deployedProjectId, setDeployedProjectId] = React.useState<string | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [deployPanelTab, setDeployPanelTab] = React.useState<"test" | "deploy" | null>(null);
  const paymentHandledRef = React.useRef(false);
  const hireIntentResumeRef = React.useRef(false);
  const sessionPreviewRestoreRef = React.useRef(false);
  const [wizardStepId, setWizardStepId] = React.useState<HireWizardStepId>("company");
  const [builderLeftTab, setBuilderLeftTab] = React.useState<HireBuilderLeftTab>("build");
  const [builderRightTab, setBuilderRightTab] = React.useState<HireBuilderRightTab>("preview");
  const [showPreviewSignUp, setShowPreviewSignUp] = React.useState(false);
  const [contactSalesOpen, setContactSalesOpen] = React.useState(false);
  const [planCheckoutOpen, setPlanCheckoutOpen] = React.useState(false);
  const [planCheckoutPlanId, setPlanCheckoutPlanId] = React.useState<
    "intern" | "associate" | "professional" | null
  >(null);
  const planCheckoutContactRef = React.useRef<SiteGlueCheckoutContact | null>(null);
  const [signupPrefill, setSignupPrefill] = React.useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    title?: string;
    phone?: string;
    profileImageUrl?: string;
  }>({});
  const [onboardingComplete, setOnboardingComplete] = React.useState(() =>
    readHireBuilderOnboardingComplete(),
  );
  const isSiteGlueBrand =
    !partnerConfig &&
    (getMarketingChrome().brandId === "siteglue" ||
      resolveClientMarketingBrand(searchParams) === "siteglue");
  const isPartnerBrand = Boolean(partnerConfig);

  const [consultantState, setConsultantState] = React.useState<SiteGlueConsultantState | null>(() =>
    typeof window !== "undefined" &&
    (getMarketingChrome().brandId === "siteglue" ||
      resolveClientMarketingBrand(
        typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
      ) === "siteglue")
      ? readConsultantState()
      : null,
  );
  /** Progress card plan pick → Trish confirms in chat (plan-first onboarding). */
  const [externalPlanPick, setExternalPlanPick] = React.useState<
    Exclude<AiEmployeePlanId, "enterprise"> | null
  >(null);
  /** Pricing page ?plan=associate|professional|intern → apply after Trish welcome. */
  const initialPlanFromUrl = React.useMemo(
    () => parseAiEmployeePlanIdFromParam(searchParams.get("plan")),
    [searchParams],
  );
  const [trainingDraft, setTrainingDraft] = React.useState(() => {
    // Fresh + Build new agent: never hydrate a prior Company Profile into the panel.
    if (typeof window !== "undefined" && isHireForceNewAgent()) {
      return defaultHireWizardTrainingDraft();
    }
    return readHireWizardTrainingDraft();
  });
  const [toolCredentials, setToolCredentials] = React.useState(() =>
    readHireWizardToolCredentials(),
  );
  const [emrCredentialsSkipped, setEmrCredentialsSkipped] = React.useState(() =>
    readHireWizardEmrCredentialsSkipped(),
  );
  const [isGeneratingCompanyProfile, setIsGeneratingCompanyProfile] = React.useState(false);
  const [isScrapingWebsitePages, setIsScrapingWebsitePages] = React.useState(false);
  const [isGeneratingFaqs, setIsGeneratingFaqs] = React.useState(false);
  const [isUploadingTrainingFiles, setIsUploadingTrainingFiles] = React.useState(false);
  const [companyProfileDialogOpen, setCompanyProfileDialogOpen] = React.useState(false);
  const lastWizardWebsiteRef = React.useRef<string | null>(null);
  /** Blocks debounced color sync from writing default blue over scraped brand colors. */
  const skipWidgetColorSyncUntilEnrichRef = React.useRef(false);
  const userEditedWidgetColorsRef = React.useRef(false);

  React.useEffect(() => {
    const websiteUrl = websiteSuggestion?.websiteUrl?.trim();
    if (!websiteUrl) return;

    if (lastWizardWebsiteRef.current !== websiteUrl) {
      lastWizardWebsiteRef.current = websiteUrl;
      setWizardStepId(readHireWizardStepForWebsite(websiteUrl));
      return;
    }

    writeHireWizardContext(websiteUrl, wizardStepId);
  }, [websiteSuggestion?.websiteUrl, wizardStepId]);

  React.useEffect(() => {
    writeHireWizardTrainingDraft(trainingDraft);
  }, [trainingDraft]);

  React.useEffect(() => {
    writeHireWizardToolCredentials(toolCredentials, { emrSkipped: emrCredentialsSkipped });
  }, [toolCredentials, emrCredentialsSkipped]);

  React.useEffect(() => {
    const resolvedWebsite = websiteUrl.trim() || websiteSuggestion?.websiteUrl?.trim() || "";
    if (!resolvedWebsite) return;
    setTrainingDraft((current) => {
      if (current.primaryWebsiteUrl === resolvedWebsite) {
        return current;
      }
      // Same site, different string form (www / trailing slash) — keep profile; sync URL only.
      if (
        current.primaryWebsiteUrl &&
        normalizeHireWebsiteSessionKey(current.primaryWebsiteUrl) ===
          normalizeHireWebsiteSessionKey(resolvedWebsite)
      ) {
        return {
          ...current,
          primaryWebsiteUrl: resolvedWebsite,
          additionalWebsiteUrls: Array.from(
            new Set([resolvedWebsite, ...(current.additionalWebsiteUrls ?? [])]),
          ),
        };
      }
      // New website — prefer scraped brand colors when bootstrap already found them.
      const scraped = websiteSuggestion?.brandColors;
      userEditedWidgetColorsRef.current = false;
      return {
        ...current,
        primaryWebsiteUrl: resolvedWebsite,
        additionalWebsiteUrls: [resolvedWebsite],
        scrapedPageUrls: [],
        companyProfileGenerated: false,
        companyProfileText: "",
        faqsGenerated: false,
        qaPairs: [],
        widgetColors: scraped
          ? { ...DEFAULT_HIRE_WIDGET_COLORS, ...scraped }
          : { ...DEFAULT_HIRE_WIDGET_COLORS },
      };
    });
  }, [websiteUrl, websiteSuggestion?.websiteUrl, websiteSuggestion?.brandColors]);

  // Apply brand colors from bootstrap analysis as soon as they arrive.
  React.useEffect(() => {
    const scraped = websiteSuggestion?.brandColors;
    if (!scraped?.headerColor) return;
    setTrainingDraft((current) => {
      const sameWebsite =
        !websiteSuggestion?.websiteUrl ||
        current.primaryWebsiteUrl === websiteSuggestion.websiteUrl ||
        !current.primaryWebsiteUrl;
      if (!sameWebsite) return current;
      // Don't overwrite colors the user already customized in Configure.
      if (userEditedWidgetColorsRef.current) return current;
      if (
        current.widgetColors.headerColor === scraped.headerColor &&
        current.widgetColors.widgetButtonColor === scraped.widgetButtonColor
      ) {
        return current;
      }
      return {
        ...current,
        widgetColors: {
          ...current.widgetColors,
          ...scraped,
        },
      };
    });
  }, [websiteSuggestion?.brandColors, websiteSuggestion?.websiteUrl]);

  const selectedPreset =
    AGENT_PROFILE_PRESETS.find((p) => p.url === profilePresetUrl) ??
    DEFAULT_AGENT_PROFILE_PRESET;
  const profileImageDisplayUrl = profilePresetUrl || selectedPreset.url;

  React.useEffect(() => {
    if (recommendationsMode) return;
    // Consultant recommendation or user edit owns identity — do not overwrite from preset label.
    if (agentNameConfirmed || consultantState?.employeeName?.trim() || agentProfileUserEditedRef.current) {
      return;
    }
    if (!AGENT_PROFILE_PRESETS.some((p) => p.url === profilePresetUrl)) return;
    const parsed = parseAgentPresetLabel(selectedPreset.label);
    setCustomAgentName(parsed.agentName);
    setCustomJobTitle(parsed.titleHint);
  }, [
    profilePresetUrl,
    selectedPreset.label,
    recommendationsMode,
    agentNameConfirmed,
    consultantState?.employeeName,
  ]);

  React.useEffect(() => {
    if (!pendingCloneAgent || !recommendationsMode) return;

    setActiveTemplateProjectId(pendingCloneAgent.id);
    setClonedTemplateDefaults({
      responsibilities: responsibilitiesFromTemplateChannels(pendingCloneAgent.channels),
    });

    setStep(showPlanStep ? 2 : 1);
    onPendingCloneHandled?.();
  }, [pendingCloneAgent, recommendationsMode, showPlanStep, onPendingCloneHandled]);

  // Intern-first sets employeeName before bootstrap — sync website URL independently.
  React.useEffect(() => {
    const fromSuggestion = websiteSuggestion?.websiteUrl?.trim();
    const fromConsultant = consultantState?.websiteUrl?.trim();
    const next = fromSuggestion || fromConsultant;
    if (!next) return;
    setWebsiteUrl((current) => current.trim() || next);
  }, [websiteSuggestion?.websiteUrl, consultantState?.websiteUrl]);

  React.useEffect(() => {
    if (!websiteSuggestion?.recommendations?.agentProfile) return;
    if (consultantState?.employeeName?.trim()) return;
    const profile = websiteSuggestion.recommendations.agentProfile;
    // Prefill Configure defaults from scrape, but do not treat as user-confirmed name yet.
    if (!agentNameConfirmed) {
      setCustomAgentName(profile.agentName);
      setCustomJobTitle(profile.jobTitle);
      setProfilePresetUrl(profile.profileImageUrl);
    }
    setCustomPersona(profile.persona);
    setCompanyName((current) => {
      const normalizedCurrent = normalizeHireCompanyDisplayName(current);
      if (normalizedCurrent) return normalizedCurrent;
      return resolveHireBusinessDisplayName(
        websiteSuggestion.businessName,
        websiteSuggestion.websiteUrl,
      );
    });

    const leadHints = websiteSuggestion.recommendations.leadTeamHints;
    if (leadHints?.jobTitle?.trim()) {
      setLeadTitle((current) => current.trim() || leadHints.jobTitle!.trim());
    }
    if (leadHints?.phone?.trim()) {
      setLeadPhone((current) => current.trim() || leadHints.phone!.trim());
    }
    if (leadHints?.imageUrl?.trim()) {
      setLeadImageUrl((current) => current.trim() || leadHints.imageUrl!.trim());
    }

    personaUserEditedRef.current = false;
    let cancelled = false;
    setPersonaGenerating(true);

    void (async () => {
      try {
        const res = await fetch("/api/ai-agents/generate-persona", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: profile.agentName,
            jobTitle: profile.jobTitle,
            websiteUrl: websiteSuggestion.websiteUrl,
            companyName: websiteSuggestion.businessName,
          }),
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { persona?: string };
        if (
          !cancelled &&
          !personaUserEditedRef.current &&
          typeof data.persona === "string" &&
          data.persona.trim()
        ) {
          setCustomPersona(data.persona.trim());
        }
      } catch {
        /* keep placeholder persona */
      } finally {
        if (!cancelled) setPersonaGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [websiteSuggestion]);

  const agentProfileDraft = React.useMemo<HireRecommendationsAgentProfileDraft>(
    () => ({
      agentName: customAgentName,
      jobTitle: customJobTitle,
      profileImageUrl: profilePresetUrl,
      persona: customPersona,
    }),
    [customAgentName, customJobTitle, profilePresetUrl, customPersona],
  );

  const handleAgentProfileDraftChange = (next: HireRecommendationsAgentProfileDraft) => {
    agentProfileUserEditedRef.current = true;
    if (next.persona !== customPersona) {
      personaUserEditedRef.current = true;
    }
    setCustomAgentName(next.agentName);
    setCustomJobTitle(next.jobTitle);
    setProfilePresetUrl(next.profileImageUrl);
    setCustomPersona(next.persona);
  };

  const handleAgentImageUpload = async (file: File) => {
    setIsUploadingAgentImage(true);
    try {
      const response = await fetch(`/api/upload/avatar?filename=${encodeURIComponent(file.name)}`, {
        method: "POST",
        body: file,
      });
      if (!response.ok) throw new Error("Upload failed");
      const result = (await response.json()) as { url?: string };
      if (!result.url) throw new Error("No URL returned");
      setProfilePresetUrl(result.url);
      toast.success("Profile image uploaded");
    } catch {
      toast.error("Failed to upload profile image");
    } finally {
      setIsUploadingAgentImage(false);
    }
  };

  const startHireMutation = api.project.startMarketplaceHire.useMutation();
  const completeHireMutation = api.project.completeMarketplaceHire.useMutation();
  const preparePreviewMutation = api.project.prepareMarketplaceHirePreview.useMutation();
  const persistWebsiteDataMutation = api.project.persistAmplify360GeneratedData.useMutation();

  const handleRecommendationsChange = React.useCallback(
    (next: HireRecommendationsSelection) => {
      onRecommendationsChange?.(next);
      if (isSiteGlueBrand) {
        const mapped = planSelectionFromRecommendations(next);
        writeHirePlanSelection(mapped);
        setPlanSelection(mapped);
        onPlanSelectionChange?.(mapped);
      }
    },
    [onRecommendationsChange, isSiteGlueBrand, onPlanSelectionChange],
  );

  const handleBuilderApplyUpdates = React.useCallback(
    (updates: {
      companyName?: string;
      websiteUrl?: string;
      agentName?: string;
      jobTitle?: string;
      selection?: HireRecommendationsSelection;
      widgetColors?: HireWizardWidgetColors;
    }) => {
      if (updates.companyName) setCompanyName(updates.companyName);
      if (updates.websiteUrl) setWebsiteUrl(updates.websiteUrl);
      // Never let post-preview Trish rename a confirmed AI Employee (e.g. Sunday → Virginia).
      if (updates.agentName && !agentNameConfirmed) {
        setCustomAgentName(updates.agentName);
      }
      if (updates.jobTitle) setCustomJobTitle(updates.jobTitle);
      if (updates.selection) {
        handleRecommendationsChange(updates.selection);
      }
      if (updates.widgetColors) {
        setTrainingDraft((current) => ({
          ...current,
          widgetColors: {
            ...current.widgetColors,
            ...updates.widgetColors,
            // Keep launcher accent aligned with button when button changes.
            ...(updates.widgetColors?.widgetButtonColor &&
            !updates.widgetColors.launcherBackgroundColor
              ? { launcherBackgroundColor: updates.widgetColors.widgetButtonColor }
              : {}),
          },
        }));
      }
      setBuilderRightTab("preview");
    },
    [agentNameConfirmed, handleRecommendationsChange],
  );

  React.useEffect(() => {
    if (!wizardMode) return;
    const url = new URL(window.location.href);
    const oauthStatus = url.searchParams.get("google_oauth");
    const oauthMessage = url.searchParams.get("google_oauth_message");
    if (!oauthStatus) return;

    // OAuth full-page return — restore preview + agent identity before applying calendar flags.
    const stored = readHireCustomPreviewState();
    if (stored?.previewHireIntentId && stored.previewProjectId) {
      setPreviewHireIntentId(stored.previewHireIntentId);
      setPreviewProjectId(stored.previewProjectId);
      setPreviewGenerated(true);
      markHireBuilderOnboardingComplete();
      setOnboardingComplete(true);
      setWizardStepId("summary");
      setBuilderRightTab("preview");
      if (stored.agentName?.trim()) {
        setCustomAgentName(stored.agentName.trim());
        setAgentNameConfirmed(true);
      }
      if (stored.jobTitle?.trim()) setCustomJobTitle(stored.jobTitle.trim());
      if (stored.profileImageUrl?.trim()) setProfilePresetUrl(stored.profileImageUrl.trim());
      if (stored.agentNameConfirmed) setAgentNameConfirmed(true);
    }

    const baseSelection =
      recommendationsSelection ?? readHireRecommendationsSelection();
    if (oauthStatus === "success" && baseSelection) {
      handleRecommendationsChange({
        ...baseSelection,
        appointmentsEnabled: true,
        schedulingIntegrationId: "google_calendar",
      });
      markHireGoogleConnectResumePending();
      setResumeAfterGoogleConnect(true);
      toast.success("Google Calendar connected.");
    } else if (oauthStatus === "error") {
      toast.error(oauthMessage || "Google connection failed.");
    }

    url.searchParams.delete("google_oauth");
    url.searchParams.delete("google_oauth_message");
    window.history.replaceState({}, "", url.toString());
  }, [
    wizardMode,
    recommendationsSelection,
    handleRecommendationsChange,
  ]);

  const utils = api.useUtils();
  const createCheckoutMutation = api.stripe.createMarketplaceHireCheckoutSession.useMutation();

  React.useEffect(() => {
    if (companyName.includes("&")) {
      const fixed = resolveHireBusinessDisplayName(
        companyName,
        websiteUrl || websiteSuggestion?.websiteUrl,
      );
      if (fixed && fixed !== companyName) setCompanyName(fixed);
    }
  }, [companyName, websiteUrl, websiteSuggestion?.websiteUrl]);

  // Keep Organization name filled when Trish identifies the business (input previously stayed empty).
  React.useEffect(() => {
    if (companyName.trim()) return;
    const raw =
      consultantState?.businessName?.trim() ||
      websiteSuggestion?.businessName?.trim() ||
      "";
    if (!raw) return;
    const next = resolveHireBusinessDisplayName(
      raw,
      websiteUrl || websiteSuggestion?.websiteUrl || consultantState?.websiteUrl,
    );
    if (next) setCompanyName(next);
  }, [
    companyName,
    consultantState?.businessName,
    consultantState?.websiteUrl,
    websiteSuggestion?.businessName,
    websiteSuggestion?.websiteUrl,
    websiteUrl,
  ]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const website = params.get("website");
    const company = params.get("company");
    const emailParam = params.get("email");
    const firstNameParam = params.get("firstName");
    const lastNameParam = params.get("lastName");
    if (website) setWebsiteUrl(website);
    if (company) {
      setCompanyName(resolveHireBusinessDisplayName(company, website ?? undefined));
    }
    if (emailParam) setEmail(emailParam);
    if (firstNameParam) setFirstName(firstNameParam);
    if (lastNameParam) setLastName(lastNameParam);
  }, []);

  const isSubmitting =
    startHireMutation.isPending ||
    completeHireMutation.isPending ||
    preparePreviewMutation.isPending ||
    createCheckoutMutation.isPending;

  const resolveHireResponsibilities = (): HireAgentResponsibilities => {
    if ((recommendationsMode || splitLayout) && recommendationsSelection) {
      return mergeHireResponsibilities(
        responsibilitiesFromRecommendations(recommendationsSelection),
        effectiveTemplateDefaults?.responsibilities,
      );
    }
    if (showPlanStep) {
      return mergeHireResponsibilities(
        responsibilitiesFromHirePlan(
          planSelection.planId,
          planSelection.schedulingIntegrationId,
        ),
        effectiveTemplateDefaults?.responsibilities,
      );
    }
    return mergeHireResponsibilities(
      responsibilities,
      effectiveTemplateDefaults?.responsibilities,
    );
  };

  const buildHirePreferences = (employer?: EmployerFormValues | null): HireAgentPreferences => {
    const roster = buildHireTeamRoster({
      employer: employerFields,
      lead: leadFields,
      extras: recommendationsSelection?.extraTeamMembers ?? [],
    });

    const employerAsLead: HireContact | undefined = employer
      ? {
          firstName: employer.firstName.trim(),
          lastName: employer.lastName.trim(),
          email: employer.email.trim(),
          phone: employerPhone.trim() || undefined,
        }
      : employerFields.email.trim()
        ? {
            firstName: employerFields.firstName.trim(),
            lastName: employerFields.lastName.trim(),
            email: employerFields.email.trim(),
            phone: employerFields.phone?.trim() || undefined,
          }
        : undefined;

    const scrapedLocations =
      recommendationsSelection && (recommendationsSelection.locationDetails?.length ?? 0) > 0
        ? recommendationsSelection.locationDetails
            .slice(0, recommendationsSelection.locationCount)
            .map((loc) => ({
              name: loc.name?.trim() || formatHireLocationLabel(loc),
              address: loc.address?.trim() || undefined,
              city: loc.city?.trim() || undefined,
              state: loc.state?.trim() || undefined,
              postalCode: loc.postalCode?.trim() || undefined,
              phone: loc.phone?.trim() || undefined,
              businessHours: loc.businessHours?.trim() || undefined,
            }))
        : undefined;

    const locationLeadMembers =
      recommendationsSelection && (recommendationsSelection.locationDetails?.length ?? 0) > 0
        ? recommendationsSelection.locationDetails
            .slice(0, recommendationsSelection.locationCount)
            .map((loc) => {
              const locationName = loc.name?.trim() || formatHireLocationLabel(loc);
              const leadEmailForLoc = loc.leadTeamMemberEmail?.trim();
              const rosterLead = leadEmailForLoc
                ? roster.find((entry) => entry.email === leadEmailForLoc)
                : undefined;
              const leadFromState = locationLeads[locationName];
              const resolved =
                rosterLead ??
                (leadFromState
                  ? {
                      name: memberDisplayName(leadFromState),
                      email: leadFromState.email,
                      phone: leadFromState.phone,
                    }
                  : undefined) ??
                (employerAsLead
                  ? {
                      name: `${employerAsLead.firstName} ${employerAsLead.lastName}`.trim(),
                      email: employerAsLead.email,
                      phone: employerAsLead.phone,
                    }
                  : undefined);

              if (!resolved?.email) return null;
              const { firstName: leadFirst, lastName: leadLast } = splitDisplayName(resolved.name);
              return {
                locationName,
                leadTeamMember: {
                  firstName: leadFirst,
                  lastName: leadLast,
                  email: resolved.email,
                  ...(resolved.phone?.trim() ? { phone: resolved.phone.trim() } : {}),
                },
              };
            })
            .filter((entry): entry is { locationName: string; leadTeamMember: HireContact } =>
              Boolean(entry),
            )
        : activeLocations.length > 0
          ? activeLocations
              .map((locationName) => {
                const lead = locationLeads[locationName] ?? employerAsLead;
                if (!lead) return null;
                return { locationName, leadTeamMember: lead };
              })
              .filter((entry): entry is { locationName: string; leadTeamMember: HireContact } =>
                Boolean(entry),
              )
          : [];

    const primaryLead =
      locationLeadMembers[0]?.leadTeamMember ?? employerAsLead ?? employer?.leadTeamMember;

    const resolvedLayout = resolveHireOrganizationLayout(
      websiteSuggestion?.vertical ?? "general",
      `${companyName.trim() || websiteSuggestion?.businessName || ""} ${websiteSuggestion?.summary ?? ""} ${trainingDraft.companyProfileText ?? ""}`,
    );

    const extras = recommendationsSelection?.extraTeamMembers ?? [];

    const departmentMembers =
      recommendationsSelection &&
      (resolvedLayout === "departments" ||
        recommendationsSelection.departmentCount > 1 ||
        (recommendationsSelection.departmentDetails?.length ?? 0) > 1)
        ? recommendationsSelection.departmentDetails
            .slice(
              0,
              Math.max(
                recommendationsSelection.departmentCount,
                recommendationsSelection.departmentDetails.length,
              ),
            )
            .map((dept) => {
              const departmentName = dept.name.trim();
              const leadEmailForDept = dept.leadTeamMemberEmail?.trim();
              const rosterLead = leadEmailForDept
                ? roster.find((entry) => entry.email === leadEmailForDept)
                : undefined;
              const extraLead = extras.find(
                (m) =>
                  m.departmentName?.trim().toLowerCase() === departmentName.toLowerCase() &&
                  m.email?.trim(),
              );
              const resolved =
                rosterLead ??
                (extraLead
                  ? {
                      name: extraLead.name.trim(),
                      email: extraLead.email.trim(),
                      phone: extraLead.phone,
                    }
                  : undefined) ??
                (employerAsLead
                  ? {
                      name: `${employerAsLead.firstName} ${employerAsLead.lastName}`.trim(),
                      email: employerAsLead.email,
                      phone: employerAsLead.phone,
                    }
                  : undefined);

              if (!resolved?.email || !departmentName) return null;
              const { firstName: leadFirst, lastName: leadLast } = splitDisplayName(resolved.name);
              const otherEmails = new Set(
                (dept.otherTeamMemberEmails ?? []).filter(Boolean).map((e) => e.trim().toLowerCase()),
              );
              // Extras assigned to this department who are not the lead become other members.
              for (const extra of extras) {
                if (
                  extra.departmentName?.trim().toLowerCase() === departmentName.toLowerCase() &&
                  extra.email?.trim() &&
                  extra.email.trim().toLowerCase() !== resolved.email.toLowerCase()
                ) {
                  otherEmails.add(extra.email.trim().toLowerCase());
                }
              }
              return {
                departmentName,
                leadTeamMember: {
                  firstName: leadFirst,
                  lastName: leadLast,
                  email: resolved.email,
                  ...(resolved.phone?.trim() ? { phone: resolved.phone.trim() } : {}),
                },
                otherTeamMemberEmails: [...otherEmails],
              };
            })
            .filter(
              (
                entry,
              ): entry is {
                departmentName: string;
                leadTeamMember: HireContact;
                otherTeamMemberEmails: string[];
              } => Boolean(entry),
            )
        : undefined;

    const schedulingIntegrationId =
      recommendationsSelection?.appointmentsEnabled &&
      recommendationsSelection.schedulingIntegrationId !== "none"
        ? recommendationsSelection.schedulingIntegrationId
        : recommendationsSelection?.emrIntegrationEnabled &&
            recommendationsSelection.emrIntegrationId !== "none"
          ? recommendationsSelection.emrIntegrationId
          : undefined;

    return {
      agentName: customAgentName.trim() || parseAgentPresetLabel(selectedPreset.label).agentName,
      profileImageUrl: profilePresetUrl || selectedPreset.url,
      companyName: companyName.trim() || undefined,
      titleHint: customJobTitle.trim() || undefined,
      persona: customPersona.trim() || undefined,
      responsibilities: resolveHireResponsibilities(),
      ...(primaryLead ? { leadTeamMember: primaryLead } : {}),
      ...(scrapedLocations && scrapedLocations.length > 0 ? { scrapedLocations } : {}),
      ...(locationLeadMembers.length > 0 ? { locationLeadMembers } : {}),
      ...(resolvedLayout === "departments" || (departmentMembers?.length ?? 0) > 1
        ? { organizationLayout: "departments" as const }
        : {}),
      ...(departmentMembers && departmentMembers.length > 0 ? { departmentMembers } : {}),
      ...(extras.length > 0
        ? {
            extraTeamMembers: extras.map((m) => ({
              name: m.name.trim(),
              email: m.email.trim(),
              ...(m.title?.trim() ? { title: m.title.trim() } : {}),
              ...(m.phone?.trim() ? { phone: m.phone.trim() } : {}),
              ...(m.imageUrl?.trim() ? { imageUrl: m.imageUrl.trim() } : {}),
              ...(m.departmentName?.trim() ? { departmentName: m.departmentName.trim() } : {}),
              ...(m.locationName?.trim() ? { locationName: m.locationName.trim() } : {}),
            })),
          }
        : {}),
      ...(schedulingIntegrationId ? { schedulingIntegrationId } : {}),
      ...(employer
        ? {
            employerContact: {
              firstName: employer.firstName,
              lastName: employer.lastName,
              email: employer.email,
            },
          }
        : {}),
      widgetColors: trainingDraft.widgetColors,
      ...((recommendationsSelection?.teamHandoffCtaClusters?.length ?? 0) > 0
        ? {
            teamHandoffCtaClusters: recommendationsSelection!.teamHandoffCtaClusters,
          }
        : {}),
      ...(resolveClientMarketingBrand(
        typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
      ) === "siteglue"
        ? { marketingBrand: "siteglue" as const }
        : {}),
      ...(partnerConfig ? { partnerBrandSlug: partnerConfig.brandSlug } : {}),
    };
  };

  const prepareHirePrefsForMutation = (
    employer?: EmployerFormValues | null,
    options?: { silent?: boolean },
  ): HireAgentPreferences | null => {
    const result = serializeHirePreferencesForApi(buildHirePreferences(employer), {
      defaultProfileImageUrl: DEFAULT_AGENT_PROFILE_PRESET.url,
    });
    if (!result.ok) {
      if (!options?.silent) {
        toast.error(result.message);
      }
      return null;
    }
    return result.data;
  };

  const validateEmployerStep = (options?: {
    silent?: boolean;
  }): EmployerFormValues | null => {
    const clerkContact = user ? hireContactFromClerkUser(user) : null;
    const resolvedFirstName =
      firstName.trim() || clerkContact?.firstName || leadFirstName.trim();
    const resolvedLastName =
      lastName.trim() || clerkContact?.lastName || leadLastName.trim();
    const resolvedCompanyName =
      companyName.trim() ||
      websiteSuggestion?.businessName?.trim() ||
      "";
    const resolvedWebsiteUrl =
      websiteUrl.trim() || websiteSuggestion?.websiteUrl?.trim() || "";
    const resolvedEmail = email.trim() || clerkContact?.email || leadEmail.trim();

    if (!resolvedFirstName || !resolvedLastName) {
      if (!options?.silent) toast.error("Enter your first and last name.");
      return null;
    }
    if (!resolvedCompanyName) {
      if (!options?.silent) toast.error("Enter your company name.");
      return null;
    }
    const emailParsed = zodEmailSafeParse(resolvedEmail);
    if (!emailParsed) {
      if (!options?.silent) toast.error("Enter a valid work email address.");
      return null;
    }
    const parsed = urlSchema.safeParse({ url: resolvedWebsiteUrl });
    if (!parsed.success) {
      if (!options?.silent) {
        toast.error("Enter a valid website URL (e.g. https://yourbusiness.com).");
      }
      return null;
    }

    let leadTeamMember: HireContact | undefined;
    if (threeStepRecommendations && activeLocations.length > 0) {
      for (const loc of activeLocations) {
        const lead = locationLeads[loc];
        if (!lead?.firstName?.trim() || !lead?.lastName?.trim()) {
          toast.error(`Enter a lead team member name for ${loc}.`);
          return null;
        }
        const leadEmailParsed = zodEmailSafeParse(lead.email ?? "");
        if (!leadEmailParsed) {
          toast.error(`Enter a valid email for ${loc}.`);
          return null;
        }
      }
      const firstLoc = activeLocations[0]!;
      leadTeamMember = locationLeads[firstLoc];
    } else {
      const hasLeadFields =
        leadFirstName.trim() ||
        leadLastName.trim() ||
        leadEmail.trim() ||
        leadPhone.trim();
      if (hasLeadFields) {
        if (!leadFirstName.trim() || !leadLastName.trim()) {
          toast.error("Enter the lead team member's first and last name.");
          return null;
        }
        const leadEmailParsed = zodEmailSafeParse(leadEmail);
        if (!leadEmailParsed) {
          toast.error("Enter a valid email for the lead team member.");
          return null;
        }
        leadTeamMember = {
          firstName: leadFirstName.trim(),
          lastName: leadLastName.trim(),
          email: leadEmailParsed,
          phone: leadPhone.trim() || undefined,
        };
      }
    }

    return {
      firstName: resolvedFirstName,
      lastName: resolvedLastName,
      email: emailParsed,
      companyName: resolvedCompanyName,
      websiteUrl: parsed.data.url,
      leadTeamMember,
    };
  };

  const validateForm = () => {
    const employer = validateEmployerStep();
    if (!employer) return null;
    if (!validateAgentProfileStep()) return null;
    return employer;
  };

  const threeStepRecommendations = Boolean(showPlanStep && splitLayout);
  const overviewStep = 1;
  const agentProfileStep = threeStepRecommendations ? null : showPlanStep ? 2 : 1;
  const companyProfileStep = threeStepRecommendations ? 2 : showPlanStep ? 3 : 2;
  const pricingStep = threeStepRecommendations ? 3 : showPlanStep ? 1 : 1;
  const splitCombinedLayout = splitLayout && threeStepRecommendations && !wizardMode;
  const useWizardLayout = splitLayout && threeStepRecommendations && wizardMode;
  const displayAgentName = customAgentName.trim() || agentName;

  const pricingTeamContacts = React.useMemo(() => {
    const employerName = memberDisplayName({ firstName, lastName });
    const hasLeadFields =
      leadFirstName.trim() || leadLastName.trim() || leadEmail.trim();
    const leadName = hasLeadFields
      ? memberDisplayName({
          firstName: leadFirstName.trim() || firstName,
          lastName: leadLastName.trim() || lastName,
        })
      : employerName;
    const leadEmailDisplay = hasLeadFields ? leadEmail.trim() : email.trim();

    return {
      employerContact: {
        name: employerName || "Account Owner",
        email: email.trim() || undefined,
      },
      leadTeamMember: {
        name: leadName || "Lead Team Member",
        email: leadEmailDisplay || undefined,
      },
    };
  }, [firstName, lastName, email, leadFirstName, leadLastName, leadEmail]);

  const employerFields = React.useMemo(
    () => ({
      displayName: memberDisplayName({ firstName, lastName }),
      firstName,
      lastName,
      email: email.trim(),
      title: employerTitle,
      phone: employerPhone,
      imageUrl: employerImageUrl,
    }),
    [firstName, lastName, email, employerTitle, employerPhone, employerImageUrl],
  );

  const leadFields = React.useMemo(
    () => ({
      displayName: memberDisplayName({
        firstName: leadFirstName.trim() || firstName,
        lastName: leadLastName.trim() || lastName,
      }),
      firstName: leadFirstName.trim() || firstName,
      lastName: leadLastName.trim() || lastName,
      email: leadEmail.trim() || email.trim(),
      title: leadTitle,
      phone: leadPhone,
      imageUrl: leadImageUrl,
    }),
    [leadFirstName, leadLastName, leadEmail, leadPhone, leadTitle, leadImageUrl, firstName, lastName, email],
  );

  const hireBuilderTeamContacts = React.useMemo(() => {
    const roster = buildHireTeamRoster({
      employer: employerFields,
      lead: leadFields,
      extras: recommendationsSelection?.extraTeamMembers ?? [],
    });
    return roster.map((entry) => ({
      name: entry.name,
      email: entry.email,
      title: entry.title,
      phone: entry.phone,
    }));
  }, [employerFields, leadFields, recommendationsSelection?.extraTeamMembers]);

  const lastLocationSyncKeyRef = React.useRef("");
  React.useEffect(() => {
    if (!recommendationsSelection || !wizardMode) return;
    const resolvedCompany =
      resolveHireBusinessDisplayName(
        companyName.trim() || websiteSuggestion?.businessName,
        websiteSuggestion?.websiteUrl ?? websiteUrl,
      ) || "Main Office";
    const leadEmail =
      leadFields.email.trim() ||
      employerFields.email.trim() ||
      email.trim() ||
      "";
    const syncKey = `${resolvedCompany}|${leadEmail}|${recommendationsSelection.locationCount}`;
    if (lastLocationSyncKeyRef.current === syncKey) return;

    const synced = ensureHireLocationDetailsInSelection(recommendationsSelection, {
      companyName: resolvedCompany,
      leadTeamMemberEmail: leadEmail || undefined,
      phone: websiteSuggestion?.recommendations.leadTeamHints?.phone?.trim(),
    });
    const currentLead = recommendationsSelection.locationDetails?.[0]?.leadTeamMemberEmail;
    const nextLead = synced.locationDetails[0]?.leadTeamMemberEmail;
    const locationsChanged =
      synced.locationDetails.length !== (recommendationsSelection.locationDetails?.length ?? 0) ||
      synced.locationDetails.some(
        (loc, index) =>
          loc.name !== recommendationsSelection.locationDetails?.[index]?.name ||
          loc.address !== recommendationsSelection.locationDetails?.[index]?.address,
      );
    if (!locationsChanged && currentLead === nextLead) {
      lastLocationSyncKeyRef.current = syncKey;
      return;
    }

    lastLocationSyncKeyRef.current = syncKey;
    handleRecommendationsChange(synced);
  }, [
    wizardMode,
    companyName,
    websiteSuggestion?.businessName,
    websiteSuggestion?.websiteUrl,
    websiteSuggestion?.recommendations.leadTeamHints?.phone,
    websiteUrl,
    leadFields.email,
    employerFields.email,
    email,
    recommendationsSelection,
    handleRecommendationsChange,
  ]);

  const lastDepartmentSyncKeyRef = React.useRef("");
  React.useEffect(() => {
    if (!recommendationsSelection || !wizardMode) return;
    const layout = resolveHireOrganizationLayout(
      websiteSuggestion?.vertical ?? "general",
      `${companyName.trim() || websiteSuggestion?.businessName || ""} ${websiteSuggestion?.summary ?? ""}`,
    );
    if (layout !== "departments") return;
    const leadEmail =
      leadFields.email.trim() ||
      employerFields.email.trim() ||
      email.trim() ||
      "";
    const syncKey = `${leadEmail}|${recommendationsSelection.departmentCount}|${recommendationsSelection.departmentDetails.map((dept) => dept.name).join("|")}`;
    if (lastDepartmentSyncKeyRef.current === syncKey) return;

    const synced = ensureHireDepartmentDetailsInSelection(recommendationsSelection, {
      leadTeamMemberEmail: leadEmail || undefined,
    });
    const needsLeadSync = synced.departmentDetails.some((dept, index) => {
      const current = recommendationsSelection.departmentDetails[index];
      return dept.leadTeamMemberEmail !== current?.leadTeamMemberEmail;
    });
    if (!needsLeadSync) {
      lastDepartmentSyncKeyRef.current = syncKey;
      return;
    }

    lastDepartmentSyncKeyRef.current = syncKey;
    handleRecommendationsChange(synced);
  }, [
    wizardMode,
    companyName,
    websiteSuggestion?.vertical,
    websiteSuggestion?.businessName,
    websiteSuggestion?.summary,
    leadFields.email,
    employerFields.email,
    email,
    recommendationsSelection,
    handleRecommendationsChange,
  ]);

  const applyEmployerFormValues = React.useCallback(
    (values: {
      displayName?: string;
      firstName: string;
      lastName: string;
      email: string;
      title?: string;
      phone?: string;
      imageUrl?: string;
    }) => {
      const nameParts = values.displayName?.trim()
        ? splitDisplayName(values.displayName)
        : { firstName: values.firstName, lastName: values.lastName };
      setFirstName(nameParts.firstName);
      setLastName(nameParts.lastName);
      setEmail(values.email);
      setEmployerTitle(values.title?.trim() ?? "");
      setEmployerPhone(values.phone?.trim() ?? "");
      setEmployerImageUrl(values.imageUrl?.trim() ?? "");
    },
    [],
  );

  const applyLeadFormValues = React.useCallback(
    (values: {
      displayName?: string;
      firstName: string;
      lastName: string;
      email: string;
      title?: string;
      phone?: string;
      imageUrl?: string;
    }) => {
      const nameParts = values.displayName?.trim()
        ? splitDisplayName(values.displayName)
        : { firstName: values.firstName, lastName: values.lastName };
      setLeadFirstName(nameParts.firstName);
      setLeadLastName(nameParts.lastName);
      setLeadEmail(values.email);
      setLeadTitle(values.title?.trim() ?? "");
      setLeadPhone(values.phone?.trim() ?? "");
      setLeadImageUrl(values.imageUrl?.trim() ?? "");
    },
    [],
  );

  React.useEffect(() => {
    if (!recommendationsSelection || !onRecommendationsChange) return;
    const effectiveCount = computeEffectiveTeamMemberCount({
      employerEmail: email,
      leadEmail: leadEmail.trim() || email,
      extraCount: recommendationsSelection.extraTeamMembers.length,
    });
    if (recommendationsSelection.teamMemberCount === effectiveCount) return;
    onRecommendationsChange({
      ...recommendationsSelection,
      teamMemberCount: effectiveCount,
    });
  }, [
    email,
    leadEmail,
    recommendationsSelection,
    onRecommendationsChange,
  ]);

  const activeLocations = React.useMemo(() => {
    if (!recommendationsSelection) return [];
    const count = recommendationsSelection.locationCount;
    if ((recommendationsSelection.locationDetails?.length ?? 0) > 0) {
      return recommendationsSelection.locationDetails
        .slice(0, count)
        .map((loc) => formatHireLocationLabel(loc));
    }
    const fromList = (recommendationsSelection.locationList ?? []).slice(0, count);
    if (fromList.length > 0) return fromList;
    if (count > 0) {
      return [companyName.trim() || "Main Office"];
    }
    return [];
  }, [recommendationsSelection, companyName]);

  React.useEffect(() => {
    if (!user) return;
    const contact = hireContactFromClerkUser(user);
    if (!contact) return;
    setFirstName((current) => current.trim() || contact.firstName);
    setLastName((current) => current.trim() || contact.lastName);
    setEmail((current) => current.trim() || contact.email);
  }, [user]);

  React.useEffect(() => {
    if (!recommendationsSelection || !websiteSuggestion) return;
    const businessHint = `${websiteSuggestion.businessName} ${websiteSuggestion.summary}`.toLowerCase();
    const isHealthcareBusiness =
      websiteSuggestion.vertical === "healthcare" ||
      /dental|dentist|orthodont|smile/.test(businessHint);
    if (!isHealthcareBusiness) return;
    const names = recommendationsSelection.departmentDetails
      .slice(0, recommendationsSelection.departmentCount)
      .map((dept) => dept.name.trim());
    const churchPlaceholder =
      /^(men's ministry|women's ministry|children's ministry|student's ministry|worship|care & prayer)$/i;
    if (names.length === 0 || !names.every((name) => churchPlaceholder.test(name))) return;
    const next = defaultHireDepartmentsForVertical(
      "healthcare",
      websiteSuggestion.businessName,
    );
    const count = Math.max(1, Math.min(recommendationsSelection.departmentCount, next.length));
    handleRecommendationsChange({
      ...recommendationsSelection,
      departmentCount: count,
      departmentDetails: next.slice(0, count),
    });
  }, [recommendationsSelection, websiteSuggestion, handleRecommendationsChange]);

  React.useEffect(() => {
    if (!user || activeLocations.length === 0) return;
    const defaultContact = hireContactFromClerkUser(user);
    if (!defaultContact) return;
    setLocationLeads((prev) => {
      const next = { ...prev };
      for (const loc of activeLocations) {
        if (!next[loc]?.email?.trim()) {
          next[loc] = {
            ...defaultContact,
            phone: next[loc]?.phone?.trim() || defaultContact.phone,
          };
        }
      }
      return next;
    });
  }, [user, activeLocations]);

  React.useEffect(() => {
    onDisplayCompanyNameChange?.(companyName.trim());
  }, [companyName, onDisplayCompanyNameChange]);

  React.useEffect(() => {
    if (!splitCombinedLayout || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const isPaymentReturn = payment === "cancelled" || payment === "success";
    const stored = readHireCustomPreviewState();
    const hireIntentIdFromUrl = params.get("hireIntentId")?.trim() || null;
    const projectIdFromParams = params.get("previewProjectId")?.trim() || null;

    const restoreInvalid = () => {
      clearHireCustomPreviewState();
      setPreviewHireIntentId(null);
      setPreviewProjectId(null);
      setPreviewGenerated(false);
    };

    if (
      stored &&
      (!stored.templateProjectId ||
        stored.templateProjectId !== activeTemplateProjectId)
    ) {
      restoreInvalid();
    }

    if (!isPaymentReturn) {
      return;
    }

    const hireIntentId =
      hireIntentIdFromUrl || stored?.previewHireIntentId || null;

    if (hireIntentId && activeTemplateProjectId) {
      void utils.project.getMarketplaceHirePreviewContext
        .fetch({
          hireIntentId,
          templateProjectId: activeTemplateProjectId,
        })
        .then((ctx) => {
          if (!ctx.valid) {
            restoreInvalid();
            return;
          }
          setPreviewHireIntentId(ctx.previewHireIntentId);
          setPreviewProjectId(ctx.previewProjectId);
          setPreviewGenerated(true);
          writeHireCustomPreviewState({
            previewHireIntentId: ctx.previewHireIntentId,
            previewProjectId: ctx.previewProjectId,
            templateProjectId: ctx.templateProjectId,
          });
        })
        .catch(() => {
          restoreInvalid();
        });
    } else if (projectIdFromParams && hireIntentId) {
      setPreviewHireIntentId(hireIntentId);
      setPreviewProjectId(projectIdFromParams);
      setPreviewGenerated(true);
    }

    if (payment === "cancelled") {
      setDeployPanelTab("deploy");
      if (projectIdFromParams) setPreviewVersion((v) => Math.max(v, 1));
    }

    const clean = new URL(window.location.href);
    clean.searchParams.delete("payment");
    clean.searchParams.delete("hireIntentId");
    clean.searchParams.delete("previewProjectId");
    window.history.replaceState({}, "", clean.pathname + clean.search);
  }, [splitCombinedLayout, activeTemplateProjectId, utils.project.getMarketplaceHirePreviewContext]);

  React.useEffect(() => {
    if (!useWizardLayout || !splitCombinedLayout || typeof window === "undefined") return;
    if (!bootstrapComplete || !isSignedIn) return;
    if (hireIntentResumeRef.current || previewGenerated) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("payment")) return;

    const hireIntentIdFromUrl = params.get("hireIntentId")?.trim();
    if (!hireIntentIdFromUrl) return;

    hireIntentResumeRef.current = true;

    void utils.project.getMarketplaceHireResumeContext
      .fetch({ hireIntentId: hireIntentIdFromUrl })
      .then((ctx) => {
        if (ctx.action === "complete") {
          const params = new URLSearchParams(window.location.search);
          if (params.get("payment") === "success") {
            return;
          }
          window.location.href = buildMarketplaceHireCompletionPath(hireIntentIdFromUrl);
          return;
        }
        if (!ctx.valid) {
          hireIntentResumeRef.current = false;
          return;
        }

        if (ctx.templateProjectId !== activeTemplateProjectId) {
          setActiveTemplateProjectId(ctx.templateProjectId);
        }

        setPreviewHireIntentId(ctx.previewHireIntentId);
        setPreviewProjectId(ctx.previewProjectId);
        setPreviewGenerated(true);
        markHireBuilderOnboardingComplete();
        setOnboardingComplete(true);
        setWizardStepId("summary");
        writeHireWizardContext(ctx.websiteUrl, "summary");
        writeHireCustomPreviewState({
          previewHireIntentId: ctx.previewHireIntentId,
          previewProjectId: ctx.previewProjectId,
          templateProjectId: ctx.templateProjectId,
        });

        if (!websiteUrl.trim()) {
          setWebsiteUrl(ctx.websiteUrl);
        }

        const clean = new URL(window.location.href);
        clean.searchParams.delete("hireIntentId");
        window.history.replaceState({}, "", clean.pathname + clean.search);
      })
      .catch(() => {
        hireIntentResumeRef.current = false;
      });
  }, [
    useWizardLayout,
    splitCombinedLayout,
    bootstrapComplete,
    isSignedIn,
    previewGenerated,
    activeTemplateProjectId,
    websiteUrl,
    utils.project.getMarketplaceHireResumeContext,
  ]);

  React.useEffect(() => {
    if (!useWizardLayout || previewGenerated) return;
    if (!bootstrapComplete || !isSignedIn) return;
    const stored = readHireCustomPreviewState();
    if (!stored) return;
    // Allow restore when template was not persisted (e.g. older OAuth returns).
    if (
      stored.templateProjectId &&
      stored.templateProjectId !== activeTemplateProjectId
    ) {
      return;
    }
    if (sessionPreviewRestoreRef.current) return;

    sessionPreviewRestoreRef.current = true;

    void utils.project.getMarketplaceHireResumeContext
      .fetch({ hireIntentId: stored.previewHireIntentId })
      .then((ctx) => {
        if (ctx.action === "complete") {
          if (readStripePaymentCompletionState(stored.previewHireIntentId) === "pending") {
            return;
          }
          window.location.href = buildMarketplaceHireCompletionPath(stored.previewHireIntentId);
          return;
        }
        if (!ctx.valid) {
          clearHireCustomPreviewState();
          sessionPreviewRestoreRef.current = false;
          return;
        }

        setPreviewHireIntentId(ctx.previewHireIntentId);
        setPreviewProjectId(ctx.previewProjectId);
        setPreviewGenerated(true);
        markHireBuilderOnboardingComplete();
        setOnboardingComplete(true);
        setWizardStepId("summary");
        if (stored.agentName?.trim()) {
          setCustomAgentName(stored.agentName.trim());
          setAgentNameConfirmed(true);
        }
        if (stored.jobTitle?.trim()) setCustomJobTitle(stored.jobTitle.trim());
        if (stored.profileImageUrl?.trim()) {
          setProfilePresetUrl(stored.profileImageUrl.trim());
        }
        if (stored.agentNameConfirmed) setAgentNameConfirmed(true);
        if (ctx.websiteUrl) {
          writeHireWizardContext(ctx.websiteUrl, "summary");
        }
        writeHireCustomPreviewState({
          previewHireIntentId: ctx.previewHireIntentId,
          previewProjectId: ctx.previewProjectId,
          templateProjectId: ctx.templateProjectId,
          agentName: stored.agentName,
          jobTitle: stored.jobTitle,
          profileImageUrl: stored.profileImageUrl,
          agentNameConfirmed: stored.agentNameConfirmed ?? Boolean(stored.agentName?.trim()),
        });
        setBuilderRightTab("preview");
      })
      .catch(() => {
        clearHireCustomPreviewState();
        sessionPreviewRestoreRef.current = false;
      });
  }, [
    useWizardLayout,
    previewGenerated,
    bootstrapComplete,
    isSignedIn,
    activeTemplateProjectId,
    utils.project.getMarketplaceHireResumeContext,
  ]);

  React.useEffect(() => {
    if (!previewProjectId) return;
    const prefs = readHireAgentPreferences(previewProjectId);
    if (!prefs?.locationLeadMembers?.length) return;
    setLocationLeads((prev) => {
      const next = { ...prev };
      for (const entry of prefs.locationLeadMembers ?? []) {
        next[entry.locationName] = {
          ...(next[entry.locationName] ?? entry.leadTeamMember),
          ...entry.leadTeamMember,
        };
      }
      return next;
    });
  }, [previewProjectId]);

  const handleContinueFromRecommendations = () => {
    if (!validateAgentProfileStep()) return;
    setActiveTemplateProjectId(templateProjectId);
    setClonedTemplateDefaults(templateDefaults);
    applyRecommendationsPricing();
    setStep(companyProfileStep);
  };

  const applyRecommendationsPricing = () => {
    if (!recommendationsSelection) return;
    if (!recommendationsMode && !splitLayout) return;
    writeHireRecommendationsSelection(recommendationsSelection);
    const mappedPlan = planSelectionFromRecommendations(recommendationsSelection);
    writeHirePlanSelection(mappedPlan);
    setPlanSelection(mappedPlan);
    setResponsibilities(
      mergeHireResponsibilities(
        responsibilitiesFromRecommendations(recommendationsSelection),
        effectiveTemplateDefaults?.responsibilities,
      ),
    );
  };

  const handleFinishFromPricing = () => {
    applyRecommendationsPricing();
    void handleContinue();
  };

  const resolveSiteGlueCheckoutWebsiteUrl = (): string | null => {
    const raw =
      consultantState?.websiteUrl?.trim() ||
      websiteSuggestion?.websiteUrl?.trim() ||
      websiteUrl.trim() ||
      "";
    if (!raw) return null;
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = urlSchema.safeParse({ url: withProtocol });
    return parsed.success ? parsed.data.url : null;
  };

  const buildSiteGlueEmployerFormFromContext = (): EmployerFormValues | null => {
    const contact = user ? hireContactFromClerkUser(user) : null;
    const resolvedWebsite = resolveSiteGlueCheckoutWebsiteUrl();
    if (!contact?.email || !resolvedWebsite) return null;
    return {
      firstName: contact.firstName || firstName.trim() || "SiteGlue",
      lastName: contact.lastName || lastName.trim() || "User",
      email: contact.email,
      companyName:
        companyName.trim() ||
        consultantState?.businessName?.trim() ||
        websiteSuggestion?.businessName?.trim() ||
        "My Business",
      websiteUrl: resolvedWebsite,
      leadTeamMember: undefined,
    };
  };

  const buildSiteGlueEmployerFormFromCheckout = (
    checkout: SiteGlueCheckoutContact,
  ): EmployerFormValues | null => {
    const resolvedWebsite = resolveSiteGlueCheckoutWebsiteUrl();
    const email = checkout.email.trim();
    if (!resolvedWebsite || !email) return null;
    return {
      firstName: checkout.firstName.trim() || firstName.trim() || "SiteGlue",
      lastName: checkout.lastName.trim() || lastName.trim() || "User",
      email,
      companyName:
        companyName.trim() ||
        consultantState?.businessName?.trim() ||
        websiteSuggestion?.businessName?.trim() ||
        "My Business",
      websiteUrl: resolvedWebsite,
      leadTeamMember: undefined,
    };
  };

  const handleDeploy = async (opts?: { fromCheckout?: boolean }) => {
    applyRecommendationsPricing();
    let form: EmployerFormValues | null = null;

    // Checkout / Pay securely: never require Company Settings form fields.
    // Build from Clerk + Trish website context (state updates from the modal may not have flushed).
    if (opts?.fromCheckout && isSiteGlueBrand) {
      if (planCheckoutContactRef.current) {
        form = buildSiteGlueEmployerFormFromCheckout(planCheckoutContactRef.current);
      }
      if (!form && isSignedIn) {
        form = buildSiteGlueEmployerFormFromContext();
      }
      if (!form) {
        toast.error(
          resolveSiteGlueCheckoutWebsiteUrl()
            ? "Could not start checkout. Confirm your account email and try again."
            : "Add your organization website in chat with Trish before paying.",
        );
        return;
      }
    } else {
      form = validateForm();
      if (!form && isSiteGlueBrand && isSignedIn) {
        form = buildSiteGlueEmployerFormFromContext();
      }
      if (!form && isSiteGlueBrand && planCheckoutContactRef.current) {
        form = buildSiteGlueEmployerFormFromCheckout(planCheckoutContactRef.current);
      }
      if (!form) {
        if (isSiteGlueBrand && !isSignedIn) {
          toast.error("Create your free SiteGlue account to finish onboarding.");
        }
        if (!(isSiteGlueBrand && !isSignedIn)) {
          setStep(companyProfileStep);
        }
        return;
      }
    }

    // SiteGlue bills by catalog plan (Intern/Associate/Professional), not skill-unit totals.
    const effectiveSiteGluePlan: AiEmployeePlanId =
      consultantState?.selectedPlan ??
      consultantState?.recommendedPlan ??
      aiEmployeePlanIdFromHirePlanId(planSelection.planId);
    const totalCents = isSiteGlueBrand
      ? aiEmployeePlanPriceCents(effectiveSiteGluePlan)
      : recommendationsSelection
        ? computeRecommendationsTotalCents(recommendationsSelection)
        : 0;
    const prefs = prepareHirePrefsForMutation(form);
    if (!prefs) return;

    try {
      let hireIntentId: string;
      let clonedProjectId: string;

      if (previewHireIntentId && previewProjectId) {
        hireIntentId = previewHireIntentId;
        clonedProjectId = previewProjectId;
        await preparePreviewMutation.mutateAsync({
          hireIntentId,
          hirePreferences: prefs,
        });
      } else {
        const guestSessionId =
          typeof document !== "undefined"
            ? document.cookie
                .split("; ")
                .find((row) => row.startsWith("ez-gpt-session="))
                ?.split("=")[1]
            : undefined;

        const forceNewAgent = isHireForceNewAgent();
        const started = await startHireMutation.mutateAsync({
          marketingBrand: prefs.marketingBrand,
          templateProjectId: activeTemplateProjectId,
          websiteUrl: form.websiteUrl,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          guestSessionId,
          hirePreferences: prefs,
          forceNewAgent,
        });
        if (forceNewAgent) clearHireForceNewAgent();
        hireIntentId = started.hireIntentId;
        clonedProjectId = started.clonedProjectId;
        setPreviewHireIntentId(hireIntentId);
      }

      persistSessionData(clonedProjectId, form);
      setPreviewProjectId(clonedProjectId);
      writeHireCustomPreviewState({
        previewHireIntentId: hireIntentId,
        previewProjectId: clonedProjectId,
        templateProjectId: activeTemplateProjectId,
      });

      if (totalCents > 0) {
        if (!isSignedIn) {
          setPendingHire({
            hireIntentId,
            websiteUrl: form.websiteUrl,
            form: {
              firstName: form.firstName,
              lastName: form.lastName,
              email: form.email,
              leadTeamMember: form.leadTeamMember,
            },
          });
          writePendingHireIntentId(hireIntentId);
          setAuthPhase("signup");
          if (isSiteGlueBrand) {
            setAuthModalOpen(false);
            setAuthGateOnly(true);
            setBuilderRightTab("hire");
            return;
          }
          setAuthModalOpen(true);
          return;
        }

        const successUrl = new URL(window.location.href);
        successUrl.searchParams.set("hireIntentId", hireIntentId);
        successUrl.searchParams.set("payment", "success");
        successUrl.searchParams.delete("cancelled");

        const cancelUrl = new URL(window.location.href);
        cancelUrl.searchParams.set("payment", "cancelled");
        cancelUrl.searchParams.set("hireIntentId", hireIntentId);
        cancelUrl.searchParams.set("previewProjectId", clonedProjectId);

        writeHireCustomPreviewState({
          previewHireIntentId: hireIntentId,
          previewProjectId: clonedProjectId,
          templateProjectId: activeTemplateProjectId,
        });

        const checkout = await createCheckoutMutation.mutateAsync({
          hireIntentId,
          totalCents,
          successUrl: successUrl.href,
          cancelUrl: cancelUrl.href,
          ...(isSiteGlueBrand ? { marketingBrand: "siteglue" as const } : {}),
        });
        window.location.assign(checkout.checkoutUrl);
        return;
      }

      if (isSignedIn) {
        await finishHire(hireIntentId, form.websiteUrl, form, {
          redirectToDashboardProfile: splitLayout,
        });
        return;
      }

      setPendingHire({
        hireIntentId,
        websiteUrl: form.websiteUrl,
        form: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          leadTeamMember: form.leadTeamMember,
        },
      });
      writePendingHireIntentId(hireIntentId);
      setAuthPhase("signup");
      if (isSiteGlueBrand) {
        setAuthModalOpen(false);
        setAuthGateOnly(true);
        setBuilderRightTab("hire");
        return;
      }
      setAuthModalOpen(true);
    } catch (error) {
      toast.error(
        getTrpcErrorMessage(
          error,
          "Could not deploy your agent. Try again or contact support if this continues.",
        ),
      );
    }
  };

  const buildPreviewEmployerForm = (): EmployerFormValues | null => {
    // Guest/auto preview: avoid "Enter your first and last name" toasts during generate.
    const complete = validateEmployerStep({ silent: true });
    if (complete) return complete;

    const websiteCandidate =
      websiteUrl.trim() || websiteSuggestion?.websiteUrl?.trim() || "";
    const parsed = urlSchema.safeParse({ url: websiteCandidate });
    if (!parsed.success) {
      return null;
    }

    const contact = user ? hireContactFromClerkUser(user) : null;
    if (contact?.email) {
      return {
        firstName: contact.firstName || firstName.trim() || "Preview",
        lastName: contact.lastName || lastName.trim() || "User",
        email: contact.email || email.trim(),
        companyName: companyName.trim() || websiteSuggestion?.businessName || "My Business",
        websiteUrl: parsed.data.url,
        leadTeamMember: undefined,
      };
    }

    // Pre–Sign Up Preview: placeholder contact tied to browser guest session.
    // Claimed to the real Clerk email when the user signs up.
    const guestSessionId = readGuestSessionId();
    if (!guestSessionId) {
      return null;
    }

    const displayCompany =
      companyName.trim() || websiteSuggestion?.businessName || "My Business";
    return {
      firstName: firstName.trim() || displayCompany.split(/\s+/)[0] || "Guest",
      lastName: lastName.trim() || "Preview",
      email: buildGuestPreviewHireEmail(guestSessionId),
      companyName: displayCompany,
      websiteUrl: parsed.data.url,
      leadTeamMember: undefined,
    };
  };

  const handleGenerateAgent = async (opts?: { manual?: boolean }) => {
    const silentValidation = autoGeneratePreview && !opts?.manual;

    if (!validateAgentProfileStep({ silent: silentValidation })) {
      if (opts?.manual) setStep(overviewStep);
      return;
    }

    if (
      autoGeneratePreview &&
      !opts?.manual &&
      (autoGenerateAttemptedRef.current || autoGenerateInFlightRef.current)
    ) {
      return;
    }

    applyRecommendationsPricing();
    onPreviewGenerateStart?.();
    if (autoGeneratePreview && !opts?.manual) {
      autoGenerateAttemptedRef.current = true;
    }
    autoGenerateInFlightRef.current = true;
    setIsGenerating(true);
    try {
      const form = buildPreviewEmployerForm();
      if (!form) {
        if (autoGeneratePreview && !opts?.manual) {
          autoGenerateAttemptedRef.current = false;
        }
        const message = "Complete Company Settings before generating your agent preview.";
        onPreviewGenerateError?.(message);
        toast.error(message);
        setStep(companyProfileStep);
        return;
      }

      const guestSessionId = readGuestSessionId();

      const prefs = prepareHirePrefsForMutation(form);
      if (!prefs) {
        setIsGenerating(false);
        autoGenerateInFlightRef.current = false;
        return;
      }
      skipWidgetColorSyncUntilEnrichRef.current = true;
      const forceNewAgent = isHireForceNewAgent();
      const started = await startHireMutation.mutateAsync({
        marketingBrand: prefs.marketingBrand,
        templateProjectId: activeTemplateProjectId,
        websiteUrl: form.websiteUrl,
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        guestSessionId,
        hirePreferences: prefs,
        forceNewAgent,
      });
      if (forceNewAgent) clearHireForceNewAgent();

      const previewResult = await preparePreviewMutation.mutateAsync({
        hireIntentId: started.hireIntentId,
        hirePreferences: prefs,
        ...(isSignedIn ? {} : guestSessionId ? { guestSessionId } : {}),
      });

      await switchToHireOrganizationIfNeeded(previewResult.organizationId);

      persistSessionData(started.clonedProjectId, form);
      setPreviewProjectId(started.clonedProjectId);
      setPreviewHireIntentId(started.hireIntentId);
      setPreviewGenerated(true);
      markHireBuilderOnboardingComplete();
      setOnboardingComplete(true);
      setWizardStepId("summary");
      writeHireWizardContext(form.websiteUrl, "summary");
      setBuilderRightTab(isSiteGlueBrand ? "live_preview" : "preview");
      writeHireCustomPreviewState({
        previewHireIntentId: started.hireIntentId,
        previewProjectId: started.clonedProjectId,
        templateProjectId: activeTemplateProjectId,
        agentName: prefs.agentName,
        jobTitle: prefs.titleHint,
        profileImageUrl: prefs.profileImageUrl,
        agentNameConfirmed: true,
      });
      setAgentNameConfirmed(true);
      setPreviewVersion((v) => v + 1);
      toast.success(
        isSiteGlueBrand
          ? "AI Employee ready — Preview shows your website with the chat widget."
          : "Agent profile preview is ready.",
      );
      onPreviewGenerateComplete?.();
      void enrichPreviewFromWebsite(
        form.websiteUrl,
        started.clonedProjectId,
        started.hireIntentId,
        form,
      );
    } catch (error) {
      skipWidgetColorSyncUntilEnrichRef.current = false;
      const message =
        error instanceof Error ? error.message : "Could not generate agent preview.";
      if (autoGeneratePreview && !opts?.manual) {
        autoGenerateAttemptedRef.current = false;
      }
      onPreviewGenerateError?.(message);
      toast.error(message);
    } finally {
      autoGenerateInFlightRef.current = false;
      setIsGenerating(false);
    }
  };

  React.useEffect(() => {
    if (
      wizardMode ||
      !autoGeneratePreview ||
      !bootstrapComplete ||
      !splitLayout ||
      !showPlanStep ||
      previewGenerated ||
      autoGenerateAttemptedRef.current ||
      autoGenerateInFlightRef.current ||
      (!isSignedIn && !isSiteGlueBrand) ||
      !websiteSuggestion ||
      !recommendationsSelection ||
      !onboardingComplete
    ) {
      return;
    }

    if (personaGenerating && !resolvedAgentPersona()) {
      return;
    }

    if (!customAgentName.trim() || !customJobTitle.trim()) {
      return;
    }

    const timer = window.setTimeout(() => {
      void handleGenerateAgent();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    wizardMode,
    autoGeneratePreview,
    bootstrapComplete,
    splitLayout,
    showPlanStep,
    previewGenerated,
    isSignedIn,
    websiteSuggestion,
    recommendationsSelection,
    personaGenerating,
    customPersona,
    customAgentName,
    customJobTitle,
    companyName,
    websiteUrl,
    email,
    firstName,
    lastName,
    onboardingComplete,
  ]);

  const widgetColorsSyncKey = React.useMemo(
    () =>
      [
        trainingDraft.widgetColors.headerColor,
        trainingDraft.widgetColors.widgetButtonColor,
        trainingDraft.widgetColors.launcherBackgroundColor ?? "",
        trainingDraft.widgetColors.textColor ?? "",
      ].join("|"),
    [trainingDraft.widgetColors],
  );

  React.useEffect(() => {
    if (!splitLayout || !previewHireIntentId || !previewProjectId || !isSignedIn) return;
    if (skipWidgetColorSyncUntilEnrichRef.current && !userEditedWidgetColorsRef.current) {
      return;
    }

    const form = buildPreviewEmployerForm();
    if (!form) return;

    const timer = setTimeout(() => {
      const prefs = prepareHirePrefsForMutation(form, { silent: true });
      if (!prefs) return;

      void preparePreviewMutation
        .mutateAsync({
          hireIntentId: previewHireIntentId,
          hirePreferences: prefs,
        })
        .then(() => {
          setPreviewRefreshKey((k) => k + 1);
        })
        .catch(() => {
          toast.error("Could not update agent preview. Please try again.");
        });
    }, 800);

    return () => clearTimeout(timer);
  }, [
    splitLayout,
    previewHireIntentId,
    previewProjectId,
    isSignedIn,
    recommendationsSelection,
    customAgentName,
    customJobTitle,
    customPersona,
    profilePresetUrl,
    companyName,
    firstName,
    lastName,
    email,
    websiteUrl,
    locationLeads,
    leadPhone,
    leadFirstName,
    leadLastName,
    leadEmail,
    recommendationsSelection?.teamHandoffCtaClusters,
    recommendationsSelection?.teamHandoffEnabled,
    widgetColorsSyncKey,
  ]);

  const handleContinueFromPlan = () => {
    if (recommendationsMode && recommendationsSelection) {
      writeHireRecommendationsSelection(recommendationsSelection);
      const mappedPlan = planSelectionFromRecommendations(recommendationsSelection);
      writeHirePlanSelection(mappedPlan);
      setPlanSelection(mappedPlan);
      setResponsibilities(
        mergeHireResponsibilities(
          responsibilitiesFromRecommendations(recommendationsSelection),
          effectiveTemplateDefaults?.responsibilities,
        ),
      );
    } else {
      writeHirePlanSelection(planSelection);
    }
    setStep(agentProfileStep);
  };

  const handleNextFromCompanyProfile = () => {
    if (!validateEmployerStep()) return;
    setResponsibilities(resolveHireResponsibilities());
    if (threeStepRecommendations && !splitLayout) {
      setStep(pricingStep);
    }
  };

  const handleNextFromAgentProfile = () => {
    if (!validateAgentProfileStep()) return;
    setStep(companyProfileStep);
  };

  const resolvedAgentPersona = (): string =>
    customPersona.trim() ||
    websiteSuggestion?.recommendations?.agentProfile?.persona?.trim() ||
    "";

  const validateAgentProfileStep = (opts?: { silent?: boolean }): boolean => {
    if (!customAgentName.trim()) {
      if (!opts?.silent) toast.error("Enter your agent's name.");
      return false;
    }
    if (!customJobTitle.trim()) {
      if (!opts?.silent) toast.error("Enter your agent's job title.");
      return false;
    }
    if (threeStepRecommendations && !resolvedAgentPersona()) {
      if (personaGenerating) {
        if (!opts?.silent) {
          toast.error("Please wait while we generate your agent persona from your website.");
        }
        return false;
      }
      if (!opts?.silent) toast.error("Enter your agent's persona.");
      return false;
    }
    return true;
  };

  const persistSessionData = (
    projectId: string,
    form: EmployerFormValues,
  ) => {
    const prefs = buildHirePreferences(form);
    writeHireAgentPreferences(projectId, prefs);
    try {
      const primaryLoc = activeLocations[0];
      const leadForm = primaryLoc && locationLeads[primaryLoc]
        ? hireContactToLeadForm(locationLeads[primaryLoc])
        : form.leadTeamMember
          ? hireContactToLeadForm(form.leadTeamMember)
          : {
              name: `${form.firstName} ${form.lastName}`.trim(),
              firstName: form.firstName,
              lastName: form.lastName,
              jobTitle: "Lead Team Member",
              email: form.email,
              phone: "",
            };
      sessionStorage.setItem(newProjectLeadSessionKey(projectId), JSON.stringify(leadForm));
    } catch {
      /* ignore */
    }
  };

  const finishHire = React.useCallback(
    async (
      hireIntentId: string,
      _website: string,
      form: EmployerFormValues,
      options?: { stayOnPage?: boolean; redirectToDashboardProfile?: boolean },
    ) => {
      const prefsForm = form.companyName?.trim() ? form : buildPreviewEmployerForm();
      const prefs = prefsForm ? prepareHirePrefsForMutation(prefsForm, { silent: true }) : null;
      if (prefs) {
        try {
          await preparePreviewMutation.mutateAsync({
            hireIntentId,
            hirePreferences: prefs,
          });
        } catch (error) {
          console.warn("[finishHire] failed to flush hire preferences before complete:", error);
        }
      }

      const result = await completeHireMutation.mutateAsync({
        hireIntentId,
        marketingBrand: prefs?.marketingBrand,
      });
      await switchToHireOrganizationIfNeeded(result.organizationId);
      persistSessionData(result.projectId, form);
      setAuthModalOpen(false);
      setPendingHire(null);
      clearPendingHireIntentId();

      const websiteForImport =
        _website.trim() || result.websiteUrl?.trim() || "";

      if (splitLayout && options?.stayOnPage) {
        setPreviewProjectId(result.projectId);
        setDeployedProjectId(result.projectId);
        toast.success(`${customAgentName.trim() || "Your agent"} is ready to deploy on your website.`);
        onSuccess?.();
        return;
      }

      if (options?.redirectToDashboardProfile) {
        toast.success(
          `${customAgentName.trim() || "Your agent"} is ready — opening My Profile in your dashboard.`,
        );
        onSuccess?.();
        navigateToAppUrl(
          buildProjectDashboardProfileUrl(result.projectId, {
            importWebsite: websiteForImport || undefined,
          }),
        );
        return;
      }

      toast.success(`${customAgentName.trim() || "Your agent"} is ready — opening your dashboard.`);
      onSuccess?.();
      setWebsiteUrl("");
      setCompanyName("");
      setLeadFirstName("");
      setLeadLastName("");
      setLeadEmail("");
      setLeadPhone("");
      const settingsPath = websiteForImport
        ? `/projects/${result.projectId}/settings?importWebsite=${encodeURIComponent(websiteForImport)}`
        : `/projects/${result.projectId}/settings`;
      navigateToAppUrl(`${resolveEasyGptAppOrigin()}${settingsPath}`);
    },
    [
      completeHireMutation,
      preparePreviewMutation,
      prepareHirePrefsForMutation,
      buildPreviewEmployerForm,
      switchToHireOrganizationIfNeeded,
      customAgentName,
      onSuccess,
      splitLayout,
    ],
  );

  React.useEffect(() => {
    if (!splitLayout || !isSignedIn || paymentHandledRef.current) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") !== "success") return;

    const hireIntentId = resolveStripeReturnHireIntentId(params);
    if (!hireIntentId) return;

    const completionState = readStripePaymentCompletionState(hireIntentId);
    if (completionState === "done" || completionState === "pending") return;

    paymentHandledRef.current = true;
    writeStripePaymentCompletionState(hireIntentId, "pending");

    void (async () => {
      try {
        await finishHire(hireIntentId, websiteUrl || params.get("website") || "", {
          firstName,
          lastName,
          email,
          companyName,
          websiteUrl: websiteUrl || "",
          leadTeamMember: undefined,
        }, { redirectToDashboardProfile: true });
        writeStripePaymentCompletionState(hireIntentId, "done");
        const clean = new URL(window.location.href);
        clean.searchParams.delete("payment");
        clean.searchParams.delete("hireIntentId");
        window.history.replaceState({}, "", clean.pathname + clean.search);
      } catch (error) {
        clearStripePaymentCompletionState(hireIntentId);
        paymentHandledRef.current = false;
        toast.error(
          getTrpcErrorMessage(error, "Payment succeeded but deploy failed."),
        );
      }
    })();
  }, [splitLayout, isSignedIn, finishHire, firstName, lastName, email, companyName, websiteUrl]);

  const handleAuthVerified = React.useCallback(async () => {
    if (!pendingHire) {
      // SiteGlue auth gate (Test or Onboard): wait for Clerk session; do not force deploy here.
      if (
        !authGateOnly &&
        !pendingDeployAfterAuthRef.current &&
        !pendingOpenTabAfterAuthRef.current
      ) {
        return;
      }
      setAuthModalOpen(false);
      setAuthGateOnly(false);
      return;
    }
    try {
      const effectiveSiteGluePlan: AiEmployeePlanId =
        consultantState?.selectedPlan ??
        consultantState?.recommendedPlan ??
        aiEmployeePlanIdFromHirePlanId(planSelection.planId);
      const totalCents = isSiteGlueBrand
        ? aiEmployeePlanPriceCents(effectiveSiteGluePlan)
        : recommendationsSelection
          ? computeRecommendationsTotalCents(recommendationsSelection)
          : 0;

      if (splitLayout && totalCents > 0) {
        const successUrl = new URL(window.location.href);
        successUrl.searchParams.set("hireIntentId", pendingHire.hireIntentId);
        successUrl.searchParams.set("payment", "success");
        successUrl.searchParams.delete("cancelled");

        const cancelUrl = new URL(window.location.href);
        cancelUrl.searchParams.set("payment", "cancelled");
        cancelUrl.searchParams.set("hireIntentId", pendingHire.hireIntentId);
        if (previewProjectId) {
          cancelUrl.searchParams.set("previewProjectId", previewProjectId);
        }

        if (previewProjectId) {
          writeHireCustomPreviewState({
            previewHireIntentId: pendingHire.hireIntentId,
            previewProjectId,
            templateProjectId: activeTemplateProjectId,
          });
        }

        const checkout = await createCheckoutMutation.mutateAsync({
          hireIntentId: pendingHire.hireIntentId,
          totalCents,
          successUrl: successUrl.href,
          cancelUrl: cancelUrl.href,
          ...(isSiteGlueBrand ? { marketingBrand: "siteglue" as const } : {}),
        });
        window.location.assign(checkout.checkoutUrl);
        return;
      }

      await finishHire(
        pendingHire.hireIntentId,
        pendingHire.websiteUrl,
        pendingHire.form,
        { redirectToDashboardProfile: splitLayout },
      );
    } catch (error) {
      toast.error(
        getTrpcErrorMessage(error, "Could not complete hiring this agent."),
      );
    }
  }, [
    pendingHire,
    authGateOnly,
    finishHire,
    splitLayout,
    recommendationsSelection,
    createCheckoutMutation,
    previewProjectId,
    isSiteGlueBrand,
    activeTemplateProjectId,
    consultantState?.selectedPlan,
    consultantState?.recommendedPlan,
    planSelection.planId,
  ]);

  // After Sign Up: open the tab that requested auth (Preview / Test / Onboard).
  React.useEffect(() => {
    if (!isSiteGlueBrand || !isSignedIn || !user) return;
    const nextTab = pendingOpenTabAfterAuthRef.current;
    if (!nextTab) return;
    if (!bootstrapComplete) return;

    pendingOpenTabAfterAuthRef.current = null;
    setAuthModalOpen(false);
    setAuthGateOnly(false);

    const contact = hireContactFromClerkUser(user);
    if (contact?.email) {
      if (!firstName.trim() && contact.firstName) setFirstName(contact.firstName);
      if (!lastName.trim() && contact.lastName) setLastName(contact.lastName);
      if (!email.trim()) setEmail(contact.email);
    }
    const siteName =
      companyName.trim() ||
      consultantState?.businessName?.trim() ||
      websiteSuggestion?.businessName?.trim();
    if (siteName && !companyName.trim()) setCompanyName(siteName);
    const siteUrl =
      websiteUrl.trim() ||
      consultantState?.websiteUrl?.trim() ||
      websiteSuggestion?.websiteUrl?.trim();
    if (siteUrl && !websiteUrl.trim()) setWebsiteUrl(siteUrl);

    setBuilderRightTab(nextTab);
  }, [
    isSiteGlueBrand,
    isSignedIn,
    user,
    bootstrapComplete,
    firstName,
    lastName,
    email,
    companyName,
    websiteUrl,
    consultantState?.businessName,
    consultantState?.websiteUrl,
    websiteSuggestion?.businessName,
    websiteSuggestion?.websiteUrl,
  ]);

  // After Sign Up from Onboard click: finish hire with Clerk identity + consult context.
  React.useEffect(() => {
    if (!isSiteGlueBrand || !isSignedIn || !user) return;
    if (!pendingDeployAfterAuthRef.current) return;
    if (!bootstrapComplete) return;

    pendingDeployAfterAuthRef.current = false;
    setAuthModalOpen(false);
    setAuthGateOnly(false);

    const contact = hireContactFromClerkUser(user);
    if (contact?.email) {
      if (!firstName.trim() && contact.firstName) setFirstName(contact.firstName);
      if (!lastName.trim() && contact.lastName) setLastName(contact.lastName);
      if (!email.trim()) setEmail(contact.email);
    }
    const siteName =
      companyName.trim() ||
      consultantState?.businessName?.trim() ||
      websiteSuggestion?.businessName?.trim();
    if (siteName && !companyName.trim()) setCompanyName(siteName);
    const siteUrl =
      websiteUrl.trim() ||
      consultantState?.websiteUrl?.trim() ||
      websiteSuggestion?.websiteUrl?.trim();
    if (siteUrl && !websiteUrl.trim()) setWebsiteUrl(siteUrl);

    const timer = window.setTimeout(() => {
      void handleDeploy();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    isSiteGlueBrand,
    isSignedIn,
    user,
    bootstrapComplete,
    firstName,
    lastName,
    email,
    companyName,
    websiteUrl,
    consultantState?.businessName,
    consultantState?.websiteUrl,
    websiteSuggestion?.businessName,
    websiteSuggestion?.websiteUrl,
  ]);

  const handleContinue = async () => {
    const form = validateForm();
    if (!form) return;

    try {
      const guestSessionId =
        typeof document !== "undefined"
          ? document.cookie
              .split("; ")
              .find((row) => row.startsWith("ez-gpt-session="))
              ?.split("=")[1]
          : undefined;

      const prefs = prepareHirePrefsForMutation(form);
      if (!prefs) return;

      const forceNewAgent = isHireForceNewAgent();
      const started = await startHireMutation.mutateAsync({
        marketingBrand: prefs.marketingBrand,
        templateProjectId: activeTemplateProjectId,
        websiteUrl: form.websiteUrl,
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        guestSessionId,
        hirePreferences: prefs,
        forceNewAgent,
      });
      if (forceNewAgent) clearHireForceNewAgent();

      persistSessionData(started.clonedProjectId, form);

      if (isSignedIn) {
        await finishHire(started.hireIntentId, form.websiteUrl, form);
        return;
      }

      setPendingHire({
        hireIntentId: started.hireIntentId,
        websiteUrl: form.websiteUrl,
        form: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          leadTeamMember: form.leadTeamMember,
        },
      });
      writePendingHireIntentId(started.hireIntentId);
      setAuthPhase("signup");
      setAuthModalOpen(true);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not start hiring this agent.",
      );
    }
  };

  const stepLabels = threeStepRecommendations
    ? splitLayout
      ? ([
          { n: 1 as const, label: "Recommendations" },
          { n: 2 as const, label: "Company Settings" },
        ] as const)
      : ([
          { n: 1 as const, label: "Recommendations" },
          { n: 2 as const, label: "Company Profile" },
          { n: 3 as const, label: "Agent Pricing" },
        ] as const)
    : showPlanStep
      ? ([
          {
            n: 1 as const,
            label: recommendationsMode ? "Recommendations" : "Select plan",
          },
          { n: 2 as const, label: "Agent Profile" },
          { n: 3 as const, label: "Company Profile" },
        ] as const)
      : ([
          { n: 1 as const, label: "Agent Profile" },
          { n: 2 as const, label: "Company Profile" },
        ] as const);

  const stepSubtitle = (() => {
    if (splitCombinedLayout) {
      return "Review your Inbound GPT profile and company settings, then generate a preview and deploy.";
    }
    if (threeStepRecommendations && step === 1) {
      return splitLayout
        ? "Step 1 of 2 — Review and edit your recommended agent profile and settings."
        : "Step 1 of 3 — Review and edit your recommended agent profile and settings.";
    }
    if (threeStepRecommendations && step === 2) {
      return splitLayout
        ? "Step 2 of 2 — Tell us about your business so we can personalize your agent."
        : "Step 2 of 3 — Tell us about your business so we can personalize your agent.";
    }
    if (threeStepRecommendations && step === 3) {
      return "Step 3 of 3 — Adjust skills, usage, and pricing for your agent.";
    }
    if (showPlanStep && step === 1) {
      return recommendationsMode
        ? "Step 1 of 3 — Review your recommended agent setup and monthly pricing."
        : "Step 1 of 3 — Choose the plan and options for your Inbound GPT agent.";
    }
    if (step === companyProfileStep) {
      return showPlanStep
        ? "Step 3 of 3 — Tell us about your business so we can personalize your agent."
        : "Step 2 of 2 — Tell us about your business so we can personalize your agent.";
    }
    return showPlanStep
      ? "Step 2 of 3 — Choose your agent's name, photo, and job title."
      : "Step 1 of 2 — Choose your agent's name, photo, and job title.";
  })();

  const siteGluePlanId: AiEmployeePlanId =
    consultantState?.selectedPlan ??
    consultantState?.recommendedPlan ??
    aiEmployeePlanIdFromHirePlanId(planSelection.planId);

  const recommendationsTotalCents = recommendationsSelection
    ? isSiteGlueBrand
      ? aiEmployeePlanPriceCents(siteGluePlanId)
      : computeRecommendationsTotalCents(recommendationsSelection)
    : 0;
  const recommendationsTotalLabel = isSiteGlueBrand
    ? formatAiEmployeePlanHeaderLabel(siteGluePlanId)
    : formatHireTotalPrice(recommendationsTotalCents);
  const pricingLineItems = recommendationsSelection
    ? summarizeRecommendationsPricing(recommendationsSelection)
    : [];
  const isFreeDeploy = recommendationsTotalCents === 0;

  const resolvedPrimaryWebsiteUrl =
    websiteUrl.trim() || websiteSuggestion?.websiteUrl?.trim() || trainingDraft.primaryWebsiteUrl;

  const resolvedBusinessName =
    resolveHireBusinessDisplayName(
      companyName.trim() ||
        websiteSuggestion?.businessName ||
        recommendationsBusinessName ||
        "",
      resolvedPrimaryWebsiteUrl || websiteSuggestion?.websiteUrl,
    ) || "your business";

  const accountSummaryLabel = isSignedIn
    ? `Account: ${[firstName, lastName].filter(Boolean).join(" ") || "Signed in user"}${email ? ` · ${email}` : ""}`
    : undefined;

  const organizationLayout = React.useMemo(
    () =>
      resolveHireOrganizationLayout(
        websiteSuggestion?.vertical ?? "general",
        `${resolvedBusinessName} ${websiteSuggestion?.summary ?? ""}`,
      ),
    [websiteSuggestion?.vertical, websiteSuggestion?.summary, resolvedBusinessName],
  );

  const organizationVisibleSections = React.useMemo(() => {
    const sections = getOrganizationVisibleSections(organizationLayout);
    // Guests: hide Team Members until Sign Up; keep locations/departments editable.
    if (isSiteGlueBrand && !isSignedIn) {
      return sections.filter((s) => s !== "team");
    }
    return sections;
  }, [organizationLayout, isSiteGlueBrand, isSignedIn]);

  /** Website scrape finished enough to show gated Sign Up on Profile / Preview / Test / Onboard. */
  const websiteScraped = Boolean(
    bootstrapComplete &&
      (websiteSuggestion?.websiteUrl?.trim() || consultantState?.websiteUrl?.trim()),
  );

  const hireAuthUrls = React.useMemo(() => {
    const path =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : MARKETING_BUILD_CUSTOM;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      signInUrl: buildSignInHref({ redirect_url: path }),
      completionRedirectUrl: origin ? `${origin}${path}` : MARKETING_BUILD_CUSTOM,
    };
  }, []);

  const requestSignUpForTab = React.useCallback(
    (tab: HireBuilderRightTab) => {
      // SiteGlue: navigate to the tab and show inline Sign Up (no popup modal).
      pendingOpenTabAfterAuthRef.current = tab;
      pendingDeployAfterAuthRef.current = false;
      setAuthPhase("signup");
      setAuthGateOnly(true);
      setAuthModalOpen(false);
      setBuilderRightTab(tab);
    },
    [],
  );

  const handleBuilderRightTabChange = React.useCallback(
    (tab: HireBuilderRightTab) => {
      // Preview + Test are available after scrape/preview generate (guests included).
      // Onboard still gates to inline Sign Up for guests.
      if (isSiteGlueBrand && !isSignedIn && tab === "hire") {
        pendingOpenTabAfterAuthRef.current = tab;
        setAuthGateOnly(true);
        setAuthPhase("signup");
      }
      setBuilderRightTab(tab);
    },
    [isSiteGlueBrand, isSignedIn],
  );

  const renderSiteGlueAuthGatedTab = React.useCallback(
    (args: {
      tab: HireBuilderRightTab;
      placeholder: React.ReactNode;
      content: React.ReactNode;
    }) => {
      if (!isSiteGlueBrand || isSignedIn) return args.content;
      if (!websiteScraped) return args.placeholder;
      // Only mount Clerk on the active tab. Inactive gated tabs stay mounted in the
      // tabbed layout; mounting SignUp there portals a "Create your account" dialog
      // behind the Onboarding Progress modal.
      if (builderRightTab !== args.tab) {
        return <div className="h-full min-h-[520px]" aria-hidden />;
      }
      return (
        <HireOnboardingAuthPanel
          phase={authPhase}
          signInUrl={hireAuthUrls.signInUrl}
          hireCompletionRedirectUrl={hireAuthUrls.completionRedirectUrl}
          initialEmail={signupPrefill.email}
          initialFirstName={signupPrefill.firstName}
          initialLastName={signupPrefill.lastName}
          productLabel="SiteGlue"
          onVerified={() => {
            pendingOpenTabAfterAuthRef.current = args.tab;
            void handleAuthVerified();
          }}
        />
      );
    },
    [
      isSiteGlueBrand,
      isSignedIn,
      websiteScraped,
      builderRightTab,
      authPhase,
      hireAuthUrls.signInUrl,
      hireAuthUrls.completionRedirectUrl,
      signupPrefill.email,
      signupPrefill.firstName,
      signupPrefill.lastName,
      handleAuthVerified,
    ],
  );

  const applyGeneratedCompanyProfile = (
    result: HireWizardWebsiteGenerationResult,
    sourceWebsite: string,
    opts?: { scope?: "all" | "locations" | "departments" },
  ) => {
    const scope = opts?.scope ?? "all";
    const profileText = enrichCompanyProfileWithScrapedData(
      buildCompanyProfileDisplayText(result),
      result,
      {
        businessName: result.practiceName,
        locationList: websiteSuggestion?.recommendations.locationList,
      },
    );
    const leadEmail =
      leadFields.email.trim() ||
      employerFields.email.trim() ||
      email.trim() ||
      "";

    if (scope === "all" && result.practiceName?.trim()) {
      setCompanyName(
        resolveHireBusinessDisplayName(result.practiceName.trim(), sourceWebsite),
      );
    }

    const extractedColors = {
      ...(result.headerColor
        ? {
            headerColor: result.headerColor,
            // Match button/launcher to brand when extraction succeeds.
            widgetButtonColor: result.widgetButtonColor ?? result.headerColor,
            launcherBackgroundColor:
              result.launcherBackgroundColor ??
              result.widgetButtonColor ??
              result.headerColor,
          }
        : {}),
      ...(result.widgetButtonColor ? { widgetButtonColor: result.widgetButtonColor } : {}),
      ...(result.launcherBackgroundColor
        ? { launcherBackgroundColor: result.launcherBackgroundColor }
        : {}),
      ...(result.textColor ? { textColor: result.textColor } : {}),
    };

    if (scope === "all") {
      setTrainingDraft((current) => ({
        ...current,
        primaryWebsiteUrl: sourceWebsite,
        additionalWebsiteUrls: [sourceWebsite],
        scrapedPageUrls: result.scrapedPageUrls ?? current.scrapedPageUrls,
        companyProfileGenerated: true,
        companyProfileText: profileText,
        widgetColors: {
          ...current.widgetColors,
          ...extractedColors,
        },
      }));
    }

    if (recommendationsSelection) {
      const resolvedCompany =
        resolveHireBusinessDisplayName(
          result.practiceName?.trim() || companyName.trim(),
          sourceWebsite,
        ) || companyName.trim() || "Main Office";
      const locationDetails = dedupeHireLocationDetails(
        mapGeneratedLocationsToHireDetails(
          result.locations ?? [],
          leadEmail,
          resolvedCompany,
        ),
      );
      // Prefer the richest Organization Profile text (Service Offerings) for departments.
      const profileSource = [
        trainingDraft.companyProfileText?.trim(),
        profileText.trim(),
      ]
        .filter((t): t is string => Boolean(t && t.length > 40))
        .sort((a, b) => b.length - a.length)[0] ?? profileText;
      const departmentDefaults = defaultHireDepartmentsForVertical(
        websiteSuggestion?.vertical ?? "general",
        profileSource,
      );
      const locationPhone =
        locationDetails[0]?.phone?.trim() ||
        websiteSuggestion?.recommendations.leadTeamHints?.phone?.trim();

      if (scope === "locations") {
        // Locations only — keep existing departments untouched.
        const nextSelection = ensureHireLocationDetailsInSelection(
          {
            ...recommendationsSelection,
            locationCount: Math.max(1, locationDetails.length || 1),
            locationDetails:
              locationDetails.length > 0
                ? locationDetails
                : recommendationsSelection.locationDetails,
            locationList:
              locationDetails.length > 0
                ? locationDetailsToList(locationDetails)
                : recommendationsSelection.locationList,
            departmentCount: recommendationsSelection.departmentCount,
            departmentDetails: recommendationsSelection.departmentDetails,
          },
          {
            companyName: resolvedCompany,
            leadTeamMemberEmail: leadEmail,
            phone: locationPhone,
          },
        );
        handleRecommendationsChange(nextSelection);
        return;
      }

      if (scope === "departments") {
        // Departments only — keep existing locations untouched.
        const nextSelection = ensureHireDepartmentDetailsInSelection(
          {
            ...recommendationsSelection,
            locationCount: recommendationsSelection.locationCount,
            locationDetails: recommendationsSelection.locationDetails,
            locationList: recommendationsSelection.locationList,
            departmentCount: Math.max(1, departmentDefaults.length),
            departmentDetails: departmentDefaults,
          },
          { leadTeamMemberEmail: leadEmail },
        );
        handleRecommendationsChange(nextSelection);
        return;
      }

      let nextSelection = applyOrganizationLayoutToSelection(
        ensureHireLocationDetailsInSelection(
          {
            ...recommendationsSelection,
            locationCount: Math.max(1, locationDetails.length),
            locationDetails,
            locationList: locationDetailsToList(locationDetails),
            departmentCount: departmentDefaults.length,
            departmentDetails: departmentDefaults,
          },
          {
            companyName: resolvedCompany,
            leadTeamMemberEmail: leadEmail,
            phone: locationPhone,
          },
        ),
        organizationLayout,
        {
          vertical: websiteSuggestion?.vertical ?? "general",
          businessName: resolvedCompany,
          contentHint: profileText,
          leadTeamMemberEmail: leadEmail,
          phone: locationPhone,
        },
      );
      if (result.teamHandoffCtaClusters?.length) {
        nextSelection = {
          ...nextSelection,
          teamHandoffEnabled: true,
          teamHandoffCtaClusters: normalizeHireTeamHandoffCtaClusters(
            result.teamHandoffCtaClusters,
            nextSelection.teamHandoffCtaClusters ?? [],
          ),
        };
      }
      handleRecommendationsChange(nextSelection);
    }
  };

  const fetchWebsiteGeneration = async (sourceWebsite: string, maxPages = 10) => {
    const tryHireWizard = async () => {
      const res = await fetch("/api/marketplace/hire-wizard/generate-from-website", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website: sourceWebsite, maxPages }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Could not generate company profile from website.");
      }
      return (await res.json()) as HireWizardWebsiteGenerationResult;
    };

    const tryCompanyProfileLongForm = async () => {
      const res = await fetch("/api/onboarding/generate-company-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website: sourceWebsite }),
        signal: AbortSignal.timeout(280_000),
      }).catch(() => null);
      if (!res?.ok) return "";
      const payload = (await res.json()) as {
        extendedText?: string;
        extendedKnowledge?: string;
      };
      return (
        payload.extendedText?.trim() || payload.extendedKnowledge?.trim() || ""
      );
    };

    try {
      const [wizardResult, companyProfileText] = await Promise.all([
        tryHireWizard(),
        tryCompanyProfileLongForm(),
      ]);
      if (companyProfileText) {
        return { ...wizardResult, extendedKnowledge: companyProfileText };
      }
      return wizardResult;
    } catch (hireWizardError) {
      console.warn(
        "hire-wizard generate-from-website failed, falling back to onboarding route:",
        hireWizardError,
      );
      const res = await fetch("/api/onboarding/generate-all-from-website", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website: sourceWebsite, maxPages }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Could not generate company profile from website.");
      }
      return (await res.json()) as HireWizardWebsiteGenerationResult;
    }
  };

  const enrichPreviewFromWebsite = async (
    sourceWebsite: string,
    projectId: string,
    hireIntentId: string,
    form: EmployerFormValues,
  ) => {
    try {
      const result = await fetchWebsiteGeneration(sourceWebsite, 10);
      applyGeneratedCompanyProfile(result, sourceWebsite);

      const profileText = buildCompanyProfileDisplayText(result);
      const questions = (result.faqOptions ?? []).filter(Boolean);

      if (questions.length > 0 && profileText.trim()) {
        try {
          const faqRes = await fetch("/api/marketplace/hire-wizard/generate-faq-answers", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              questions,
              context: profileText,
              businessName: companyName.trim() || resolvedBusinessName,
            }),
          });
          if (faqRes.ok) {
            const faqData = (await faqRes.json()) as {
              qaPairs?: Array<{ question: string; answer: string }>;
            };
            setTrainingDraft((current) => ({
              ...current,
              qaPairs: faqData.qaPairs ?? [],
              faqsGenerated: (faqData.qaPairs ?? []).length > 0,
              companyProfileText: profileText,
              companyProfileGenerated: true,
            }));
          }
        } catch (faqErr) {
          console.warn("[enrichPreviewFromWebsite] FAQ generation failed:", faqErr);
        }
      }

      const agentDisplayName = customAgentName.trim() || displayAgentName;
      const jobTitleDisplay = customJobTitle.trim() || "Virtual Assistant";
      const practiceName =
        resolveHireBusinessDisplayName(result.practiceName?.trim() || companyName.trim(), sourceWebsite) ||
        resolvedBusinessName;
      const faqForWidget = questions.slice(0, 5);
      const rawClusters = result.teamHandoffCtaClusters ?? [];
      const ctaClusters =
        rawClusters.length > 0
          ? normalizeHireTeamHandoffCtaClusters(rawClusters, [])
          : (recommendationsSelection?.teamHandoffCtaClusters ?? []);

      const fallbackEmail = form.email.trim();
      const poweredBy = getMarketplacePoweredByDefaults(
        resolveClientMarketingBrand(
          typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
        ),
      );
      await persistWebsiteDataMutation.mutateAsync({
        projectId,
        practiceName,
        practiceDescription: result.practiceDescription,
        idealCustomerDescription: result.idealCustomerDescription,
        extendedKnowledge: result.extendedKnowledge,
        websiteUrls: sourceWebsite,
        websiteUrl: sourceWebsite,
        agentName: agentDisplayName,
        jobTitle: jobTitleDisplay,
        agentPersona: customPersona.trim() || undefined,
        suggestedQuestions: faqForWidget,
        faqOptions: faqForWidget,
        replaceWidgetFaqs: true,
        ctaClusters: ctaClusters.length > 0 ? ctaClusters : undefined,
        startingMessage: `Hi! I'm ${agentDisplayName}, ${jobTitleDisplay} at ${practiceName}. How can I help you today?`,
        widgetVersion: "variant-b",
        customBrandingEnabled: true,
        poweredByCompanyName: poweredBy.name,
        poweredByCompanyUrl: poweredBy.url,
        hideAttribution: false,
        appendChatHandoffMcpInstructions: true,
        timeOfDayWelcomeMessageEnabled: true,
        headerColor: result.headerColor,
        textColor: result.textColor,
        launcherBackgroundColor:
          result.launcherBackgroundColor ?? result.widgetButtonColor ?? result.headerColor,
        widgetButtonColor: result.widgetButtonColor ?? result.headerColor,
        fallbackEmail: fallbackEmail.includes("@") ? fallbackEmail : undefined,
        channelsPartial: {
          aiChat: true,
          chatTeamHandoffDelegateEnabled: true,
        },
      });

      // Merge extracted colors into prefs directly — setTrainingDraft is async and
      // must not let stale draft colors overwrite the brand scrape on preparePreview.
      // Also re-apply generated CTAs + locations so Hire setup does not keep template defaults.
      const prefs = prepareHirePrefsForMutation(form, { silent: true });
      if (prefs) {
        const brandColors = {
          ...(result.headerColor ? { headerColor: result.headerColor } : {}),
          ...(result.widgetButtonColor || result.headerColor
            ? {
                widgetButtonColor: result.widgetButtonColor ?? result.headerColor,
                launcherBackgroundColor:
                  result.launcherBackgroundColor ??
                  result.widgetButtonColor ??
                  result.headerColor,
              }
            : {}),
          ...(result.textColor ? { textColor: result.textColor } : {}),
        };
        const generatedClusters =
          ctaClusters.length > 0
            ? ctaClusters
            : prefs.teamHandoffCtaClusters ?? undefined;
        const guestSessionId = readGuestSessionId();
        const preview = await preparePreviewMutation.mutateAsync({
          hireIntentId,
          hirePreferences: {
            ...prefs,
            widgetColors: {
              ...prefs.widgetColors,
              ...brandColors,
            },
            ...(generatedClusters?.length
              ? { teamHandoffCtaClusters: generatedClusters }
              : {}),
            responsibilities: {
              ...prefs.responsibilities,
              teamHandoff: true,
            },
          },
          ...(isSignedIn ? {} : guestSessionId ? { guestSessionId } : {}),
        });
        await switchToHireOrganizationIfNeeded(preview.organizationId);
      }
      setPreviewRefreshKey((k) => k + 1);
    } catch (error) {
      console.warn("[enrichPreviewFromWebsite]", error);
    } finally {
      skipWidgetColorSyncUntilEnrichRef.current = false;
    }
  };

  const handleGenerateCompanyProfile = async () => {
    const websiteCandidate = resolvedPrimaryWebsiteUrl;
    const parsed = urlSchema.safeParse({ url: websiteCandidate });
    if (!parsed.success) {
      toast.error("Enter a valid business website URL first.");
      return;
    }
    if (!websiteUrl.trim()) {
      setWebsiteUrl(parsed.data.url);
    }
    setIsGeneratingCompanyProfile(true);
    try {
      const result = await fetchWebsiteGeneration(parsed.data.url, 10);
      applyGeneratedCompanyProfile(result, parsed.data.url);
      toast.success("Company profile generated from your website.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not generate company profile.");
    } finally {
      setIsGeneratingCompanyProfile(false);
    }
  };

  const handleGenerateOrgStructure = async (scope: "locations" | "departments") => {
    const websiteCandidate = resolvedPrimaryWebsiteUrl;
    const parsed = urlSchema.safeParse({ url: websiteCandidate });
    if (!parsed.success) {
      toast.error("Enter a valid organization website URL first.");
      return;
    }
    const setGenerating =
      scope === "locations" ? setIsGeneratingLocations : setIsGeneratingDepartments;
    setGenerating(true);
    try {
      // Departments: prefer Organization Profile Service Offerings — no scrape, no location churn.
      if (scope === "departments" && recommendationsSelection) {
        const profileSource = trainingDraft.companyProfileText?.trim() ?? "";
        const fromProfile = extractHireDepartmentsFromProfileText(profileSource);
        if (fromProfile.length >= 2 || /##\s*service offerings/i.test(profileSource)) {
          const departmentDefaults =
            fromProfile.length >= 2
              ? fromProfile
              : defaultHireDepartmentsForVertical(
                  websiteSuggestion?.vertical ?? "general",
                  profileSource,
                );
          const leadEmail =
            leadFields.email.trim() ||
            employerFields.email.trim() ||
            email.trim() ||
            "";
          const nextSelection = ensureHireDepartmentDetailsInSelection(
            {
              ...recommendationsSelection,
              departmentCount: Math.max(1, departmentDefaults.length),
              departmentDetails: departmentDefaults,
            },
            { leadTeamMemberEmail: leadEmail },
          );
          handleRecommendationsChange(nextSelection);
          toast.success("Departments generated from Organization Profile.");
          return;
        }
      }

      // Locations (and departments fallback without a usable profile): scrape website.
      if (scope === "locations") {
        suppressDepartmentAutoSyncRef.current = true;
      }
      const result = await fetchWebsiteGeneration(parsed.data.url, 10);
      applyGeneratedCompanyProfile(result, parsed.data.url, { scope });
      toast.success(
        scope === "locations"
          ? "Locations generated from your website."
          : "Departments generated from your website.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : scope === "locations"
            ? "Could not generate locations."
            : "Could not generate departments.",
      );
    } finally {
      if (scope === "locations") {
        // Allow one tick for selection updates before re-enabling auto-sync.
        window.setTimeout(() => {
          suppressDepartmentAutoSyncRef.current = false;
        }, 0);
      }
      setGenerating(false);
    }
  };

  const handleScrapeWebsitePages = async () => {
    const websiteCandidate = resolvedPrimaryWebsiteUrl;
    const parsed = urlSchema.safeParse({ url: websiteCandidate });
    if (!parsed.success) {
      toast.error("Enter a valid business website URL first.");
      return;
    }
    setIsScrapingWebsitePages(true);
    try {
      const result = await fetchWebsiteGeneration(parsed.data.url, 10);
      const scrapedUrls = result.scrapedPageUrls ?? [];
      setTrainingDraft((current) => ({
        ...current,
        primaryWebsiteUrl: parsed.data.url,
        additionalWebsiteUrls: scrapedUrls.length > 0 ? scrapedUrls : [parsed.data.url],
        scrapedPageUrls: scrapedUrls,
      }));
      toast.success(
        scrapedUrls.length > 0
          ? `Scraped ${scrapedUrls.length} pages from your website.`
          : "Website content prepared for training.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not scrape website pages.");
    } finally {
      setIsScrapingWebsitePages(false);
    }
  };

  const handleGenerateFaqs = async () => {
    if (!trainingDraft.companyProfileText.trim()) {
      toast.error("Generate your company profile first.");
      return;
    }
    setIsGeneratingFaqs(true);
    try {
      const profileResult = await fetchWebsiteGeneration(resolvedPrimaryWebsiteUrl, 10);
      const questions = (profileResult.faqOptions ?? []).filter(Boolean);
      if (questions.length === 0) {
        toast.error("No FAQ questions could be generated from your website.");
        return;
      }
      const faqRes = await fetch("/api/marketplace/hire-wizard/generate-faq-answers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questions,
          context: trainingDraft.companyProfileText,
          businessName: companyName.trim() || resolvedBusinessName,
        }),
      });
      if (!faqRes.ok) {
        throw new Error("Could not generate FAQ answers.");
      }
      const faqData = (await faqRes.json()) as {
        qaPairs?: Array<{ question: string; answer: string }>;
      };
      setTrainingDraft((current) => ({
        ...current,
        qaPairs: faqData.qaPairs ?? [],
        faqsGenerated: (faqData.qaPairs ?? []).length > 0,
      }));
      toast.success("FAQs generated from your website.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not generate FAQs.");
    } finally {
      setIsGeneratingFaqs(false);
    }
  };

  const handleUploadTrainingFiles = async (files: FileList) => {
    setIsUploadingTrainingFiles(true);
    try {
      const uploaded = [...trainingDraft.uploadedFiles];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/marketplace/hire-wizard/extract-file-text", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const payload = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error ?? `Could not upload ${file.name}`);
        }
        const data = (await res.json()) as {
          name: string;
          size: number;
          type: string;
          text: string;
        };
        uploaded.push({
          id: `${Date.now()}-${file.name}`,
          name: data.name,
          size: data.size,
          type: data.type,
          text: data.text,
        });
      }
      setTrainingDraft((current) => ({ ...current, uploadedFiles: uploaded }));
      toast.success("Files uploaded for training.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not upload files.");
    } finally {
      setIsUploadingTrainingFiles(false);
    }
  };

  // SiteGlue: after website scrape bootstrap, generate Intern preview so Preview
  // (site + widget) and Test unlock — including Get Started ?website= before plan pick.
  React.useEffect(() => {
    if (!isSiteGlueBrand || !useWizardLayout) return;
    if (!bootstrapComplete || previewGenerated || isGenerating) return;
    if (autoGenerateAttemptedRef.current || autoGenerateInFlightRef.current) return;
    if (!websiteSuggestion?.websiteUrl?.trim()) return;
    if (!recommendationsSelection) return;
    if (personaGenerating && !resolvedAgentPersona()) return;

    const defaultName =
      customAgentName.trim() ||
      consultantState?.employeeName?.trim() ||
      websiteSuggestion.recommendations?.agentProfile?.agentName?.trim() ||
      "Samantha";
    const defaultTitle =
      customJobTitle.trim() ||
      consultantState?.recommendedAgents?.[0]?.roleName?.trim() ||
      websiteSuggestion.recommendations?.agentProfile?.jobTitle?.trim() ||
      "Online Support Specialist";

    if (!customAgentName.trim() || !customJobTitle.trim()) {
      if (!customAgentName.trim()) setCustomAgentName(defaultName);
      if (!customJobTitle.trim()) setCustomJobTitle(defaultTitle);
      return;
    }

    if (!onboardingComplete) {
      setOnboardingComplete(true);
      markHireBuilderOnboardingComplete();
      return;
    }

    const timer = window.setTimeout(() => {
      void handleGenerateAgent();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    isSiteGlueBrand,
    useWizardLayout,
    bootstrapComplete,
    previewGenerated,
    isGenerating,
    websiteSuggestion,
    recommendationsSelection,
    onboardingComplete,
    customAgentName,
    customJobTitle,
    personaGenerating,
    customPersona,
    consultantState?.employeeName,
    consultantState?.recommendedAgents,
  ]);

  // Keep Sign Up panel in sync with consultant step (don't rely only on callback timing /
  // LLM copy that may claim the form is open before step === sign_up).
  // SiteGlue uses inline gated-tab Sign Up on Onboard only — Preview/Test stay open for guests.
  const shouldShowPreviewSignUp =
    !isSiteGlueBrand &&
    !isSignedIn &&
    (showPreviewSignUp || consultantState?.step === "sign_up");

  React.useEffect(() => {
    if (isSignedIn || consultantState?.step !== "sign_up") return;
    if (isSiteGlueBrand) {
      // Do not force Sign Up onto Preview/Test — guests can try the widget first.
      setBuilderLeftTab("build");
      return;
    }
    setShowPreviewSignUp(true);
    setBuilderRightTab("hire");
    setBuilderLeftTab("build");
  }, [consultantState?.step, isSignedIn, isSiteGlueBrand]);

  // Budget / plan step — open Pricing tab so Intern · Associate · Professional cards are visible.
  React.useEffect(() => {
    if (!isSiteGlueBrand || consultantState?.step !== "budget") return;
    setBuilderRightTab("pricing");
  }, [isSiteGlueBrand, consultantState?.step]);

  // Intern Preview must not unmount Trish before Company Profile / What I Learned finish.
  const siteGlueScrapeInProgress =
    isSiteGlueBrand &&
    !consultantState?.companyProfileMarkdown?.trim() &&
    (consultantState?.step === "building_knowledge" ||
      consultantState?.step === "analyzing" ||
      consultantState?.step === "website" ||
      isBootstrapping);

  const showOnboardingChat =
    useWizardLayout &&
    Boolean(onSubmitWebsite) &&
    (siteGlueScrapeInProgress ||
      // SiteGlue guests keep Trish after Preview generate (test before Sign Up).
      (isSiteGlueBrand && !isSignedIn) ||
      (!previewGenerated &&
        // Keep Trish visible while waiting for Sign Up / email verify resume.
        (!onboardingComplete || !bootstrapComplete || shouldShowPreviewSignUp) &&
        !shouldSkipHireBuilderOnboarding({
          bootstrapComplete,
          previewGenerated,
        })));

  const trishGoalOptions = React.useMemo(() => {
    if (!websiteSuggestion) return undefined;
    if (websiteSuggestion.onboardingGoalSuggestions?.length) {
      return websiteSuggestion.onboardingGoalSuggestions;
    }
    return buildOnboardingGoalsFromWebsite(
      websiteSuggestion.vertical,
      `${websiteSuggestion.businessName} ${websiteSuggestion.categoryLabel} ${websiteSuggestion.summary}`,
      websiteSuggestion.businessName,
    );
  }, [websiteSuggestion]);

  React.useEffect(() => {
    if (showOnboardingChat && !agentNameConfirmed) {
      onDisplayAgentNameChange?.("");
      return;
    }
    onDisplayAgentNameChange?.(customAgentName.trim() || agentName);
  }, [
    customAgentName,
    agentName,
    onDisplayAgentNameChange,
    showOnboardingChat,
    agentNameConfirmed,
  ]);

  React.useEffect(() => {
    if (!useWizardLayout || previewGenerated) return;
    if (!bootstrapComplete && onboardingComplete) {
      setOnboardingComplete(false);
    }
  }, [useWizardLayout, bootstrapComplete, previewGenerated, onboardingComplete]);

  // After email verify / new-tab return: restore website suggestion + keep Build chat.
  React.useEffect(() => {
    if (!useWizardLayout || previewGenerated || !isSignedIn) return;
    const saved = readSavedHireBuilderOnboardingState();
    if (saved?.step === "sign_up" || saved?.step === "complete" || saved?.step === "setup") {
      setShowPreviewSignUp(false);
      if (!onboardingComplete && bootstrapComplete) {
        // Let Trish resume; do not force HireBuilderBuildChat yet.
      }
    }
  }, [
    useWizardLayout,
    previewGenerated,
    isSignedIn,
    onboardingComplete,
    bootstrapComplete,
  ]);

  const applyTrishConfigPreference = React.useCallback(
    (preference: TrishConfigPreference) => {
      if (!websiteSuggestion) return null;
      const next =
        preference === "recommended"
          ? defaultRecommendationsFromSuggestion(websiteSuggestion)
          : resetRecommendationsToFreeTier(
              defaultRecommendationsFromSuggestion(websiteSuggestion),
            );
      handleRecommendationsChange(next);
      writeHireRecommendationsSelection(next);
      return next;
    },
    [handleRecommendationsChange, websiteSuggestion],
  );

  const handleOnboardingSetupAgent = () => {
    if (!bootstrapComplete) return;
    setShowPreviewSignUp(false);
    setOnboardingComplete(true);
    // Open live Preview for SiteGlue (guests included) while the workspace generates.
    setBuilderRightTab(isSiteGlueBrand ? "live_preview" : "preview");
    // Apply free/recommended synchronously so generate does not use stale free-tier state.
    const preference = trishConfigPreferenceRef.current;
    if (preference) {
      applyTrishConfigPreference(preference);
    }
    // Prepare preview workspace so Preview widget + Test unlock after scrape.
    if (bootstrapComplete && (isSignedIn || isSiteGlueBrand)) {
      void handleGenerateAgent();
    }
  };

  const handleTrishRequestSignUp = React.useCallback(
    (prefill?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      title?: string;
      phone?: string;
      profileImageUrl?: string;
    }) => {
      if (prefill) setSignupPrefill((prev) => ({ ...prev, ...prefill }));
      // SiteGlue: Sign Up lives on Onboard — Preview/Test stay usable for guests.
      if (isSiteGlueBrand) {
        requestSignUpForTab("hire");
        setBuilderLeftTab("build");
        return;
      }
      setShowPreviewSignUp(true);
      setBuilderRightTab("preview");
      setBuilderLeftTab("build");
      requestAnimationFrame(() => {
        setBuilderRightTab("preview");
        setShowPreviewSignUp(true);
      });
    },
    [isSiteGlueBrand, requestSignUpForTab],
  );

  const handleOpenOnboardTab = React.useCallback(() => {
    if (isSiteGlueBrand && !isSignedIn) {
      requestSignUpForTab("hire");
      setBuilderLeftTab("build");
      return;
    }
    setBuilderRightTab("hire");
    setBuilderLeftTab("build");
  }, [isSiteGlueBrand, isSignedIn, requestSignUpForTab]);

  const handleDeployRef = React.useRef(handleDeploy);
  handleDeployRef.current = handleDeploy;

  const handleOnboardClick = React.useCallback(() => {
    if (isSiteGlueBrand && !isSignedIn) {
      // Inline Sign Up on Onboard tab (no popup modal).
      writeSavedHireBuilderOnboardingState({
        step: "complete",
        submittedWebsite:
          consultantState?.websiteUrl ||
          websiteSuggestion?.websiteUrl ||
          websiteUrl ||
          "",
      });
      requestSignUpForTab("hire");
      return;
    }
    void handleDeployRef.current();
  }, [
    isSiteGlueBrand,
    isSignedIn,
    consultantState?.websiteUrl,
    websiteSuggestion?.websiteUrl,
    websiteUrl,
    requestSignUpForTab,
  ]);

  /** Soft clear shell bootstrap state; chat navigates via navigateHireBuilderStartOver. */
  const handleTrishStartOver = React.useCallback(() => {
    onStartOver?.();
  }, [onStartOver]);

  const handleTrishAgentProfileSelected = React.useCallback(
    (profile: { agentName: string; jobTitle: string; profileImageUrl: string }) => {
      setAgentNameConfirmed(true);
      setCustomAgentName(profile.agentName);
      setCustomJobTitle(profile.jobTitle);
      if (profile.profileImageUrl) {
        setProfilePresetUrl(profile.profileImageUrl);
      }
      // Keep Recommended AI Employee panel in sync with chat (Mia → Tammy, etc.).
      setConsultantState((prev) =>
        prev
          ? {
              ...prev,
              employeeName: profile.agentName || prev.employeeName,
              employeeProfileImageUrl:
                profile.profileImageUrl || prev.employeeProfileImageUrl,
              recommendedAgents: prev.recommendedAgents.map((agent, index) =>
                index === 0 && profile.jobTitle
                  ? { ...agent, roleName: profile.jobTitle }
                  : agent,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const handleTrishConfigPreference = React.useCallback(
    (preference: TrishConfigPreference) => {
      trishConfigPreferenceRef.current = preference;
      applyTrishConfigPreference(preference);
    },
    [applyTrishConfigPreference],
  );

  const handleConsultantPlanRecommended = React.useCallback(
    (planId: string) => {
      const id = planId as AiEmployeePlanId;
      if (id === "enterprise") {
        // Keep current selection; Enterprise stays contact/custom.
        return;
      }
      const next = recommendationsFromAiEmployeePlan(id, {
        locationCount: recommendationsSelection?.locationCount,
        teamMemberCount: recommendationsSelection?.teamMemberCount,
        schedulingLevel: consultantState?.recommendedSchedulingLevel,
        schedulingIntegrationId: recommendationsSelection?.schedulingIntegrationId,
        emrIntegrationId: recommendationsSelection?.emrIntegrationId,
      });
      handleRecommendationsChange(next);
      writeHireRecommendationsSelection(next);
      const mapped = planSelectionFromRecommendations(next);
      writeHirePlanSelection(mapped);
      setPlanSelection(mapped);
      setConsultantState((prev) =>
        prev
          ? {
              ...prev,
              selectedPlan: id,
              recommendedPlan: id,
              budgetConfirmed: true,
            }
          : prev,
      );
    },
    [
      recommendationsSelection,
      consultantState?.recommendedSchedulingLevel,
      handleRecommendationsChange,
      setPlanSelection,
    ],
  );

  /** Pricing tab: Intern → Sign Up Free modal; Associate/Professional → Buy Now checkout. */
  const handlePricingPlanAction = React.useCallback(
    (planId: Exclude<AiEmployeePlanId, "enterprise">) => {
      handleConsultantPlanRecommended(planId);
      setExternalPlanPick(planId);

      if (planId === "intern") {
        writeSavedHireBuilderOnboardingState({
          step: "complete",
          submittedWebsite:
            consultantState?.websiteUrl ||
            websiteSuggestion?.websiteUrl ||
            websiteUrl ||
            "",
        });
      }

      // Same Sign Up / Checkout modal for Intern (free) and paid plans.
      setOnboardingProgressDismissed(true);
      setPlanCheckoutPlanId(planId);
      setPlanCheckoutOpen(true);
    },
    [
      handleConsultantPlanRecommended,
      consultantState?.websiteUrl,
      websiteSuggestion?.websiteUrl,
      websiteUrl,
    ],
  );

  const handlePlanCheckoutContinueToPayment = React.useCallback(
    async (contact: SiteGlueCheckoutContact) => {
      planCheckoutContactRef.current = contact;
      setFirstName(contact.firstName.trim());
      setLastName(contact.lastName.trim());
      setEmail(contact.email.trim());
      setSignupPrefill({
        firstName: contact.firstName.trim(),
        lastName: contact.lastName.trim(),
        email: contact.email.trim(),
      });
      const biz =
        companyName.trim() ||
        consultantState?.businessName?.trim() ||
        websiteSuggestion?.businessName?.trim();
      if (biz) setCompanyName(biz);
      const site =
        websiteUrl.trim() ||
        consultantState?.websiteUrl?.trim() ||
        websiteSuggestion?.websiteUrl?.trim();
      if (site) setWebsiteUrl(site);

      // Keep checkout open until Stripe redirect; skip Company Settings validation.
      await handleDeploy({ fromCheckout: true });
    },
    [
      companyName,
      consultantState?.businessName,
      consultantState?.websiteUrl,
      websiteSuggestion?.businessName,
      websiteSuggestion?.websiteUrl,
      websiteUrl,
    ],
  );

  const handleConsultantConnectIntegration = React.useCallback((_platformId: string) => {
    setBuilderLeftTab("configure");
    toast.message("Open Tools in Build to connect securely via OAuth — never share passwords in chat.");
  }, []);

  const trainingDraftCompanyProfileRef = React.useRef(trainingDraft.companyProfileText);
  trainingDraftCompanyProfileRef.current = trainingDraft.companyProfileText;
  const handleRecommendationsChangeRef = React.useRef(handleRecommendationsChange);
  handleRecommendationsChangeRef.current = handleRecommendationsChange;

  const handleConsultantStateChange = React.useCallback((next: SiteGlueConsultantState) => {
    setConsultantState(next);

    // Sync ministries / departments from Trish + Organization Profile into Organization tab.
    // Skip while Locations Generate is in flight so the two Generators stay independent.
    const selection = recommendationsSelectionRef.current;
    if (
      isSiteGlueBrand &&
      selection &&
      !suppressDepartmentAutoSyncRef.current
    ) {
      const fromProfile = extractHireDepartmentsFromProfileText(
        next.companyProfileMarkdown?.trim() || trainingDraftCompanyProfileRef.current || "",
      );
      const nextDeptNames =
        fromProfile.length >= 2
          ? fromProfile.map((d) => d.name)
          : (next.departments ?? []).map((d) => d.trim()).filter(Boolean);
      if (nextDeptNames.length >= 2) {
        const currentNames = selection.departmentDetails
          .slice(0, selection.departmentCount)
          .map((d) => d.name.trim());
        const looksLikePlaceholderDefaults =
          currentNames.length <= 1 ||
          currentNames.every((n) =>
            /^(front desk|clinical|admissions|academics|student life|athletics|general)$/i.test(
              n,
            ),
          );
        const changed =
          nextDeptNames.length !== currentNames.length ||
          nextDeptNames.some(
            (n, i) => n.toLowerCase() !== (currentNames[i] ?? "").toLowerCase(),
          );
        if (changed && looksLikePlaceholderDefaults) {
          handleRecommendationsChangeRef.current({
            ...selection,
            departmentCount: nextDeptNames.length,
            departmentDetails: nextDeptNames.map((name) => ({ name })),
          });
        }
      }
    }

    // Keep Progress + Settings agent identity in sync with Trish's recommendation.
    if (isSiteGlueBrand && !agentProfileUserEditedRef.current) {
      const role = next.recommendedAgents[0]?.roleName?.trim();
      const name = next.employeeName?.trim();
      const internFirstPreview =
        next.selectedPlan === "intern" && next.budgetConfirmed;
      const pastSnapshot =
        next.step !== "website" &&
        next.step !== "analyzing" &&
        next.step !== "building_knowledge" &&
        !isSiteGlueIntakeStep(next.step);
      if (name && role && (pastSnapshot || internFirstPreview)) {
        setCustomAgentName(name);
        setCustomJobTitle(role);
        if (next.employeeProfileImageUrl?.trim()) {
          setProfilePresetUrl(next.employeeProfileImageUrl.trim());
        }
      }
    }

    // Legacy sign_up step: signed-in users open Onboard; guests keep Live Preview / Progress.
    if (next.step === "sign_up") {
      if (isSiteGlueBrand) {
        setBuilderRightTab(
          isSignedIn ? "hire" : previewGenerated ? "live_preview" : "preview",
        );
        setBuilderLeftTab("build");
        return;
      }
      setShowPreviewSignUp(true);
      setBuilderRightTab("preview");
      setBuilderLeftTab("build");
      return;
    }

    // Guests: open Live Preview once generated; otherwise stay on Progress.
    if (isSiteGlueBrand && !isSignedIn && next.step === "complete") {
      setBuilderRightTab(previewGenerated ? "live_preview" : "preview");
      return;
    }

    // Intern-first / post-bootstrap: open Live Preview (site + widget) for guests and signed-in.
    const internFirstPreview =
      isSiteGlueBrand &&
      next.selectedPlan === "intern" &&
      next.budgetConfirmed;
    if (internFirstPreview || onboardingComplete || previewGenerated) {
      if (
        next.step === "complete" ||
        next.step === "building_knowledge" ||
        next.step === "analyzing" ||
        next.step === "snapshot" ||
        previewGenerated ||
        onboardingComplete
      ) {
        setBuilderRightTab(
          isSiteGlueBrand
            ? previewGenerated || internFirstPreview
              ? "live_preview"
              : "preview"
            : "live_preview",
        );
        return;
      }
    }

    // Right-panel routing: never jump to Opportunity Report except at draft/final report steps.
    // Keep Company Profile visible through snapshot/goals so scrape results stay on screen.
    if (
      next.step === "building_knowledge" ||
      next.step === "analyzing" ||
      next.step === "snapshot" ||
      next.step === "goals"
    ) {
      setBuilderRightTab("knowledge");
      return;
    }
    if (next.step === "report_draft" || next.step === "report" || next.step === "review") {
      // Strategy tab removed — show Company Profile instead.
      setBuilderRightTab("knowledge");
      return;
    }
    if (next.step === "budget") {
      setBuilderRightTab(previewGenerated ? "live_preview" : "preview");
      return;
    }
    if (
      next.step === "meet_employee" ||
      next.step === "recommended_agents" ||
      next.step === "channels"
    ) {
      setBuilderRightTab(previewGenerated ? "live_preview" : "preview");
      return;
    }
    if (next.step === "complete") {
      setBuilderRightTab(previewGenerated ? "live_preview" : "preview");
    }
  }, [isSiteGlueBrand, previewGenerated, onboardingComplete, isSignedIn]);

  // When Organization Profile gains Service Offerings, replace placeholder departments.
  // Only keyed on profile text — never on recommendationsSelection — so Locations Generate
  // cannot rewrite Departments as a side effect.
  React.useEffect(() => {
    if (!isSiteGlueBrand) return;
    if (suppressDepartmentAutoSyncRef.current) return;
    const selection = recommendationsSelectionRef.current;
    if (!selection) return;
    const profile = trainingDraft.companyProfileText?.trim();
    if (!profile || !/##\s*service offerings/i.test(profile)) return;
    const fromProfile = extractHireDepartmentsFromProfileText(profile);
    if (fromProfile.length < 2) return;
    const currentNames = selection.departmentDetails
      .slice(0, selection.departmentCount)
      .map((d) => d.name.trim());
    const looksLikePlaceholderDefaults =
      currentNames.length <= 1 ||
      currentNames.every((n) =>
        /^(front desk|clinical|admissions|academics|student life|athletics|general)$/i.test(n),
      );
    if (!looksLikePlaceholderDefaults) return;
    const nextNames = fromProfile.map((d) => d.name);
    const changed =
      nextNames.length !== currentNames.length ||
      nextNames.some((n, i) => n.toLowerCase() !== (currentNames[i] ?? "").toLowerCase());
    if (!changed) return;
    handleRecommendationsChange({
      ...selection,
      departmentCount: nextNames.length,
      departmentDetails: nextNames.map((name) => ({ name })),
    });
  }, [isSiteGlueBrand, trainingDraft.companyProfileText, handleRecommendationsChange]);

  const handleOpenReportTab = React.useCallback(() => {
    // Strategy tab removed from SiteGlue hire builder.
    setBuilderRightTab("knowledge");
  }, []);

  const handleOpenKnowledgeTab = React.useCallback(() => {
    setBuilderRightTab("knowledge");
  }, []);

  const handleOpenEmployeeTab = React.useCallback(() => {
    // SiteGlue: tab id "preview" is Progress (checklist + recommended employee).
    setBuilderRightTab("preview");
  }, []);

  const handleOpenPricingTab = React.useCallback(() => {
    setBuilderRightTab(isSiteGlueBrand ? "pricing" : "preview");
  }, [isSiteGlueBrand]);

  const handleCompanyProfileReady = React.useCallback(
    (result: HireWizardWebsiteGenerationResult, websiteUrl: string, profileText: string) => {
      applyGeneratedCompanyProfile(result, websiteUrl);
      // Prefer the authoritative text from Trish (includes snapshot fallbacks).
      if (profileText.trim()) {
        setTrainingDraft((current) => ({
          ...current,
          primaryWebsiteUrl: websiteUrl || current.primaryWebsiteUrl,
          companyProfileGenerated: true,
          companyProfileText: profileText,
        }));
        setConsultantState((prev) =>
          prev
            ? {
                ...prev,
                companyProfileMarkdown: profileText,
                websiteUrl: websiteUrl || prev.websiteUrl,
                businessName:
                  result.practiceName?.trim() ||
                  prev.businessName ||
                  companyName ||
                  prev.businessName,
              }
            : prev,
        );
      }

      // Generate FAQ Q&A during SiteGlue onboarding (not only after preview enrich).
      const questions = (result.faqOptions ?? []).filter(Boolean);
      const faqContext = profileText.trim() || buildCompanyProfileDisplayText(result);
      if (questions.length > 0 && faqContext.trim()) {
        void (async () => {
          try {
            const faqRes = await fetch("/api/marketplace/hire-wizard/generate-faq-answers", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                questions,
                context: faqContext,
                businessName:
                  result.practiceName?.trim() || companyName.trim() || resolvedBusinessName,
              }),
            });
            if (!faqRes.ok) return;
            const faqData = (await faqRes.json()) as {
              qaPairs?: Array<{ question: string; answer: string }>;
            };
            const qaPairs = faqData.qaPairs ?? [];
            if (qaPairs.length === 0) return;
            setTrainingDraft((current) => ({
              ...current,
              qaPairs,
              faqsGenerated: true,
              companyProfileText: current.companyProfileText || faqContext,
              companyProfileGenerated: true,
            }));
            // Push suggested questions into the live preview widget when it already exists.
            if (previewProjectId) {
              const faqForWidget = questions.slice(0, 5);
              void persistWebsiteDataMutation
                .mutateAsync({
                  projectId: previewProjectId,
                  suggestedQuestions: faqForWidget,
                  faqOptions: faqForWidget,
                  replaceWidgetFaqs: true,
                })
                .catch((err) =>
                  console.warn("[handleCompanyProfileReady] FAQ persist failed:", err),
                );
            }
          } catch (faqErr) {
            console.warn("[handleCompanyProfileReady] FAQ generation failed:", faqErr);
          }
        })();
      }

      // Intern-first Preview: Company Profile can update in the background (signed-in only).
      if (isSiteGlueBrand && isSignedIn && (onboardingComplete || previewGenerated)) {
        setBuilderRightTab("live_preview");
        return;
      }
      setBuilderRightTab("knowledge");
    },
    // applyGeneratedCompanyProfile closes over selection/training state — intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      recommendationsSelection,
      companyName,
      leadFields.email,
      employerFields.email,
      email,
      isSiteGlueBrand,
      isSignedIn,
      resolvedBusinessName,
      previewProjectId,
      persistWebsiteDataMutation,
      onboardingComplete,
      previewGenerated,
    ],
  );

  React.useEffect(() => {
    if (isSignedIn && showPreviewSignUp) {
      setShowPreviewSignUp(false);
    }
  }, [isSignedIn, showPreviewSignUp]);

  React.useEffect(() => {
    if (!useWizardLayout || !onboardingComplete || !isSignedIn) return;
    if (!bootstrapComplete || previewGenerated) return;
    // SiteGlue Intern-first: stay on live Preview while the workspace generates.
    setBuilderRightTab(isSiteGlueBrand ? "live_preview" : "preview");
  }, [
    useWizardLayout,
    onboardingComplete,
    isSignedIn,
    bootstrapComplete,
    previewGenerated,
    isSiteGlueBrand,
  ]);

  // Show employee card once Intern identity exists — do NOT force step→complete
  // (that cancelled scrape / What I Learned while still on building_knowledge).
  const internPreviewReady =
    Boolean(consultantState?.employeeName?.trim()) &&
    (consultantState?.step === "complete" ||
      Boolean(consultantState?.companyProfileMarkdown?.trim()) ||
      previewGenerated ||
      (Boolean(consultantState?.budgetConfirmed) &&
        (consultantState?.selectedPlan === "intern" ||
          consultantState?.recommendedPlan === "intern")));

  const isBuildingCompanyProfile =
    isSiteGlueBrand &&
    !internPreviewReady &&
    !consultantState?.companyProfileMarkdown?.trim() &&
    !trainingDraft.companyProfileText?.trim() &&
    (consultantState?.step === "building_knowledge" ||
      consultantState?.step === "analyzing" ||
      isBootstrapping);

  // Get Started (?website=) scrapes before a plan is chosen — do not show Progress until then.
  const planConfirmedForProgressModal =
    Boolean(consultantState?.budgetConfirmed) ||
    Boolean(consultantState?.selectedPlan);

  // Re-open Onboarding Progress when Trish starts a new analyze / build phase (after plan).
  React.useEffect(() => {
    if (!isSiteGlueBrand) return;
    if (!planConfirmedForProgressModal) {
      onboardingProgressStepRef.current = null;
      return;
    }
    const step = consultantState?.step ?? null;
    const enteredWork =
      isBootstrapping ||
      step === "analyzing" ||
      step === "building_knowledge" ||
      (isGenerating && !previewGenerated);
    const prev = onboardingProgressStepRef.current;
    const key = enteredWork
      ? `${step ?? "boot"}:${isBootstrapping ? "1" : "0"}:${isGenerating && !previewGenerated ? "g" : ""}`
      : null;
    if (enteredWork && key && key !== prev) {
      setOnboardingProgressDismissed(false);
    }
    onboardingProgressStepRef.current = key;
  }, [
    isSiteGlueBrand,
    planConfirmedForProgressModal,
    consultantState?.step,
    isBootstrapping,
    isGenerating,
    previewGenerated,
  ]);

  const siteGluePlanCeilingId = isSiteGlueBrand ? siteGluePlanId : null;

  const requestSiteGluePlanUpgrade = isSiteGlueBrand
    ? () => {
        const current = siteGluePlanId;
        const next =
          current === "intern"
            ? "associate"
            : current === "associate"
              ? "professional"
              : current === "professional"
                ? "enterprise"
                : null;
        if (next && next !== "enterprise") {
          handleConsultantPlanRecommended(next);
        } else if (next === "enterprise") {
          setContactSalesOpen(true);
        }
      }
    : undefined;

  const organizationNameWebsiteFields =
    useWizardLayout && websiteSuggestion && recommendationsSelection ? (
      <>
        <div className="space-y-2">
          <Label htmlFor="wizard-hire-company">
            {isSiteGlueBrand ? "Organization" : "Company name"}
          </Label>
          <Input
            id="wizard-hire-company"
            autoComplete="organization"
            value={
              companyName.trim() ||
              consultantState?.businessName?.trim() ||
              websiteSuggestion?.businessName?.trim() ||
              ""
            }
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wizard-hire-website">
            {isSiteGlueBrand ? "Organization website" : "Company website"}
          </Label>
          <Input
            id="wizard-hire-website"
            autoComplete="url"
            value={
              websiteUrl.trim() ||
              websiteSuggestion?.websiteUrl ||
              consultantState?.websiteUrl ||
              ""
            }
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
        </div>
      </>
    ) : null;

  const teamLocationsDepartmentsSection =
    useWizardLayout && websiteSuggestion && recommendationsSelection ? (
      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {isSiteGlueBrand && !isSignedIn
              ? "Locations & Departments"
              : "Team, Locations & Departments"}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {isSiteGlueBrand && !isSignedIn
              ? "Review what Trish found on your website. Use Generate with AI if locations or departments are missing. Sign up to manage Team Members."
              : "Assign lead team members to each location. Your account is the default lead team member."}
          </p>
        </div>
        {isSignedIn && accountSummaryLabel ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {accountSummaryLabel}
          </div>
        ) : null}
        <HireCompanyProfilePricingSection
          selection={recommendationsSelection}
          onChange={handleRecommendationsChange}
          employerContact={pricingTeamContacts.employerContact}
          leadTeamMember={pricingTeamContacts.leadTeamMember}
          employerFields={employerFields}
          leadFields={leadFields}
          onEmployerUpdate={(values) => {
            setFirstName(values.firstName);
            setLastName(values.lastName);
            setEmail(values.email);
            setEmployerTitle(values.title ?? "");
            setEmployerPhone(values.phone ?? "");
            setEmployerImageUrl(values.imageUrl ?? "");
          }}
          onLeadUpdate={(values) => {
            setLeadFirstName(values.firstName);
            setLeadLastName(values.lastName);
            setLeadEmail(values.email);
            setLeadTitle(values.title ?? "");
            setLeadPhone(values.phone ?? "");
            setLeadImageUrl(values.imageUrl ?? "");
          }}
          visibleSections={organizationVisibleSections}
          hideUnitPricing={isSiteGlueBrand}
          onGenerateLocations={
            isSiteGlueBrand ? () => void handleGenerateOrgStructure("locations") : undefined
          }
          onGenerateDepartments={
            isSiteGlueBrand ? () => void handleGenerateOrgStructure("departments") : undefined
          }
          isGeneratingLocations={isGeneratingLocations}
          isGeneratingDepartments={isGeneratingDepartments}
        />
      </section>
    ) : null;

  /** Channel toggles — SiteGlue: under Profile as My Channels; else under configure. */
  const myChannelsSection =
    useWizardLayout && websiteSuggestion && recommendationsSelection ? (
      <section className="space-y-4">
        <HireRecommendationsStep
          businessName={resolvedBusinessName}
          selection={recommendationsSelection}
          onChange={handleRecommendationsChange}
          section="responsibilities"
          title={isSiteGlueBrand ? "My Channels" : "Responsibilities"}
          channelsSectionTitle={isSiteGlueBrand ? "My Channels" : "Responsibilities"}
          stepDescription={
            isSiteGlueBrand
              ? `Enable the communication channels ${displayAgentName.trim() || "your AI Employee"} can use for ${resolvedBusinessName}.`
              : undefined
          }
          hideTitle={false}
          showTotal={!isSiteGlueBrand}
          hideUnitPricing={isSiteGlueBrand}
          showTooltips
          onResponsibilityPreview={() => setBuilderRightTab("preview")}
          planCeilingId={isSiteGlueBrand ? siteGluePlanCeilingId : null}
          onRequestPlanUpgrade={
            isSiteGlueBrand ? requestSiteGluePlanUpgrade : undefined
          }
        />
      </section>
    ) : null;

  const agentProfileAndSettingsSections =
    useWizardLayout && websiteSuggestion && recommendationsSelection ? (
      <>
        <section className="space-y-6">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {isSiteGlueBrand
                ? !agentNameConfirmed
                  ? "Profile"
                  : `${displayAgentName.trim() || "Agent"}${/s$/i.test(displayAgentName.trim() || "Agent") ? "'" : "'s"} Profile`
                : "Company Settings"}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isSiteGlueBrand
                ? "Review your AI Employee profile, channels, training, and tools."
                : `Confirm your business details for ${resolvedBusinessName}.`}
            </p>
          </div>
          {isSignedIn && accountSummaryLabel ? (
            <div className="rounded-lg border border-[#C3D4FC] bg-[#F0F5FF] px-4 py-3 text-sm text-slate-700">
              <p>
                <span className="font-medium text-slate-900">Account:</span>{" "}
                {accountSummaryLabel.replace(/^Account:\s*/, "")}
              </p>
            </div>
          ) : null}
          {!isSiteGlueBrand ? organizationNameWebsiteFields : null}
          <HireRecommendationsOverviewStep
            businessName={resolvedBusinessName}
            websiteUrl={websiteSuggestion.websiteUrl}
            recommendations={websiteSuggestion.recommendations}
            agentProfile={agentProfileDraft}
            onAgentProfileChange={handleAgentProfileDraftChange}
            selection={recommendationsSelection}
            onSelectionChange={handleRecommendationsChange}
            isGeneratingPersona={personaGenerating}
            hideSettings
          />
          {!isSiteGlueBrand ? (
            <HireWizardWidgetColorsSection
              colors={trainingDraft.widgetColors}
              onChange={(widgetColors) => {
                userEditedWidgetColorsRef.current = true;
                setTrainingDraft((current) => ({ ...current, widgetColors }));
              }}
            />
          ) : null}
        </section>

        {isSiteGlueBrand && myChannelsSection ? (
          <section className="border-t border-slate-200 pt-8">{myChannelsSection}</section>
        ) : null}

        <section className="border-t border-slate-200 pt-8">
          <HireWizardTrainingStep
            businessName={resolvedBusinessName}
            primaryWebsiteUrl={resolvedPrimaryWebsiteUrl}
            selection={recommendationsSelection}
            onSelectionChange={handleRecommendationsChange}
            trainingDraft={trainingDraft}
            onTrainingDraftChange={setTrainingDraft}
            onGenerateCompanyProfile={handleGenerateCompanyProfile}
            isGeneratingCompanyProfile={isGeneratingCompanyProfile}
            onViewCompanyProfile={() => setCompanyProfileDialogOpen(true)}
            onScrapeWebsitePages={handleScrapeWebsitePages}
            isScrapingWebsitePages={isScrapingWebsitePages}
            onGenerateFaqs={handleGenerateFaqs}
            isGeneratingFaqs={isGeneratingFaqs}
            onUploadFiles={handleUploadTrainingFiles}
            isUploadingFiles={isUploadingTrainingFiles}
          />
        </section>

        <section className="border-t border-slate-200 pt-8">
          <HireWizardToolsStep
            selection={recommendationsSelection}
            onSelectionChange={handleRecommendationsChange}
            credentials={toolCredentials}
            onCredentialsChange={setToolCredentials}
            emrCredentialsSkipped={emrCredentialsSkipped}
            onEmrCredentialsSkippedChange={setEmrCredentialsSkipped}
            previewProjectId={previewProjectId}
            previewHireIntentId={previewHireIntentId}
            templateProjectId={activeTemplateProjectId}
            businessName={resolvedBusinessName}
            websiteUrl={resolvedPrimaryWebsiteUrl}
            companyProfileText={trainingDraft.companyProfileText}
          />
        </section>
      </>
    ) : null;

  /** SiteGlue Organization tab: name/website → branding → team/locations → Organization Profile. */
  const siteGlueOrganizationPanel =
    isSiteGlueBrand && useWizardLayout ? (
      <div className="flex h-full min-h-[520px] flex-col overflow-y-auto">
        {websiteSuggestion && recommendationsSelection ? (
          <TooltipProvider>
            <div className="space-y-8 p-4 sm:p-5">
              <section className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Organization</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Confirm your organization name, website, branding, team, and locations as Trish
                    learns about your business.
                  </p>
                </div>
                {organizationNameWebsiteFields}
                <HireWizardWidgetColorsSection
                  title="Organization Branding"
                  description="Customize chat widget colors for your website. Defaults are extracted from your site when available."
                  colors={trainingDraft.widgetColors}
                  onChange={(widgetColors) => {
                    userEditedWidgetColorsRef.current = true;
                    setTrainingDraft((current) => ({ ...current, widgetColors }));
                  }}
                />
              </section>
              <div className="border-t border-slate-200 pt-8">{teamLocationsDepartmentsSection}</div>
              <div className="border-t border-slate-200 pt-8">
                <SiteGlueExtendedKnowledgePanel
                  documentScroll
                  businessName={
                    consultantState?.companyProfileMarkdown?.trim() ||
                    trainingDraft.companyProfileText?.trim()
                      ? consultantState?.businessName ||
                        companyName ||
                        websiteSuggestion?.businessName ||
                        resolvedBusinessName
                      : undefined
                  }
                  profileText={
                    consultantState?.companyProfileMarkdown?.trim() ||
                    trainingDraft.companyProfileText?.trim() ||
                    ""
                  }
                  isGenerating={
                    !internPreviewReady &&
                    !consultantState?.companyProfileMarkdown?.trim() &&
                    !trainingDraft.companyProfileText?.trim() &&
                    (consultantState?.step === "building_knowledge" ||
                      consultantState?.step === "analyzing" ||
                      (isBootstrapping && !trainingDraft.companyProfileText))
                  }
                  className="rounded-xl border border-slate-200"
                />
              </div>
            </div>
          </TooltipProvider>
        ) : (
          <HireBuilderTabPlaceholder
            icon={Settings}
            title="Organization"
            description="As Trish learns about your organization, your website, branding, locations, and team members will appear here for review."
            className="min-h-[520px]"
          />
        )}
      </div>
    ) : null;

  const siteGlueProfilePlaceholder = (
    <HireBuilderTabPlaceholder
      icon={Settings}
      title="Profile"
      description="Your AI Employee profile, training, and tools will appear here after you share your website with Trish."
      className="h-full min-h-[520px]"
    />
  );

  const siteGluePreviewPlaceholder = (
    <HireBuilderTabPlaceholder
      icon={Globe}
      title="Preview"
      description="See how your AI Employee will welcome visitors on your organization’s website. Share your website with Trish to get started."
      className="h-full min-h-[520px]"
    />
  );

  const siteGlueTestPlaceholder = (
    <HireBuilderTabPlaceholder
      icon={MessageCircle}
      title="Test"
      description="After setup, test Web Chat, Text Messages, Contact Form, Voice, and other enabled channels with your AI Employee."
      className="h-full min-h-[520px]"
    />
  );

  const siteGlueOnboardPlaceholder = (
    <HireBuilderTabPlaceholder
      icon={Rocket}
      title="Onboard"
      description="Review your AI Employee proposal, then onboard to your SiteGlue dashboard when you’re ready."
      className="h-full min-h-[520px]"
    />
  );

  /** SiteGlue Profile tab: available before Sign Up (no auth gate). */
  const siteGlueSettingsPanel =
    isSiteGlueBrand && useWizardLayout
      ? isBuildingCompanyProfile
        ? (
            <BuildingCompanyProfileProgress className="h-full min-h-[520px]" />
          )
        : agentProfileAndSettingsSections
          ? (
              <TooltipProvider>
                <div className="h-full min-h-[520px] space-y-10 overflow-y-auto p-4 pb-6 sm:p-5">
                  {agentProfileAndSettingsSections}
                </div>
              </TooltipProvider>
            )
          : siteGlueProfilePlaceholder
      : null;

  const hireBuilderConfigureContent =
    useWizardLayout && websiteSuggestion && recommendationsSelection ? (
      <TooltipProvider>
        <div className="space-y-10 pb-6">
          {agentProfileAndSettingsSections}
          {!isSiteGlueBrand ? (
            <>
              <section className="border-t border-slate-200 pt-8">{myChannelsSection}</section>
              <section className="border-t border-slate-200 pt-8">
                {teamLocationsDepartmentsSection}
              </section>
            </>
          ) : null}
        </div>
      </TooltipProvider>
    ) : useWizardLayout && isSiteGlueBrand ? (
      siteGlueProfilePlaceholder
    ) : (
      <div className="flex min-h-[240px] items-center justify-center text-sm text-slate-500">
        Preparing your configuration…
      </div>
    );

  const previewWebsiteHostLabel = React.useMemo(() => {
    const raw =
      consultantState?.websiteUrl ||
      resolvedPrimaryWebsiteUrl ||
      websiteSuggestion?.websiteUrl ||
      "";
    if (!raw.trim()) return "Preview";
    try {
      const host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname.replace(
        /^www\./i,
        "",
      );
      return host ? `Preview · ${host}` : "Preview";
    } catch {
      return "Preview";
    }
  }, [
    consultantState?.websiteUrl,
    resolvedPrimaryWebsiteUrl,
    websiteSuggestion?.websiteUrl,
  ]);

  const stepTabs = (
    <div className="mb-6 flex gap-2">
      {stepLabels.map(({ n, label }) => {
        const active = step === n;
        const done = step > n;
        const className = `flex flex-1 items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${
          active
            ? "border-primary bg-primary/5 text-primary"
            : done
              ? "border-slate-200 bg-slate-50 text-slate-700"
              : "border-slate-200 bg-white text-slate-400"
        }`;

        if (splitLayout && threeStepRecommendations) {
          return (
            <button
              key={n}
              type="button"
              onClick={() => setStep(n)}
              className={`${className} cursor-pointer transition hover:border-primary/40`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  active ? "bg-primary text-white" : done ? "bg-slate-700 text-white" : "bg-slate-200 text-slate-500"
                }`}
              >
                {n}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </button>
          );
        }

        return (
          <div key={n} className={className}>
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                active ? "bg-primary text-white" : done ? "bg-slate-700 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >
              {n}
            </span>
            <span className="hidden sm:inline">{label}</span>
          </div>
        );
      })}
    </div>
  );

  const configContent = (
    <TooltipProvider>
    <div className="space-y-6">
        {splitCombinedLayout && !websiteSuggestion ? (
          <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-6 py-12 text-center text-sm text-slate-500">
            Preparing your recommended agent configuration…
          </div>
        ) : splitCombinedLayout && websiteSuggestion ? (
          <>
            <HireRecommendationsOverviewStep
              businessName={companyName.trim() || websiteSuggestion.businessName}
              websiteUrl={websiteSuggestion.websiteUrl}
              recommendations={websiteSuggestion.recommendations}
              agentProfile={agentProfileDraft}
              onAgentProfileChange={handleAgentProfileDraftChange}
              selection={recommendationsSelection!}
              onSelectionChange={handleRecommendationsChange}
              isGeneratingPersona={personaGenerating}
            />

            <section className="space-y-4">
              <h3 className="inline-flex items-center text-sm font-semibold text-foreground">
                Company Settings
                <InfoTip text="Your business name, website, team members, and locations. These feed your agent profile, contact forms, and notification routing." />
              </h3>

              {threeStepRecommendations && isSignedIn ? (
                <div className="rounded-lg border border-[#C3D4FC] bg-[#F0F5FF] px-4 py-3 text-sm text-slate-700">
                  <p>
                    <span className="font-medium text-slate-900">Account:</span>{" "}
                    {[firstName, lastName].filter(Boolean).join(" ") || "Signed in user"}
                    {email ? ` · ${email}` : null}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="hire-first-name">Your first name</Label>
                      <Input
                        id="hire-first-name"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hire-last-name">Your last name</Label>
                      <Input
                        id="hire-last-name"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hire-email">Work email</Label>
                    <Input
                      id="hire-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@yourbusiness.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="hire-company">Your company</Label>
                <Input
                  id="hire-company"
                  autoComplete="organization"
                  placeholder="Acme Dental"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hire-website-url">Your business website</Label>
                <Input
                  id="hire-website-url"
                  placeholder="https://yourbusiness.com"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  autoComplete="url"
                />
              </div>

              {recommendationsSelection && onRecommendationsChange ? (
                <HireCompanyProfilePricingSection
                  selection={recommendationsSelection}
                  onChange={handleRecommendationsChange}
                  employerContact={pricingTeamContacts.employerContact}
                  leadTeamMember={pricingTeamContacts.leadTeamMember}
                  employerFields={employerFields}
                  leadFields={leadFields}
                  onEmployerUpdate={applyEmployerFormValues}
                  onLeadUpdate={applyLeadFormValues}
                  hideUnitPricing={isSiteGlueBrand}
                />
              ) : null}
            </section>
          </>
        ) : threeStepRecommendations && step === overviewStep && websiteSuggestion ? (
          <>
            <HireRecommendationsOverviewStep
              businessName={websiteSuggestion.businessName}
              websiteUrl={websiteSuggestion.websiteUrl}
              recommendations={websiteSuggestion.recommendations}
              agentProfile={agentProfileDraft}
              onAgentProfileChange={handleAgentProfileDraftChange}
              selection={recommendationsSelection!}
              onSelectionChange={handleRecommendationsChange}
              isGeneratingPersona={personaGenerating}
            />
          </>
        ) : threeStepRecommendations &&
          step === pricingStep &&
          !splitLayout &&
          recommendationsSelection &&
          onRecommendationsChange ? (
          <HireRecommendationsStep
            businessName={recommendationsBusinessName}
            selection={recommendationsSelection}
            onChange={handleRecommendationsChange}
            hideTeamAndLocations
            employerContact={pricingTeamContacts.employerContact}
            leadTeamMember={pricingTeamContacts.leadTeamMember}
          />
        ) : showPlanStep && step === 1 && !threeStepRecommendations ? (
          recommendationsMode && recommendationsSelection && onRecommendationsChange ? (
            <HireRecommendationsStep
              businessName={recommendationsBusinessName}
              selection={recommendationsSelection}
              onChange={handleRecommendationsChange}
            />
          ) : (
            <HirePlanSelectionStep
              agentName={agentName}
              selection={planSelection}
              onChange={setPlanSelection}
            />
          )
        ) : step === companyProfileStep ? (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            {splitLayout && threeStepRecommendations ? "Company Settings" : "Company Profile"}
          </h3>

          {threeStepRecommendations && isSignedIn ? (
            <div className="rounded-lg border border-[#C3D4FC] bg-[#F0F5FF] px-4 py-3 text-sm text-slate-700">
              <p>
                <span className="font-medium text-slate-900">Account:</span>{" "}
                {[firstName, lastName].filter(Boolean).join(" ") || "Signed in user"}
                {email ? ` · ${email}` : null}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="hire-first-name">Your first name</Label>
                  <Input
                    id="hire-first-name"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire-last-name">Your last name</Label>
                  <Input
                    id="hire-last-name"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hire-email">Work email</Label>
                <Input
                  id="hire-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@yourbusiness.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="hire-company">Your company</Label>
            <Input
              id="hire-company"
              autoComplete="organization"
              placeholder="Acme Dental"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-website-url">Your business website</Label>
            <Input
              id="hire-website-url"
              placeholder="https://yourbusiness.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              autoComplete="url"
            />
          </div>

          {threeStepRecommendations &&
          recommendationsSelection &&
          onRecommendationsChange ? (
            <HireCompanyProfilePricingSection
              selection={recommendationsSelection}
              onChange={handleRecommendationsChange}
              employerContact={pricingTeamContacts.employerContact}
              leadTeamMember={pricingTeamContacts.leadTeamMember}
              employerFields={employerFields}
              leadFields={leadFields}
              onEmployerUpdate={applyEmployerFormValues}
              onLeadUpdate={applyLeadFormValues}
              hideUnitPricing={isSiteGlueBrand}
            />
          ) : null}

          {!threeStepRecommendations ? (
            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Lead Team Member</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  Person who receives conversation summaries, handoffs, and notifications. Leave
                  blank to use your account as the lead.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="hire-lead-first-name">First name</Label>
                  <Input
                    id="hire-lead-first-name"
                    autoComplete="off"
                    placeholder="Amy"
                    value={leadFirstName}
                    onChange={(e) => setLeadFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire-lead-last-name">Last name</Label>
                  <Input
                    id="hire-lead-last-name"
                    autoComplete="off"
                    placeholder="Roberts"
                    value={leadLastName}
                    onChange={(e) => setLeadLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="hire-lead-email">Email</Label>
                  <Input
                    id="hire-lead-email"
                    type="email"
                    autoComplete="off"
                    placeholder="amy@yourbusiness.com"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire-lead-phone">Phone (optional)</Label>
                  <Input
                    id="hire-lead-phone"
                    type="tel"
                    autoComplete="off"
                    placeholder="303-555-0100"
                    value={leadPhone}
                    onChange={(e) => setLeadPhone(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </section>
        ) : agentProfileStep != null && step === agentProfileStep ? (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Agent Profile</h3>
          <p className="text-xs text-muted-foreground">
            Choose how your agent appears to visitors — name, photo, and job title.
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hire-agent-name">Agent first name</Label>
              <Input
                id="hire-agent-name"
                autoComplete="off"
                placeholder="Samantha"
                value={customAgentName}
                onChange={(e) => {
                  setAgentNameConfirmed(true);
                  setCustomAgentName(e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hire-agent-title">Job title</Label>
              <Input
                id="hire-agent-title"
                autoComplete="off"
                placeholder="Type a response…"
                value={customJobTitle}
                onChange={(e) => setCustomJobTitle(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Profile photo</Label>
            <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
              <div className="relative shrink-0">
                <Avatar className="h-12 w-12 shrink-0">
                  <AvatarImage src={profileImageDisplayUrl} alt={customAgentName} />
                  <AvatarFallback>
                    {(customAgentName.trim() || "AG").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <label
                  className="absolute -bottom-1 -right-1 cursor-pointer rounded-full border border-white bg-primary p-1.5 text-white shadow hover:bg-primary/90"
                  aria-label="Upload profile image"
                >
                  {isUploadingAgentImage ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Camera className="h-3 w-3" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={isUploadingAgentImage}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleAgentImageUpload(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-semibold">{customAgentName.trim() || "Agent"}</p>
                <Select value={profilePresetUrl} onValueChange={setProfilePresetUrl}>
                  <SelectTrigger className="h-9 bg-background">
                    <SelectValue placeholder="Select agent" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {!AGENT_PROFILE_PRESETS.some((p) => p.url === profilePresetUrl) &&
                    profilePresetUrl ? (
                      <SelectItem value={profilePresetUrl}>
                        <div className="flex items-center gap-2">
                          <img
                            src={profilePresetUrl}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover"
                          />
                          <span>Custom upload</span>
                        </div>
                      </SelectItem>
                    ) : null}
                    {AGENT_PROFILE_PRESETS.map((preset) => (
                      <SelectItem key={preset.url} value={preset.url}>
                        <div className="flex items-center gap-2">
                          <img
                            src={preset.url}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover"
                          />
                          <span>{preset.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Upload a photo or choose a default avatar.
                </p>
              </div>
            </div>
          </div>
        </section>
        ) : null}

        {!(splitLayout && threeStepRecommendations) && threeStepRecommendations && step === overviewStep ? (
          <Button type="button" className="w-full" onClick={handleContinueFromRecommendations}>
            Continue to Company Profile
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : !(splitLayout && threeStepRecommendations) && threeStepRecommendations && step === pricingStep ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => setStep(companyProfileStep)}
                disabled={isSubmitting}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleFinishFromPricing}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Onboarding…
                  </>
                ) : (
                  <>
                    <Rocket className="mr-2 h-4 w-4" />
                    Onboard {customAgentName.trim() || agentName}
                  </>
                )}
              </Button>
            </div>
            {!isSignedIn && (
              <p className="text-center text-xs text-muted-foreground">
                Already have an account?{" "}
                <Link
                  href="#"
                  className="underline"
                  onClick={(e) => {
                    e.preventDefault();
                    applyRecommendationsPricing();
                    const form = validateForm();
                    if (!form) return;
                    void (async () => {
                      try {
                        const forceNewAgent = isHireForceNewAgent();
                        const started = await startHireMutation.mutateAsync({
                          marketingBrand:
                            typeof window !== "undefined" &&
                            new URLSearchParams(window.location.search).get("brand") === "siteglue"
                              ? "siteglue"
                              : undefined,
                          templateProjectId: activeTemplateProjectId,
                          websiteUrl: form.websiteUrl,
                          email: form.email,
                          firstName: form.firstName,
                          lastName: form.lastName,
                          forceNewAgent,
                        });
                        if (forceNewAgent) clearHireForceNewAgent();
                        persistSessionData(started.clonedProjectId, form);
                        router.push(
                          buildSignInUrl({
                            email: form.email,
                            hireIntentId: started.hireIntentId,
                          }),
                        );
                      } catch (err) {
                        toast.error(
                          err instanceof Error ? err.message : "Could not start hire flow.",
                        );
                      }
                    })();
                  }}
                >
                  Sign in
                </Link>
              </p>
            )}
          </div>
        ) : !(splitLayout && threeStepRecommendations) && threeStepRecommendations && step === companyProfileStep ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              onClick={() => setStep(overviewStep)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Button type="button" className="flex-1" onClick={handleNextFromCompanyProfile}>
              Continue to Agent Pricing
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : showPlanStep && step === 1 && !threeStepRecommendations ? (
          <Button type="button" className="w-full" onClick={handleContinueFromPlan}>
            Continue to Agent Profile
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : agentProfileStep != null && step === agentProfileStep ? (
          <div className="flex gap-2">
            {showPlanStep ? (
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => setStep(threeStepRecommendations ? overviewStep : 1)}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            ) : null}
            <Button type="button" className="flex-1" onClick={handleNextFromAgentProfile}>
              Continue to Company Profile
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : step === companyProfileStep ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() =>
                  setStep(agentProfileStep ?? (threeStepRecommendations ? overviewStep : 1))
                }
                disabled={isSubmitting}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
              <Button className="flex-1" onClick={handleContinue} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Onboarding…
                  </>
                ) : (
                  <>
                    <Rocket className="mr-2 h-4 w-4" />
                    Onboard {customAgentName.trim() || agentName}
                  </>
                )}
              </Button>
            </div>
            {!isSignedIn && (
              <p className="text-center text-xs text-muted-foreground">
                Already have an account?{" "}
                <Link
                  href="#"
                  className="underline"
                  onClick={(e) => {
                    e.preventDefault();
                    const form = validateForm();
                    if (!form) return;
                    void (async () => {
                      try {
                        const forceNewAgent = isHireForceNewAgent();
                        const started = await startHireMutation.mutateAsync({
                          marketingBrand:
                            typeof window !== "undefined" &&
                            new URLSearchParams(window.location.search).get("brand") === "siteglue"
                              ? "siteglue"
                              : undefined,
                          templateProjectId: activeTemplateProjectId,
                          websiteUrl: form.websiteUrl,
                          email: form.email,
                          firstName: form.firstName,
                          lastName: form.lastName,
                          forceNewAgent,
                        });
                        if (forceNewAgent) clearHireForceNewAgent();
                        persistSessionData(started.clonedProjectId, form);
                        router.push(
                          buildSignInUrl({
                            email: form.email,
                            hireIntentId: started.hireIntentId,
                          }),
                        );
                      } catch (err) {
                        toast.error(
                          err instanceof Error ? err.message : "Could not start hire flow.",
                        );
                      }
                    })();
                  }}
                >
                  Sign in
                </Link>
              </p>
            )}
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );

  return (
    <div
      className={cn(
        className,
        splitCombinedLayout && "flex min-h-0 w-full min-w-0 flex-1 flex-col",
        useWizardLayout && "flex min-h-0 w-full min-w-0 flex-1 flex-col",
      )}
    >
      {!splitCombinedLayout && !useWizardLayout ? (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">
            {formTitle ?? `Onboard ${agentName}`}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{stepSubtitle}</p>
        </div>
      ) : null}

      {splitCombinedLayout ? (
        <HireSplitLayout
          className="min-h-0 w-full min-w-0 flex-1"
          configPanel={configContent}
          previewPanel={
            <HireCustomDeployPanel
              previewProjectId={previewProjectId}
              previewReady={previewGenerated}
              deployedProjectId={deployedProjectId}
              agentName={displayAgentName}
              totalLabel={recommendationsTotalLabel}
              pricingLineItems={pricingLineItems}
              isFree={isFreeDeploy}
              isSubmitting={isSubmitting}
              isGenerating={isGenerating}
              previewVersion={previewVersion}
              previewRefreshKey={previewRefreshKey}
              preferredTab={deployPanelTab}
              onGenerate={() => void handleGenerateAgent({ manual: true })}
              onDeploy={() => void handleDeploy()}
              className="h-full min-h-0 rounded-none border-0 shadow-none"
            />
          }
        />
      ) : useWizardLayout ? (
        <HireBuilderTabbedLayout
          className="min-h-0 w-full min-w-0 flex-1"
          leftTab={builderLeftTab}
          onLeftTabChange={setBuilderLeftTab}
          rightTab={builderRightTab}
          onRightTabChange={handleBuilderRightTabChange}
          siteGlueConsultantChrome={isSiteGlueBrand}
          onContactSales={() => {
            trackSiteGlueCta({
              action: "contactSales",
              label: SITEGLUE_CTA_CONFIG.contactSales.label,
              location: "header",
              destination: "contact-sales-modal",
            });
            setContactSalesOpen(true);
          }}
          onBookDemo={() => {
            trackSiteGlueCta({
              action: "bookDemo",
              label: SITEGLUE_CTA_CONFIG.bookDemo.label,
              location: "header",
              destination: "contact-sales-modal",
            });
            setContactSalesOpen(true);
          }}
          isSignedIn={Boolean(isSignedIn)}
          onboardTabLabel={
            showOnboardingChat && !agentNameConfirmed
              ? "Onboard"
              : `Onboard ${displayAgentName}`
          }
          settingsTabLabel={
            isSiteGlueBrand
              ? showOnboardingChat && !agentNameConfirmed
                ? "Profile"
                : (() => {
                    const n = displayAgentName.trim() || "Agent";
                    return `${n}${/s$/i.test(n) ? "'" : "'s"} Profile`;
                  })()
              : undefined
          }
          previewTabLabel={isSiteGlueBrand ? previewWebsiteHostLabel : undefined}
          testTabLabel={
            isSiteGlueBrand
              ? showOnboardingChat && !agentNameConfirmed
                ? "Test"
                : `Test ${displayAgentName}`
              : undefined
          }
          disabledTabs={{
            configure: !previewGenerated,
            knowledge: false,
            preview: isSiteGlueBrand
              ? false
              : !previewGenerated &&
                !shouldShowPreviewSignUp &&
                !onboardingComplete,
            report: isSiteGlueBrand,
            pricing: isSiteGlueBrand ? false : true,
            hire: isSiteGlueBrand
              ? isSignedIn
                ? !(
                    previewGenerated ||
                    consultantState?.step === "sign_up" ||
                    consultantState?.step === "complete" ||
                    Boolean(consultantState?.employeeName?.trim())
                  )
                : false // clickable → Sign Up modal via handleBuilderRightTabChange
              : !previewGenerated,
            // SiteGlue Preview/Test: available for guests after scrape/preview generate.
            live_preview: isSiteGlueBrand ? false : isGenerating,
            test_agent: isSiteGlueBrand ? false : true,
          }}
          hiddenTabs={{
            // SiteGlue: Strategy hidden; Pricing shows Intern/Associate/Professional cards.
            report: isSiteGlueBrand || !consultantState?.report,
            company_settings: !isSiteGlueBrand,
            pricing: !isSiteGlueBrand,
            live_preview: !isSiteGlueBrand,
            test_agent: !isSiteGlueBrand,
          }}
          buildPanel={
            showOnboardingChat && onSubmitWebsite ? (
              isSiteGlueBrand ? (
                <SiteGlueConsultantChat
                  className="h-full min-h-0"
                  bootstrapComplete={bootstrapComplete}
                  isAnalyzing={isBootstrapping}
                  bootstrapError={bootstrapError}
                  isSignedIn={Boolean(isSignedIn)}
                  websiteSuggestion={websiteSuggestion}
                  onSubmitWebsite={onSubmitWebsite}
                  onSetupAgent={handleOnboardingSetupAgent}
                  onRequestSignUp={handleTrishRequestSignUp}
                  onOpenOnboardTab={handleOpenOnboardTab}
                  onContactSales={() => {
                    trackSiteGlueCta({
                      action: "contactSales",
                      label: SITEGLUE_CTA_CONFIG.contactSales.label,
                      location: "hire_builder_left_tab",
                      destination: "contact-sales-modal",
                    });
                    setContactSalesOpen(true);
                  }}
                  onStartOver={handleTrishStartOver}
                  onConsultantStateChange={handleConsultantStateChange}
                  onPlanRecommended={handleConsultantPlanRecommended}
                  onRequestConnectIntegration={handleConsultantConnectIntegration}
                  onOpenReportTab={handleOpenReportTab}
                  onOpenKnowledgeTab={handleOpenKnowledgeTab}
                  onOpenEmployeeTab={handleOpenEmployeeTab}
                  onOpenPricingTab={handleOpenPricingTab}
                  onAgentProfileSelected={handleTrishAgentProfileSelected}
                  onCompanyProfileReady={handleCompanyProfileReady}
                  externalPlanPick={externalPlanPick}
                  onExternalPlanPickHandled={() => setExternalPlanPick(null)}
                  initialPlanPick={initialPlanFromUrl}
                />
              ) : isPartnerBrand && partnerConfig ? (
                <PartnerOnboardingChat
                  className="h-full min-h-0"
                  partnerConfig={partnerConfig}
                  bootstrapComplete={bootstrapComplete}
                  isAnalyzing={isBootstrapping}
                  bootstrapError={bootstrapError}
                  isSignedIn={Boolean(isSignedIn)}
                  businessName={resolvedBusinessName}
                  vertical={websiteSuggestion?.vertical}
                  goalOptions={trishGoalOptions}
                  suggestedProfile={
                    websiteSuggestion?.recommendations.agentProfile
                      ? {
                          agentName: websiteSuggestion.recommendations.agentProfile.agentName,
                          jobTitle: websiteSuggestion.recommendations.agentProfile.jobTitle,
                          profileImageUrl:
                            websiteSuggestion.recommendations.agentProfile.profileImageUrl,
                        }
                      : null
                  }
                  onSubmitWebsite={onSubmitWebsite}
                  onSetupAgent={handleOnboardingSetupAgent}
                  onRequestSignUp={handleTrishRequestSignUp}
                  onAgentProfileSelected={handleTrishAgentProfileSelected}
                  onConfigPreference={handleTrishConfigPreference}
                />
              ) : (
                <TrishOnboardingChat
                  className="h-full min-h-0"
                  bootstrapComplete={bootstrapComplete}
                  isAnalyzing={isBootstrapping}
                  bootstrapError={bootstrapError}
                  isSignedIn={Boolean(isSignedIn)}
                  businessName={resolvedBusinessName}
                  vertical={websiteSuggestion?.vertical}
                  goalOptions={trishGoalOptions}
                  suggestedProfile={
                    websiteSuggestion?.recommendations.agentProfile
                      ? {
                          agentName: websiteSuggestion.recommendations.agentProfile.agentName,
                          jobTitle: websiteSuggestion.recommendations.agentProfile.jobTitle,
                          profileImageUrl:
                            websiteSuggestion.recommendations.agentProfile.profileImageUrl,
                        }
                      : null
                  }
                  onSubmitWebsite={onSubmitWebsite}
                  onSetupAgent={handleOnboardingSetupAgent}
                  onRequestSignUp={handleTrishRequestSignUp}
                  onAgentProfileSelected={handleTrishAgentProfileSelected}
                  onConfigPreference={handleTrishConfigPreference}
                />
              )
            ) : (
              <HireBuilderBuildChat
                key={`${normalizeHireWebsiteSessionKey(resolvedPrimaryWebsiteUrl || resolvedBusinessName)}-${bootstrapComplete || previewGenerated ? "ready" : "waiting"}`}
                businessName={resolvedBusinessName}
                websiteUrl={resolvedPrimaryWebsiteUrl}
                agentName={displayAgentName}
                jobTitle={customJobTitle}
                selection={recommendationsSelection!}
                widgetColors={trainingDraft.widgetColors}
                organizationLayout={organizationLayout}
                teamContacts={hireBuilderTeamContacts}
                previewReady={previewGenerated}
                disabled={
                  !isSignedIn ||
                  !recommendationsSelection ||
                  (!previewGenerated && !bootstrapComplete)
                }
                previewProjectId={previewProjectId}
                previewHireIntentId={previewHireIntentId}
                templateProjectId={activeTemplateProjectId}
                toolCredentials={toolCredentials}
                onToolCredentialsChange={setToolCredentials}
                marketingBrand={isSiteGlueBrand ? "siteglue" : "inbound"}
                aiEmployeePlanId={isSiteGlueBrand ? siteGluePlanId : null}
                resumeAfterGoogleConnect={resumeAfterGoogleConnect}
                onResumeAfterGoogleConnectConsumed={() => setResumeAfterGoogleConnect(false)}
                lockAgentName={agentNameConfirmed || previewGenerated}
                agentIdentity={{
                  agentName: customAgentName.trim() || displayAgentName,
                  jobTitle: customJobTitle,
                  profileImageUrl: profilePresetUrl,
                  agentNameConfirmed: agentNameConfirmed || previewGenerated,
                }}
                onApplyUpdates={handleBuilderApplyUpdates}
              />
            )
          }
          configurePanel={hireBuilderConfigureContent}
          companySettingsPanel={siteGlueSettingsPanel ?? undefined}
          pricingPanel={
            isSiteGlueBrand ? (
              <SiteGluePlanPricingPanel
                state={consultantState}
                isBuilding={isBuildingCompanyProfile}
                onSelectPlan={handlePricingPlanAction}
                className="h-full min-h-[520px]"
              />
            ) : undefined
          }
          knowledgePanel={siteGlueOrganizationPanel ?? undefined}
          previewPanel={
            isSiteGlueBrand ? (
              <YourAiEmployeePreview
                state={consultantState}
                isSignedIn={Boolean(isSignedIn)}
                className="h-full min-h-[520px]"
              />
            ) : !isSiteGlueBrand && shouldShowPreviewSignUp ? (
              <div className="flex h-full min-h-[520px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
                <PostAiJobInlineSignup
                  centered
                  hidePhone
                  signInUrl={buildSignInHref({
                    redirect_url:
                      typeof window !== "undefined"
                        ? `${window.location.pathname}${window.location.search}`
                        : MARKETING_BUILD_CUSTOM,
                  })}
                  completionRedirectUrl={
                    typeof window !== "undefined"
                      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
                      : MARKETING_BUILD_CUSTOM
                  }
                  initialEmail={signupPrefill.email}
                  initialFirstName={signupPrefill.firstName}
                  initialLastName={signupPrefill.lastName}
                  initialTitle={signupPrefill.title}
                  initialPhone={signupPrefill.phone}
                  initialProfileImageUrl={signupPrefill.profileImageUrl}
                  title="Create your free account"
                  description="Sign up so we can generate your new AI Employee preview and unlock Build. Use Sign in below if you already have an account. Clerk will email a confirmation link after you submit the form with a password."
                  verifyingDescription="After you confirm your email, return here — we'll generate your new AI Employee workspace."
                  onVerified={() => {
                    setShowPreviewSignUp(false);
                    if (!isSiteGlueBrand) setBuilderRightTab("preview");
                  }}
                />
              </div>
            ) : (
              <HireBuilderPreviewPanel
                previewProjectId={previewProjectId}
                previewReady={previewGenerated}
                websiteSubmitted={bootstrapComplete && Boolean(websiteSuggestion?.websiteUrl)}
                previewGenerationReady={onboardingComplete}
                agentName={displayAgentName}
                isGenerating={isGenerating}
                previewRefreshKey={previewRefreshKey}
                siteGlue={isSiteGlueBrand}
                className="h-full min-h-0"
              />
            )
          }
          livePreviewPanel={
            isSiteGlueBrand ? (
              websiteScraped ||
              consultantState?.websiteUrl ||
              resolvedPrimaryWebsiteUrl ||
              websiteSuggestion?.websiteUrl ? (
                <SiteGlueLiveSitePreviewPanel
                  websiteUrl={
                    consultantState?.websiteUrl ||
                    resolvedPrimaryWebsiteUrl ||
                    websiteSuggestion?.websiteUrl
                  }
                  previewProjectId={previewProjectId}
                  previewReady={previewGenerated}
                  isGenerating={isGenerating}
                  previewRefreshKey={previewRefreshKey}
                  agentName={displayAgentName}
                  widgetEnabled={builderRightTab === "live_preview"}
                  requireSignedInForWidget={false}
                  isSignedIn={Boolean(isSignedIn)}
                  className="h-full min-h-[520px]"
                />
              ) : (
                siteGluePreviewPlaceholder
              )
            ) : undefined
          }
          signupPanel={undefined}
          reportPanel={
            isBuildingCompanyProfile ? (
              <BuildingCompanyProfileProgress className="h-full min-h-[520px]" />
            ) : consultantState?.report ? (
              <div className="h-full min-h-[520px] overflow-y-auto p-4 sm:p-5">
                <AiImplementationStrategyView report={consultantState.report} />
              </div>
            ) : (
              <HireBuilderTabPlaceholder
                icon={ClipboardList}
                title="AI Employee Proposal"
                description="After Trish confirms what she learned about your organization, your AI Employee Proposal will appear here — desired outcomes mapped to Actions, Channels, and Human Team collaboration."
              />
            )
          }
          hirePanel={
            isSiteGlueBrand
              ? renderSiteGlueAuthGatedTab({
                  tab: "hire",
                  placeholder: siteGlueOnboardPlaceholder,
                  content: recommendationsSelection ? (
                    (() => {
                      const siteGlueHireEnabled =
                        previewGenerated ||
                        consultantState?.step === "sign_up" ||
                        consultantState?.step === "complete" ||
                        Boolean(consultantState?.employeeName?.trim()) ||
                        Boolean(
                          consultantState?.budgetConfirmed && consultantState?.employeeName,
                        );
                      return (
                        <div className="flex h-full min-h-[520px] flex-col overflow-hidden">
                          <div className="sticky top-0 z-20 shrink-0 border-b border-[#C3D4FC] bg-[#F0F5FF] px-4 py-3 sm:px-5">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-900">
                                  Onboard {displayAgentName.trim() || "your AI Employee"}
                                </p>
                                <p className="text-xs text-slate-600">
                                  Review your proposal below, then continue.
                                </p>
                              </div>
                              <Button
                                type="button"
                                className="w-full shrink-0 sm:w-auto"
                                disabled={isSubmitting || !siteGlueHireEnabled}
                                onClick={handleOnboardClick}
                              >
                                {isSubmitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Processing…
                                  </>
                                ) : (
                                  <>
                                    <Rocket className="mr-2 h-4 w-4" />
                                    Onboard {displayAgentName.trim() || "AI Employee"}
                                    {!isFreeDeploy ? ` · ${recommendationsTotalLabel}` : ""}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                          <div className="min-h-0 flex-1 overflow-y-auto">
                            <HireBuilderHirePanel
                              businessName={resolvedBusinessName}
                              agentName={displayAgentName}
                              selection={recommendationsSelection}
                              totalLabel={recommendationsTotalLabel}
                              isFree={isFreeDeploy}
                              previewReady={previewGenerated}
                              hireEnabled={siteGlueHireEnabled}
                              isGenerating={isGenerating}
                              isSubmitting={isSubmitting}
                              deployedProjectId={deployedProjectId}
                              accountLabel={accountSummaryLabel}
                              organizationLayout={organizationLayout}
                              productLabel="SiteGlue"
                              siteGluePlanId={siteGluePlanId}
                              onHire={handleOnboardClick}
                            />
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    siteGlueOnboardPlaceholder
                  ),
                })
              : recommendationsSelection
                ? (() => {
                    const siteGlueHireEnabled = previewGenerated;
                      return (
                      <div className="flex h-full min-h-0 flex-col overflow-hidden">
                        <div className="min-h-0 flex-1 overflow-y-auto">
                          <HireBuilderHirePanel
                            businessName={resolvedBusinessName}
                            agentName={displayAgentName}
                            selection={recommendationsSelection}
                            totalLabel={recommendationsTotalLabel}
                            isFree={isFreeDeploy}
                            previewReady={previewGenerated}
                            hireEnabled={siteGlueHireEnabled}
                            isGenerating={isGenerating}
                            isSubmitting={isSubmitting}
                            deployedProjectId={deployedProjectId}
                            accountLabel={accountSummaryLabel}
                            organizationLayout={organizationLayout}
                            productLabel="Inbound GPT"
                            siteGluePlanId={null}
                            onHire={handleOnboardClick}
                          />
                        </div>
                      </div>
                    );
                  })()
                : null
          }
          testAgentPanel={
            isSiteGlueBrand ? (
              <HireBuilderTestAgentPanel
                previewProjectId={previewProjectId}
                previewReady={previewGenerated}
                agentName={displayAgentName}
                isGenerating={isGenerating}
                isSignedIn={Boolean(isSignedIn)}
                onRequestSignUp={() => requestSignUpForTab("test_agent")}
                className="h-full min-h-[520px]"
              />
            ) : undefined
          }
        />
      ) : (
        <>
          {stepTabs}
          {configContent}
        </>
      )}

      <HireWizardCompanyProfileDialog
        open={companyProfileDialogOpen}
        onOpenChange={setCompanyProfileDialogOpen}
        businessName={resolvedBusinessName}
        profileText={trainingDraft.companyProfileText}
      />

      {isSiteGlueBrand ? (
        <HireOnboardingProgressModal
          open={
            !planCheckoutOpen &&
            !onboardingProgressDismissed &&
            planConfirmedForProgressModal &&
            (isBootstrapping ||
              isBuildingCompanyProfile ||
              (isGenerating && !previewGenerated) ||
              consultantState?.step === "analyzing" ||
              consultantState?.step === "building_knowledge" ||
              (previewGenerated && !isSignedIn))
          }
          activeStepIndex={resolveHireOnboardingProgressStepIndex({
            step: consultantState?.step,
            isBootstrapping,
            isBuildingCompanyProfile,
            isGeneratingPreview: isGenerating && !previewGenerated,
            hasCompanyProfile: Boolean(
              consultantState?.companyProfileMarkdown?.trim() ||
                trainingDraft.companyProfileText?.trim(),
            ),
            hasLocations: Boolean(
              (recommendationsSelection?.locationDetails?.length ?? 0) > 0 ||
                (websiteSuggestion?.recommendations.locationList?.length ?? 0) > 0,
            ),
            previewGenerated,
            isSignedIn: Boolean(isSignedIn),
          })}
          isGeneratingPreview={isGenerating && !previewGenerated}
          needsSignIn={previewGenerated && !isSignedIn}
          dismissible
          onOpenChange={(open) => {
            if (!open) setOnboardingProgressDismissed(true);
          }}
          onSignIn={() => {
            setOnboardingProgressDismissed(true);
            requestSignUpForTab("hire");
            setBuilderLeftTab("build");
          }}
          title="Onboarding Progress"
          description="Trish is setting up your SiteGlue AI Employee from your website. Here’s what’s happening now."
        />
      ) : null}

      {/* SiteGlue uses inline HireOnboardingAuthPanel only — never stack a Sign Up dialog behind Progress. */}
      {!isSiteGlueBrand && (pendingHire || authGateOnly) && (
        <HireOnboardingAuthModal
          open={authModalOpen}
          phase={authPhase}
          signInUrl={
            pendingHire
              ? buildSignInUrl({
                  email: pendingHire.form.email,
                  hireIntentId: pendingHire.hireIntentId,
                })
              : buildSignInHref({
                  redirect_url:
                    typeof window !== "undefined"
                      ? `${window.location.pathname}${window.location.search}`
                      : MARKETING_BUILD_CUSTOM,
                })
          }
          hireCompletionRedirectUrl={
            pendingHire
              ? buildHireRedirectUrl(pendingHire.hireIntentId)
              : typeof window !== "undefined"
                ? `${window.location.origin}${window.location.pathname}${window.location.search}`
                : MARKETING_BUILD_CUSTOM
          }
          initialEmail={pendingHire?.form.email ?? signupPrefill.email}
          initialFirstName={pendingHire?.form.firstName ?? signupPrefill.firstName}
          initialLastName={pendingHire?.form.lastName ?? signupPrefill.lastName}
          productLabel={isSiteGlueBrand ? "SiteGlue" : "Inbound GPT"}
          onVerified={handleAuthVerified}
        />
      )}

      {isSiteGlueBrand && planCheckoutPlanId ? (
        <SiteGluePlanCheckoutDialog
          open={planCheckoutOpen}
          onOpenChange={(open) => {
            setPlanCheckoutOpen(open);
            if (!open) setPlanCheckoutPlanId(null);
          }}
          planId={planCheckoutPlanId}
          isSubmitting={createCheckoutMutation.isPending || startHireMutation.isPending}
          businessName={
            consultantState?.businessName ||
            websiteSuggestion?.businessName ||
            companyName ||
            undefined
          }
          agentName={displayAgentName}
          signInUrl={hireAuthUrls.signInUrl}
          hireCompletionRedirectUrl={hireAuthUrls.completionRedirectUrl}
          onContinueToPayment={handlePlanCheckoutContinueToPayment}
        />
      ) : null}

      {isSiteGlueBrand ? (
        <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
      ) : null}
    </div>
  );
}
